import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue, push, set, update, remove, get, query as rtdbQuery, orderByChild, limitToLast, runTransaction } from "firebase/database";
import { collection, query, where, getDocs, getDoc, orderBy, getCountFromServer, doc, limit as firestoreLimit, onSnapshot, writeBatch } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useActiveWorld } from "../hooks/useActiveWorld";
import { useOutreachAccess } from "../hooks/useOutreachAccess";
import useResearchAccess from "../hooks/useResearchAccess";
import ResearchLockedNotice from "./ResearchLockedNotice";
import { computeHasEmail, isFollowUpEmailType } from "../shared/enums";
import { logOutreachEnrollment } from "../shared/contactActivityLog";
import { EMAIL_PLAN_LIMITS, PREPARED_QUEUE_CAP_DEFAULT, preparedQueueCapForPlan, resolveMembershipPlanId } from "../config/creditPlans";
import { potentialProfilesPath, profilesFromSnapshot } from "./potentialProfiles";
import { sanitizeLayout } from "./importListLayout";
import AddAgentLists from "./AddAgentLists";
import {
  API_TRIGGER,
  appendPoolLists,
  applyPoolLists,
  describePoolLists,
  prettyImportName,
  interleaveGroups,
  isAnyListPool,
  isApiPool,
  isManualPool,
  jobCanDiscoverEmail,
  executeAtsAfterBacklog,
  getLocalDayKey,
  listsNotOnPool,
  nextLocalDayStart,
  queueGuardForAddedLists,
  pacedExecuteAts,
  resolvePoolLists,
  stepWorkingDay,
  takeDailyShares,
} from "./contactPoolLists";
import { planPaceReschedule, usedTodayFromStats } from "./outreachRepace";
import { dropoutRate } from "./outreachDropout";
import DropoutBreakdownModal, { DropoutStatCell } from "./DropoutBreakdownModal";
import {
  compactFailAnalysis,
  humanizeFailReason,
  parsePotentialThreshold,
} from "./outreachFailExplain";
import { isReadySender, normalizeSenderAccountIds, senderPoolLabel, toggleSenderAccountId, usesPreviousSender, SENDER_FROM_PREVIOUS, SENDER_FROM_POOL } from "./outreachSenders";
import OutreachImportListPicker from "./OutreachImportListPicker";
import {
  LIST_MAP_GUIDELINE,
  LIST_MAP_ICP,
  LIST_MAP_TEMPLATE,
  cleanListOverride,
  cleanTemplateByList,
  collectOverrideIds,
  collectStepTemplateIds,
  persistStepListMaps,
  readListOverrideNames,
  readTemplateByListNames,
} from "./outreachTemplateMap";
import { isLiveJob, sameStepStructure } from "./outreachJobStructure";
import { globalSafeGuardChip, globalSafeGuardCopy } from "./outreachSafeGuardChip";
import { describeStepHistory, stepHistoryEmails } from "./outreachStepOutcome";
import { asModuleList, persistResearchModes, resolveWebResearchModules } from "./outreachWebResearch";
import {
  STEP_CONTACT_PAGE,
  canLoadMoreContacts,
  contactsAtStep,
  isHereAtStep,
  isPassedThroughStep,
  mergeJobEntries,
  nextPageSize,
  pageSizeOrDefault,
  passedThroughCount,
  stepHistoryAt,
  visibleContactIds,
} from "./outreachStepContacts";
import { noRecipientChipTitle, noRecipientNote } from "./outreachNoRecipient";
import { useDataConnectors } from "./dataConnectors";
import { useChanceRadars } from "./ChanceRadarSettings";
import { completedAgentTags, completedTagLabel, failedAgentTags, failedTagLabel, isCompletedTagId, isFailedTagId, labelForPoolTag } from "./outreachCompletedTag";
import "./OutreachJobsSettings.css";

// Prepare mode ("only prepare the emails, I'll send them myself").
// Daily pace (this slider) is independent of the unsent waiting cap, which
// comes from the membership plan (Starter 60 / Pro 120 / Enterprise 180)
// and is enforced in processOutreachJobs.js.
const DEFAULT_PREPARE_DAILY_LIMIT = 20;
const PREPARE_DAILY_LIMIT_MAX = 60;

// Shown on a delivery mode the account isn't licensed for. Both modes are sold
// separately and unlocked per account in the Team Dashboard.
const MODE_LOCKED_TITLE = "This delivery mode is not unlocked for your account";
const MODE_LOCKED_HINT = "Contact us to unlock this delivery mode.";

const InfoTip = ({ children }) => (
  <span className="oj-sb-tip" onClick={(e) => e.stopPropagation()}>
    <i className="fa fa-info-circle" aria-hidden="true" />
    <span className="oj-sb-tip-pop" role="tooltip">{children}</span>
  </span>
);

const TIME_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const TIME_MINUTES = ["00", "15", "30", "45"];

const TimeField = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const [h, rawM] = String(value || "09:00").split(":");
  const m = TIME_MINUTES.includes(rawM) ? rawM : (rawM || "00");
  const minutes = TIME_MINUTES.includes(m) ? TIME_MINUTES : [m, ...TIME_MINUTES].sort();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    const selectedH = hourRef.current?.querySelector("[data-selected='1']");
    const selectedM = minRef.current?.querySelector("[data-selected='1']");
    selectedH?.scrollIntoView({ block: "center" });
    selectedM?.scrollIntoView({ block: "center" });
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (nextH, nextM) => onChange(`${nextH}:${nextM}`);

  return (
    <div className={`oj-time${open ? " open" : ""}`} ref={wrapRef}>
      <button type="button" className="oj-time-btn" onClick={() => setOpen((v) => !v)}>
        <span>{`${h}:${m}`}</span>
        <i className="fa fa-clock"></i>
      </button>
      {open && (
        <div className="oj-time-pop">
          <div className="oj-time-col" ref={hourRef}>
            {TIME_HOURS.map((hh) => (
              <button
                key={hh}
                type="button"
                data-selected={hh === h ? "1" : undefined}
                className={hh === h ? "on" : ""}
                onClick={() => pick(hh, m)}
              >
                {hh}
              </button>
            ))}
          </div>
          <div className="oj-time-col" ref={minRef}>
            {minutes.map((mm) => (
              <button
                key={mm}
                type="button"
                data-selected={mm === m ? "1" : undefined}
                className={mm === m ? "on" : ""}
                onClick={() => pick(h, mm)}
              >
                {mm}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const STEP_TYPES = [
  { id: "clean", label: "Clean / Qualify", icon: "fa-broom", group: "research" },
  { id: "web_research", label: "Web Research", icon: "fa-globe", group: "research", licenseKey: "hasWebResearch" },
  { id: "instagram_research", label: "Instagram Research", icon: "fa-instagram", iconPrefix: "fab", group: "research", licenseKey: "hasInstagramResearch" },
  { id: "google_review_audit", label: "Google Review Audit", icon: "fa-star-half-stroke", group: "research", licenseKey: "hasGoogleReviewAudit" },
  { id: "data_connector", label: "Custom Researcher", icon: "fa-plug", group: "research", licenseKey: "hasDataConnectors" },
  { id: "chance_radar", label: "Chance Radar", icon: "fa-satellite-dish", group: "research" },
  { id: "potential_analyzer", label: "Potential Analyzer", icon: "fa-gauge-high", group: "research", licenseKey: "hasPotentialAnalyzer" },
  { id: "dm_email_finder", label: "DM Email Finder", icon: "fa-search", group: "research", licenseKey: "hasDmEmailFinder" },
  { id: "send_email", label: "Send Email", icon: "fa-envelope", group: "action" },
  { id: "add_tag", label: "Add Tag", icon: "fa-tag", group: "action" },
  { id: "add_to_flow", label: "Add to Flow", icon: "fa-project-diagram", group: "action" },
  { id: "wait", label: "Wait", icon: "fa-clock", group: "timing" },
  { id: "check_reply", label: "Check Reply", icon: "fa-reply", group: "timing" },
  { id: "end_flow", label: "End Flow", icon: "fa-flag-checkered", group: "action" },
];

// Web-Research sub-modes. Backend reads `step.researchModes` when that list is
// non-empty; otherwise it uses World Settings (`spySettings.activeModules`).
// Keep in sync with SPY_MODULES in LeadSpySettings.js — mirroring the ids here
// lets the step override modules per outreach agent without dragging the full
// spy-settings component into the builder. `core` is always applied so it isn't
// listed as an optional pick.
const WEB_RESEARCH_MODES = [
  { id: "email", label: "Email Discovery", icon: "fa-envelope", credits: 1, hint: "Extract & validate business emails" },
  { id: "phone", label: "Phone Discovery", icon: "fa-phone", credits: 0.5, hint: "Find & format phone numbers" },
  { id: "social", label: "Social Profiles", icon: "fa-share-alt", credits: 1, hint: "Instagram, LinkedIn, TikTok, …" },
  { id: "description", label: "AI Business Summary", icon: "fa-file-alt", credits: 1.5, hint: "Company overview + recent news" },
  { id: "seoAudit", label: "SEO Audit", icon: "fa-magnifying-glass-chart", credits: 2, hint: "Meta tags, headings, speed, wins", premium: true },
  { id: "companyProfile", label: "Company Size & Revenue", icon: "fa-building", credits: 1.5, hint: "Employees, revenue, HQ, growth", premium: true },
  { id: "shopIntelligence", label: "Shop Intelligence", icon: "fa-shopping-cart", credits: 1.5, hint: "Platform, catalog size, ratings", premium: true },
  { id: "decisionMakers", label: "Decision Makers", icon: "fa-user-tie", credits: 2, hint: "C-Level + direct contacts", premium: true },
  { id: "linkedinIntelligence", label: "LinkedIn Intel", icon: "fa-linkedin", iconPrefix: "fab", credits: 1.5, hint: "Company page + employee search", premium: true },
  { id: "deepCrawl", label: "Deep Website Crawl", icon: "fa-spider", credits: 2, hint: "Impressum, contact, about, team", premium: true },
];

// Popular starter flows shown in the right-side "Templates" drawer. Each preset
// is a plain step list — clicking Apply seeds the builder's step editor with
// fresh ids, so users can then tweak templates/tags without losing them. The
// templates never carry a templateId/tagId (those are user-specific and must
// be picked after applying).
const FLOW_TEMPLATES = [
  {
    id: "qualify_enrich_send",
    name: "Qualify → Enrich → Send",
    tagline: "Deep-vet each lead, enrich, then intro",
    steps: [
      { type: "clean", onFail: "remove", failAction: "tag" },
      { type: "potential_analyzer", onFail: "remove", failAction: "tag", minScore: 4 },
      { type: "web_research", onFail: "skip", failAction: "tag" },
      { type: "send_email" },
      { type: "end_flow" },
    ],
  },
  {
    id: "simple_intro_followup",
    name: "Simple Intro + 1 Follow-up",
    tagline: "Ship intro, wait, gentle follow-up",
    steps: [
      { type: "send_email" },
      { type: "wait", delayDays: 3, waitFrom: "after_send" },
      { type: "check_reply" },
      { type: "send_email" },
      { type: "end_flow" },
    ],
  },
  {
    id: "deep_research_send",
    name: "Deep Research First",
    tagline: "IG + web signals, then hyper-personal intro",
    steps: [
      { type: "web_research", onFail: "skip", failAction: "tag" },
      { type: "instagram_research", onFail: "skip", failAction: "tag" },
      { type: "clean", onFail: "remove", failAction: "tag" },
      { type: "send_email" },
      { type: "end_flow" },
    ],
  },
  {
    id: "persistent_sequence",
    name: "Persistent 3-Touch Sequence",
    tagline: "Three touches spaced across two weeks",
    steps: [
      { type: "send_email" },
      { type: "wait", delayDays: 4, waitFrom: "after_send" },
      { type: "check_reply" },
      { type: "send_email" },
      { type: "wait", delayDays: 6, waitFrom: "after_send" },
      { type: "check_reply" },
      { type: "send_email" },
      { type: "end_flow" },
    ],
  },
  {
    id: "dm_finder_then_send",
    name: "Find Decision-Maker → Send",
    tagline: "DM email finder, qualify, intro + FU",
    steps: [
      { type: "dm_email_finder", onFail: "remove", failAction: "tag", deepSearch: false },
      { type: "clean", onFail: "remove", failAction: "tag" },
      { type: "send_email" },
      { type: "wait", delayDays: 5, waitFrom: "after_send" },
      { type: "check_reply" },
      { type: "send_email" },
      { type: "end_flow" },
    ],
  },
];

const STATUS_OPTIONS = [
  { value: "Not Contacted", label: "Not Contacted", desc: "Fresh leads — never emailed" },
  { value: "Introduced", label: "Introduced", desc: "Got first email — ready for follow-up" },
  { value: "First Follow-up", label: "First Follow-up", desc: "Got 1st follow-up" },
  { value: "Second Follow-up", label: "Second Follow-up", desc: "Got 2nd follow-up" },
  { value: "Third Follow-up", label: "Third Follow-up", desc: "Got 3rd follow-up" },
];

const SORT_OPTIONS = [
  { value: "random", label: "Random", icon: "fa-random" },
  { value: "business_name_asc", field: "business_name", dir: "asc", label: "Business name A → Z", icon: "fa-sort-alpha-down" },
  { value: "business_name_desc", field: "business_name", dir: "desc", label: "Business name Z → A", icon: "fa-sort-alpha-up" },
  { value: "lead_name_asc", field: "lead_name", dir: "asc", label: "Contact name A → Z", icon: "fa-sort-alpha-down" },
  { value: "lead_name_desc", field: "lead_name", dir: "desc", label: "Contact name Z → A", icon: "fa-sort-alpha-up" },
  { value: "createdAt_asc", field: "createdAt", dir: "asc", label: "Oldest first", icon: "fa-sort-numeric-down" },
  { value: "createdAt_desc", field: "createdAt", dir: "desc", label: "Newest first", icon: "fa-sort-numeric-up" },
];

const EMPTY_CONTACT_POOL = { importName: "", importNames: [], statusFilter: "", tagFilter: "", sortBy: "random" };

const sortPoolDocs = (docs, sortBy) => {
  const list = [...docs];
  const sortDef = SORT_OPTIONS.find((s) => s.value === (sortBy || "random"));
  if (!sortDef || sortDef.value === "random") {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }
  return list.sort((a, b) => {
    const valA = (a.data()[sortDef.field] || "").toString().toLowerCase();
    const valB = (b.data()[sortDef.field] || "").toString().toLowerCase();
    const cmp = valA.localeCompare(valB);
    return sortDef.dir === "desc" ? -cmp : cmp;
  });
};

const buildPoolQuery = (userId, pool, importName, { requireEmail = true } = {}) => {
  let contactQuery = query(collection(firestore, `World/${userId}/db`));
  if (importName) {
    contactQuery = query(contactQuery, where("import_name", "==", importName));
  }
  if (pool.statusFilter) {
    contactQuery = query(contactQuery, where("lead_contacted", "==", pool.statusFilter));
  }
  if (pool.tagFilter) {
    contactQuery = query(contactQuery, where(`tags.${pool.tagFilter}.id`, "==", pool.tagFilter));
  }
  // Web research / DM finder write the address later — do not drop those
  // contacts before they ever reach the steps that can find it.
  if (requireEmail) {
    contactQuery = query(contactQuery, where("has_email", "==", true));
  }
  return contactQuery;
};

const persistContactPool = (pool) => applyPoolLists(pool || EMPTY_CONTACT_POOL, resolvePoolLists(pool));

const jobDailyLimit = (job) => {
  if (job?.deliveryMode === "prepare") {
    return Math.max(1, Math.min(job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT, PREPARE_DAILY_LIMIT_MAX));
  }
  return Math.max(1, job?.dailyLimit || 30);
};

const jobPaceOptions = (job, now) => ({
  dailyLimit: jobDailyLimit(job),
  startMs: now,
  skipWeekends: job?.deliveryMode === "prepare" ? false : job?.skipWeekends !== false,
  timezone: job?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
  alignNextDays: job?.deliveryMode === "prepare" ? "localMidnight" : "offset",
});

const JOB_STATUS_LABELS = {
  draft: "Draft",
  active: "Active",
  paused: "Paused",
  completed: "Completed",
};

const PREDEFINED_TAGS = [
  { id: 'dream_client', tag: 'Dream Client', color: '#8b5cf6', icon: 'fa-star' },
  { id: 'hot_lead', tag: 'Hot Lead', color: '#ef4444', icon: 'fa-fire' },
  { id: 'warm_lead', tag: 'Warm Lead', color: '#f59e0b', icon: 'fa-temperature-half' },
  { id: 'cold_lead', tag: 'Cold Lead', color: '#3b82f6', icon: 'fa-snowflake' },
  { id: 'qualified', tag: 'Qualified', color: '#22c55e', icon: 'fa-check-circle' },
  { id: 'not_qualified', tag: 'Not Qualified', color: '#6b7280', icon: 'fa-times-circle' },
  { id: 'decision_maker', tag: 'Decision Maker', color: '#ec4899', icon: 'fa-user-tie' },
  { id: 'influencer', tag: 'Influencer', color: '#06b6d4', icon: 'fa-bullhorn' },
  { id: 'high_value', tag: 'High Value', color: '#eab308', icon: 'fa-gem' },
  { id: 'high_budget', tag: 'High Budget', color: '#22c55e', icon: 'fa-money-bill-wave' },
  { id: 'medium_budget', tag: 'Medium Budget', color: '#f59e0b', icon: 'fa-money-bill' },
  { id: 'low_budget', tag: 'Low Budget', color: '#ef4444', icon: 'fa-coins' },
  { id: 'priority', tag: 'Priority', color: '#dc2626', icon: 'fa-exclamation-circle' },
  { id: 'follow_up_needed', tag: 'Follow Up Needed', color: '#f97316', icon: 'fa-clock' },
  { id: 'nurture', tag: 'Nurture', color: '#84cc16', icon: 'fa-seedling' },
  { id: 'engaged', tag: 'Engaged', color: '#14b8a6', icon: 'fa-comments' },
  { id: 'unresponsive', tag: 'Unresponsive', color: '#9ca3af', icon: 'fa-user-slash' },
  { id: 'interested', tag: 'Interested', color: '#10b981', icon: 'fa-thumbs-up' },
  { id: 'not_interested', tag: 'Not Interested', color: '#ef4444', icon: 'fa-thumbs-down' },
  { id: 'competitor', tag: 'Competitor', color: '#7c3aed', icon: 'fa-chess' },
  { id: 'partner_potential', tag: 'Partner Potential', color: '#0ea5e9', icon: 'fa-handshake' },
  { id: 'referral', tag: 'Referral', color: '#d946ef', icon: 'fa-share' },
  { id: 'vip', tag: 'VIP', color: '#fbbf24', icon: 'fa-crown' },
];

const generateId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const agentLabel = (name) => (name || "").replace(/^Outreach Job\b/i, "Outreach Agent");

// Firebase RTDB keys can't contain . # $ / [ ] — encode Firestore doc IDs for use as keys
const encodeRtdbKey = (key) => encodeURIComponent(key).replace(/\./g, '%2E');
const decodeRtdbKey = (key) => decodeURIComponent(key);

const QUALIFYING_STEPS = new Set(["clean", "web_research", "instagram_research", "google_review_audit", "data_connector", "potential_analyzer", "dm_email_finder"]);

// Adds a chip describing what happens to a removed contact (tag / delete /
// delete + blacklist). Only meaningful when the gate is set to "remove".
const pushFailActionChip = (chips, failAction) => {
  const fa = failAction || "tag";
  if (fa === "delete") {
    chips.push({ k: "fa", icon: "fa fa-trash-alt", label: "Delete contact", tone: "danger", title: "Dropped contacts are permanently deleted from the database" });
  } else if (fa === "delete_and_block") {
    chips.push({ k: "fa", icon: "fa fa-ban", label: "Delete & block", tone: "danger", title: "Dropped contacts are deleted and their email/domain is blacklisted" });
  } else {
    chips.push({ k: "fa", icon: "fa fa-tag", label: "Keep in database", tone: "slate", title: "Dropped contacts stay in the database with this agent's Failed status and leave the queue immediately" });
  }
};

const failGateHeading = (stepType) => {
  if (stepType === "potential_analyzer") return "If the score is too low";
  if (stepType === "dm_email_finder") return "If people were found but no email is";
  return "If this step fails";
};

const failGateRemoveDesc = (stepType) => {
  if (stepType === "potential_analyzer") {
    return "They leave this agent here — not marked completed. Choose below whether they stay in your database, get deleted, or get blocked.";
  }
  if (stepType === "clean") return "They leave this agent if AI qualification says they are not a fit. Choose below what happens to the contact.";
  if (stepType === "web_research") return "They leave this agent if the website crawl returns no data. Choose below what happens to the contact.";
  if (stepType === "instagram_research") return "They leave this agent if the Instagram profile can't be fetched. Choose below what happens to the contact.";
  if (stepType === "google_review_audit") return "They leave this agent if the business can't be found on Google Maps. Choose below what happens to the contact.";
  if (stepType === "data_connector") return "They leave this agent if the platform finds nothing for them or a field it needs is empty. Choose below what happens to the contact.";
  if (stepType === "dm_email_finder") {
    return "They leave this agent if decision makers exist but no usable email is found. Contacts with no decision makers skip this step and continue.";
  }
  return "They leave this agent if no email can be found. Choose below what happens to the contact.";
};

const failGateSkipDesc = (stepType) => {
  if (stepType === "potential_analyzer") {
    return "They stay in this agent and move to the next step — even if the score is too low. Not marked completed, not deleted.";
  }
  if (stepType === "dm_email_finder") {
    return "No email found, but they stay in this agent and move on. Contacts with no decision makers already skip this step automatically.";
  }
  return "This failed step is skipped. They stay in this agent and move to the next step. Not marked completed, not deleted.";
};

const failActionDesc = (failAction) => {
  if (failAction === "delete") {
    return "The contact is permanently deleted from your database. Gone from this agent and from your list.";
  }
  if (failAction === "delete_and_block") {
    return "The contact is deleted from your database and their email/domain is blacklisted, so they can never be contacted again.";
  }
  return "The contact stays in your database with this agent's Failed status. They leave the queue immediately — not marked completed.";
};

const pushOnFailChip = (chips, onFail, { removeLabel, removeTitle }) => {
  const drop = (onFail || "remove") === "remove";
  chips.push({
    k: "of",
    icon: drop ? "fa fa-user-slash" : "fa fa-forward",
    label: drop ? removeLabel : "Keep going",
    tone: drop ? "danger" : "slate",
    title: drop ? removeTitle : "Stays in this agent and moves to the next step",
  });
};

// A `send_email` step always writes the mail; what differs between the two
// delivery modes is whether the backend also delivers it. On a prepare-only
// agent nothing leaves without the user clicking send, so the step must never
// read "Send Email" — seeing "Send" on an agent you picked precisely because it
// doesn't send is what makes people distrust the mode. Every label, tooltip and
// validation message goes through here so the two can't drift apart.
const stepLabel = (stepType, isPrepare) => {
  if (isPrepare && stepType === "send_email") return "Prepare Email";
  return (STEP_TYPES.find((t) => t.id === stepType) || {}).label || stepType;
};

const PREVIEW_STEP_CAP = 8;

const sendStepMailHint = (step, topicOptions, userTopics) => {
  const ids = collectStepTemplateIds(step);
  const id = ids[0] || step.templateId;
  const named = (topicOptions || []).find((t) => t.id === id)?.name
    || step.templateName
    || "";
  if (ids.length > 1) return { name: `${ids.length} templates`, subject: "" };
  const topic = id && userTopics ? userTopics[id] : null;
  const subjects = Array.isArray(topic?.subjectLines) ? topic.subjectLines : [];
  const firstSubject = String(subjects.find((s) => (s.text || "").trim())?.text || "").trim();
  return { name: named, subject: firstSubject };
};

/**
 * Vertical sequence preview — the same language as the Popular Flow templates.
 * Job cards on the overview reuse this so an agent reads like the starter it
 * came from, with the assigned mail template sitting on each email step.
 */
const renderSequencePreview = (jobSteps, {
  isPrepare = false,
  includeStart = true,
  topicOptions,
  userTopics,
} = {}) => {
  const raw = Array.isArray(jobSteps) ? jobSteps : [];
  const withStart = includeStart
    ? [{ id: "__preview-start", type: "__start__" }, ...raw]
    : raw;
  const startPad = includeStart ? 1 : 0;
  const overflow = withStart.length > PREVIEW_STEP_CAP + startPad
    ? withStart.length - PREVIEW_STEP_CAP - startPad
    : 0;
  const previewSteps = overflow > 0
    ? (includeStart ? [withStart[0], ...raw.slice(0, PREVIEW_STEP_CAP)] : raw.slice(0, PREVIEW_STEP_CAP))
    : withStart;

  return (
    <div className="oj-template-preview">
      {previewSteps.map((ps, i) => {
        const isStart = ps.type === "__start__";
        const def = isStart ? null : STEP_TYPES.find((t) => t.id === ps.type);
        const label = isStart ? "Start" : stepLabel(ps.type, isPrepare);
        const hint = !isStart && ps.type === "send_email"
          ? sendStepMailHint(ps, topicOptions, userTopics)
          : null;
        const mail = hint && (hint.name || hint.subject) ? hint : null;
        return (
          <React.Fragment key={ps.id || `pv-${i}`}>
            <div
              className={`oj-template-preview-node${isStart ? " start" : ""}${ps.type === "end_flow" ? " end" : ""}${mail ? " mail" : ""}`}
              title={mail?.name ? `${label} · ${mail.name}` : label}
            >
              <span className="oj-template-preview-icon">
                {isStart
                  ? <img src="/img/star.svg" alt="" />
                  : <i className={`${def?.iconPrefix || "fa"} ${def?.icon || "fa-circle"}`} />}
              </span>
              <span className="oj-template-preview-copy">
                <span className="oj-template-preview-label">{label}</span>
                {mail?.name && <span className="oj-template-preview-mail">{mail.name}</span>}
                {mail?.subject && <span className="oj-template-preview-subject">{mail.subject}</span>}
              </span>
              {ps.type === "wait" && (
                <span className="oj-template-preview-badge">{ps.delayDays || 3}d</span>
              )}
            </div>
            {i < previewSteps.length - 1 && <div className="oj-template-preview-line" />}
          </React.Fragment>
        );
      })}
      {overflow > 0 && (
        <div className="oj-template-preview-more">+{overflow} more</div>
      )}
    </div>
  );
};

const listMapCount = (step, mapKey) => Object.keys(cleanListOverride(step && step[mapKey])).length;

const ListOverridePicker = ({
  contactPool,
  step,
  spec,
  options,
  optionLabel,
  heading,
  hint,
  singleHint,
  onChange,
}) => {
  const poolLists = resolvePoolLists(contactPool);
  if (poolLists.mode !== "lists" || poolLists.names.length < 2) {
    return poolLists.mode === "lists" && poolLists.names.length === 1 && singleHint
      ? <p className="oj-list-tpl-hint">{singleHint}</p>
      : null;
  }
  const mapped = cleanListOverride(step[spec.mapKey]);
  return (
    <div className="oj-list-tpl">
      <label className="oj-node-body-label">{heading}</label>
      <p className="oj-list-tpl-hint">{hint}</p>
      <div className="oj-list-tpl-grid">
        {poolLists.names.map((listName) => (
          <div className="oj-list-tpl-row" key={listName}>
            <span title={listName}>{prettyImportName(listName) || listName}</span>
            <select value={mapped[listName] || ""} onChange={(e) => onChange(listName, e.target.value)}>
              <option value="">Default</option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>{optionLabel(opt)}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

const validateSequence = (steps, isPrepare = false) => {
  const errors = [];
  if (steps.length === 0) return errors;
  const emailLabel = stepLabel("send_email", isPrepare);

  const sendSteps = steps.filter((s) => s.type === "send_email");
  if (sendSteps.some((s) => !s.templateId)) errors.push(`Every ${emailLabel} step needs a template.`);
  if (steps.filter((s) => s.type === "add_tag").some((s) => !s.tagId)) errors.push("Every Add Tag step needs a tag.");
  if (steps.filter((s) => s.type === "add_to_flow").some((s) => !s.flowId)) errors.push("Every Add to Flow step needs a flow.");
  if (steps.filter((s) => s.type === "data_connector").some((s) => !s.connectorId)) errors.push("Every Custom Researcher step needs a data source.");
  if (steps.filter((s) => s.type === "chance_radar").some((s) => !s.chanceRadarId)) errors.push("Every Chance Radar step needs a radar.");

  if (steps[0].type === "wait") {
    errors.push(`Sequence can't start with "Wait".`);
  }

  for (let i = 1; i < steps.length; i++) {
    if (steps[i].type === "wait" && steps[i - 1].type === "wait") {
      errors.push("Two Wait steps in a row (step " + i + " & " + (i + 1) + ") — combine them into one.");
      break;
    }
  }
  for (let i = 1; i < steps.length; i++) {
    if (steps[i].type === "check_reply" && steps[i - 1].type === "check_reply") {
      errors.push("Two Check Reply steps in a row — one is enough.");
      break;
    }
  }
  // Send email BEFORE qualifying steps (clean/research) — wrong order
  const firstSendIdx = steps.findIndex((s) => s.type === "send_email");
  if (firstSendIdx >= 0) {
    const qualifyAfterSend = steps.slice(firstSendIdx + 1).filter((s) => QUALIFYING_STEPS.has(s.type));
    for (const q of qualifyAfterSend) {
      const labelMap = {
        clean: "AI Clean",
        web_research: "Web Research",
        instagram_research: "Instagram Research",
        google_review_audit: "Google Review Audit",
        data_connector: "Custom Researcher",
        potential_analyzer: "Potential Analyzer",
        dm_email_finder: "DM Email Finder",
      };
      const label = labelMap[q.type] || q.type;
      errors.push(`"${label}" comes after "${emailLabel}" — qualifying steps must come first, otherwise the email is written without their findings.`);
      break;
    }
  }

  // Two send steps without a wait in between
  for (let i = 1; i < steps.length; i++) {
    if (steps[i].type === "send_email" && steps[i - 1].type === "send_email") {
      errors.push("Two " + emailLabel + " steps in a row (step " + i + " & " + (i + 1) + ") — add a Wait step between them.");
      break;
    }
  }

  return errors;
};

/**
 * API-trigger mode panel for the "Who" section.
 *
 * Rendered instead of the normal Status/Lists/Tag filters when the pool is in
 * API mode. Owns the ensure/revoke roundtrip for the agent's trigger key —
 * intentionally lightweight, no external state. The parent supplies + tracks
 * the current key value so it survives leaving the builder.
 */
const ApiModePanel = ({
  jobId,
  userId,
  apiTriggerKey,
  setApiTriggerKey,
  apiAllowReRun,
  setApiAllowReRun,
  apiKeyLoading,
  setApiKeyLoading,
  apiKeyCopied,
  setApiKeyCopied,
  deliveryMode,
  structureLocked,
  onDisable,
}) => {
  const [endpointUrl, setEndpointUrl] = useState("");

  // Fetch or generate the trigger key when the panel mounts, and whenever
  // the target job changes. Idempotent — the callable just returns the
  // existing key if one is already registered. Skipped for unsaved jobs
  // (they don't have an id yet); the save handler will run ensure() after
  // the first save.
  useEffect(() => {
    let cancelled = false;
    if (!jobId || !userId) return () => { cancelled = true; };
    (async () => {
      try {
        setApiKeyLoading(true);
        const ensureFn = httpsCallable(getFunctions(undefined, "europe-west1"), "ensureOutreachAgentKey");
        const resp = await ensureFn({ userId, jobId });
        if (cancelled) return;
        const key = resp?.data?.key;
        const url = resp?.data?.endpoint;
        if (key) setApiTriggerKey(key);
        if (url) setEndpointUrl(url);
      } catch (err) {
        console.warn("[ApiModePanel] ensureOutreachAgentKey failed:", err?.message || err);
      } finally {
        if (!cancelled) setApiKeyLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, userId, setApiTriggerKey, setApiKeyLoading]);

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 1500);
    } catch {
      // Older browsers: fallback to a hidden textarea if needed
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 1500);
    }
  };

  const rotateKey = async () => {
    if (!jobId || structureLocked) return;
    if (!window.confirm("Rotate the trigger key? Any external system using the old key will start getting 403 immediately.")) return;
    try {
      setApiKeyLoading(true);
      const revokeFn = httpsCallable(getFunctions(undefined, "europe-west1"), "revokeOutreachAgentKey");
      await revokeFn({ userId, jobId });
      const ensureFn = httpsCallable(getFunctions(undefined, "europe-west1"), "ensureOutreachAgentKey");
      const resp = await ensureFn({ userId, jobId });
      const nextKey = resp?.data?.key;
      if (nextKey) setApiTriggerKey(nextKey);
    } catch (err) {
      alert("Could not rotate the trigger key: " + (err?.message || err));
    } finally {
      setApiKeyLoading(false);
    }
  };

  const curlExample = apiTriggerKey && endpointUrl
    ? `curl -X POST ${endpointUrl} \\
  -H "x-api-key: YOUR_WORLDDB_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"agent":"${apiTriggerKey}","email":"lisa@acme.com","context":{"business_name":"Acme GmbH","dm_name":"Lisa Meier"}}'`
    : "";

  return (
    <div className="oj-sb-api-block">
      <div className="oj-sb-api-lead">
        <i className="fa fa-plug" aria-hidden="true"></i>
        <div>
          <strong>API trigger mode</strong>
          <p>External systems push one contact + context at a time. The agent prepares an email in the next tick.</p>
        </div>
      </div>

      {deliveryMode !== "prepare" && (
        <div className="oj-sb-hint warning">
          <i className="fa fa-exclamation-triangle"></i>
          API-trigger mode currently only works with Prepare delivery. Switch this agent to Prepare below before saving.
        </div>
      )}

      <div className="oj-sb-api-row">
        <label>Agent trigger key</label>
        <div className="oj-sb-api-key">
          <code>{apiKeyLoading ? "Loading…" : (apiTriggerKey || (jobId ? "Not registered yet — save to generate" : "Save the agent first to generate a key"))}</code>
          <button
            type="button"
            className="oj-sb-api-btn"
            onClick={() => copyToClipboard(apiTriggerKey)}
            disabled={!apiTriggerKey}
            title="Copy trigger key"
          >
            <i className={`fa ${apiKeyCopied ? "fa-check" : "fa-copy"}`}></i>
            {apiKeyCopied ? "Copied" : "Copy"}
          </button>
        </div>
        <span className="oj-sb-field-hint">Send this in the <code>agent</code> field of your POST body.</span>
      </div>

      {endpointUrl && (
        <div className="oj-sb-api-row">
          <label>Endpoint</label>
          <div className="oj-sb-api-key">
            <code>{endpointUrl}</code>
            <button
              type="button"
              className="oj-sb-api-btn"
              onClick={() => copyToClipboard(endpointUrl)}
              title="Copy endpoint URL"
            >
              <i className="fa fa-copy"></i> Copy
            </button>
          </div>
        </div>
      )}

      {curlExample && (
        <div className="oj-sb-api-row">
          <label>Example</label>
          <pre className="oj-sb-api-example">{curlExample}</pre>
          <button
            type="button"
            className="oj-sb-api-btn"
            onClick={() => copyToClipboard(curlExample)}
            title="Copy curl example"
          >
            <i className="fa fa-copy"></i> Copy example
          </button>
        </div>
      )}

      <div className="oj-sb-api-row">
        <div className="oj-sb-hint">
          <i className="fa fa-info-circle"></i>
          <div>
            <strong>Uses your WorldDB API key</strong>
            <p style={{ margin: "4px 0 8px" }}>Enable “Allow outreach triggers” on that key in Settings → API. Then POST <code>agent</code> + <code>email</code> (required) plus optional <code>context</code>.</p>
            <strong>Dedup behaviour</strong>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              <li>Existing email → reuse the contact.</li>
              <li>New email on a unique business domain → merge onto that contact.</li>
              <li>Ambiguous match → create a fresh contact.</li>
              <li>Free-mail domains (gmail.com, gmx.de, …) never match by domain.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="oj-sb-option">
        <div
          className="oj-sb-toggle-row"
          onClick={() => { if (!structureLocked) setApiAllowReRun((v) => !v); }}
        >
          <div className={`oj-sb-toggle${apiAllowReRun ? " on" : ""}`}><div className="oj-sb-toggle-knob"></div></div>
          <span>Allow re-trigger</span>
        </div>
        <p className="oj-sb-option-note">
          {apiAllowReRun
            ? "Repeat triggers for the same contact will start a fresh run through this agent."
            : "Contacts that already ran through this agent will get a 409 on the next trigger."}
        </p>
      </div>

      <div className="oj-sb-api-actions">
        <button
          type="button"
          className="oj-sb-api-btn ghost"
          onClick={rotateKey}
          disabled={!apiTriggerKey || apiKeyLoading || structureLocked}
        >
          <i className="fa fa-refresh"></i> Rotate key
        </button>
        <button
          type="button"
          className="oj-sb-api-btn ghost danger"
          onClick={() => {
            if (structureLocked) return;
            if (window.confirm("Turn API-trigger mode off? The key stays valid until you rotate it.")) {
              onDisable();
            }
          }}
          disabled={structureLocked}
        >
          <i className="fa fa-times"></i> Turn off API mode
        </button>
      </div>
    </div>
  );
};

const OutreachJobsSettings = ({ userId }) => {
  const { currentUserId } = useActiveWorld();
  // Which delivery modes this account is licensed for (Team-Dashboard).
  const { canSend, canPrepare, outreachLoading } = useOutreachAccess();
  const { research, researchLoading, researchContext } = useResearchAccess();
  const isStepLocked = (typeId) => {
    const def = STEP_TYPES.find((t) => t.id === typeId);
    if (!def?.licenseKey || researchLoading) return false;
    return research[def.licenseKey] !== true;
  };
  const { connectors: dataConnectors } = useDataConnectors(userId);
  const chanceRadars = useChanceRadars(userId);
  const connectorNameFor = (step) => dataConnectors.find((c) => c.id === step.connectorId)?.name || step.connectorName || "";

  // View state
  const [view, setView] = useState("list"); // list | builder | detail
  const [editingJobId, setEditingJobId] = useState(null);
  const [detailJobId, setDetailJobId] = useState(null);

  // The builder and detail views cover the screen from <body>, so the settings
  // page behind them must not keep scrolling under the overlay.
  useEffect(() => {
    if (view === "list") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [view]);

  // Jobs data
  const [jobs, setJobs] = useState({});

  // Builder state
  const [jobName, setJobName] = useState("");
  // 'send'    → the job delivers the emails itself
  // 'prepare' → the job generates them and parks them in the Prepared Mails strip
  //             for the user to review and send from Bulk mode.
  // New jobs start on prepare; the licence effect below falls back to send
  // when that mode is not unlocked.
  const [deliveryMode, setDeliveryMode] = useState("prepare");
  const [prepareDailyLimit, setPrepareDailyLimit] = useState(DEFAULT_PREPARE_DAILY_LIMIT);

  // Both modes are licensed separately, so the builder can land on a mode this
  // account may not run — on a fresh job, on an older job, or when a licence is
  // revoked mid-session. Correcting it in one place covers all three, and the
  // locked card below keeps the switch visible instead of silently swapping it.
  useEffect(() => {
    if (outreachLoading) return;
    if (deliveryMode === "prepare" && !canPrepare && canSend) setDeliveryMode("send");
    if (deliveryMode === "send" && !canSend && canPrepare) setDeliveryMode("prepare");
  }, [deliveryMode, canSend, canPrepare, outreachLoading]);
  const [dailyLimit, setDailyLimit] = useState(30);
  const [sendingWindowStart, setSendingWindowStart] = useState("09:00");
  const [sendingWindowEnd, setSendingWindowEnd] = useState("17:00");
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [recurring, setRecurring] = useState(false);
  const [prepOnly, setPrepOnly] = useState(false);
  const [steps, setSteps] = useState([]);
  const [contactPool, setContactPool] = useState({ importName: "", importNames: [], statusFilter: "", tagFilter: "", sortBy: "random" });
  // API-trigger mode is a new pool source next to `any` / `lists` / `manual`.
  // When active, the sidebar filters are hidden and the job waits for the
  // external trigger endpoint to enqueue contacts one-by-one. `apiTriggerKey`
  // and `apiAllowReRun` are job-level fields (not on the pool) so they can be
  // toggled without touching the audience contract. See outreachApi.js.
  const [apiTriggerKey, setApiTriggerKey] = useState("");
  const [apiAllowReRun, setApiAllowReRun] = useState(false);
  const [whoMenuOpen, setWhoMenuOpen] = useState(false);
  const whoMenuRef = useRef(null);
  useEffect(() => {
    if (!whoMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (whoMenuRef.current && !whoMenuRef.current.contains(event.target)) {
        setWhoMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [whoMenuOpen]);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [addingLists, setAddingLists] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [listLayout, setListLayout] = useState({ hidden: [], order: [] });
  // Right-side template drawer. Persisted in sessionStorage so the pane's open
  // state survives switching between list/detail/builder and page reloads within
  // the same tab (but doesn't leak across browser sessions).
  const [templatesOpen, setTemplatesOpen] = useState(() => {
    try { return sessionStorage.getItem('oj:templatesOpen') === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('oj:templatesOpen', templatesOpen ? '1' : '0'); } catch (_) {}
  }, [templatesOpen]);

  // Data for dropdowns
  const [importOptions, setImportOptions] = useState([]);
  const [userTopics, setUserTopics] = useState({});
  const [aiTags, setAiTags] = useState([]);
  const [flowOptions, setFlowOptions] = useState([]);
  const [senderEmails, setSenderEmails] = useState([]);
  const [cleanupGuidelines, setCleanupGuidelines] = useState([]);
  const [potentialProfiles, setPotentialProfiles] = useState([]);
  const [spyActiveModules, setSpyActiveModules] = useState([]);

  // Job activation
  const [isActivating, setIsActivating] = useState(false);
  const [activationPreview, setActivationPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // Stop dialog: preview counts from stopOutreachJob, then confirm.
  const [stopDialog, setStopDialog] = useState(null);
  const [dropoutJobId, setDropoutJobId] = useState(null);

  // Queue data for detail view
  const [queueData, setQueueData] = useState({});
  // Bounded, newest-first snapshot of the current detail job's ARCHIVED terminal
  // entries (completed/replied/failed/skipped). Finished entries are archived
  // out of the hot queue (Woodpecker-style), so the funnel loads a capped
  // window of them here purely to render the per-step contact lists — never the
  // whole archive. Counts come from job.stats, so this is display-only.
  const [archiveData, setArchiveData] = useState({});
  const [archiveWindow, setArchiveWindow] = useState(STEP_CONTACT_PAGE);
  const [archiveHasMore, setArchiveHasMore] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [stepListVisible, setStepListVisible] = useState({});
  const [queueListVisible, setQueueListVisible] = useState(STEP_CONTACT_PAGE);
  const [queueContactNames, setQueueContactNames] = useState({});
  // Per-contact reply flags captured from Firestore (independent of the queue
  // entry's status). Used to surface "effective replies" in the UI even when
  // the queue entry is still pending because `executeAt` hasn't fired yet —
  // i.e. the inbox fetcher has already flagged `lead_reply: true` on the
  // contact doc but the outreach scheduler hasn't woken the entry up yet.
  // Shape: { [contactId]: { leadReply: boolean, lastReplyTs: number|null } }
  const [contactReplyFlags, setContactReplyFlags] = useState({});
  // Inline-editable daily limit for the currently open detail job. Mirrors
  // `job.dailyLimit` and is committed back to RTDB on slider release / blur.
  // Kept separate from the Builder's `dailyLimit` so the two screens never
  // collide.
  const [editDailyLimit, setEditDailyLimit] = useState(null);
  const [savingDailyLimit, setSavingDailyLimit] = useState(false);
  // SafeGuard-rejected drafts for the currently open job (keyed by draft id).
  // Used in the Live Sequence to preview the generated email and approve/resend.
  const [rejectedDrafts, setRejectedDrafts] = useState({});
  const [expandedDraftId, setExpandedDraftId] = useState(null);
  const [expandedFailKey, setExpandedFailKey] = useState(null);
  const [failInsightByContact, setFailInsightByContact] = useState({});
  const [approvingDraftId, setApprovingDraftId] = useState(null);
  const [approveError, setApproveError] = useState(null);
  const [expandedStepIdx, setExpandedStepIdx] = useState(null);
  const [stepSettingsOpen, setStepSettingsOpen] = useState({});
  const [globalSafeGuardOn, setGlobalSafeGuardOn] = useState(false);

  // Account daily email limit (from user's plan)
  const [accountDailyLimit, setAccountDailyLimit] = useState(EMAIL_PLAN_LIMITS.standard);
  const [preparedQueueCap, setPreparedQueueCap] = useState(PREPARED_QUEUE_CAP_DEFAULT);
  useEffect(() => {
    if (!userId) return;
    let membershipFlag = null;
    let creditPlanVal = null;
    const applyPreparedCap = () => {
      const planId = resolveMembershipPlanId(creditPlanVal, { membershipPlan: membershipFlag });
      setPreparedQueueCap(preparedQueueCapForPlan(planId));
    };
    const unsubUser = onValue(ref(database, `Users/${userId}`), (snap) => {
      const u = snap.val();
      if (u) {
        setAccountDailyLimit(
          u.dailyEmailLimit || EMAIL_PLAN_LIMITS[u.emailPlan || "standard"] || EMAIL_PLAN_LIMITS.standard
        );
        membershipFlag = u.membershipPlan || null;
        applyPreparedCap();
      }
    });
    const unsubPlan = onValue(ref(database, `World/${userId}/creditPlan`), (snap) => {
      creditPlanVal = snap.exists() ? snap.val() : null;
      applyPreparedCap();
    });
    const unsubSafeGuard = onValue(
      ref(database, `World/${userId}/settings/safeGuardSettings/enabled`),
      (snap) => setGlobalSafeGuardOn(snap.val() === true)
    );
    const unsubSpy = onValue(
      ref(database, `World/${userId}/settings/spySettings/activeModules`),
      (snap) => setSpyActiveModules(asModuleList(snap.val()))
    );
    return () => {
      unsubUser();
      unsubPlan();
      unsubSafeGuard();
      unsubSpy();
    };
  }, [userId]);

  // Test run state
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState("");
  const [testSearchResults, setTestSearchResults] = useState([]);
  const [isTestSearching, setIsTestSearching] = useState(false);
  const [testRunStatus, setTestRunStatus] = useState(null); // { type: 'success'|'error', msg }
  const testSearchTimer = useRef(null);

  // Load jobs
  useEffect(() => {
    if (!userId) return;
    const jobsRef = ref(database, `World/${userId}/settings/outreachJobs`);
    const unsub = onValue(jobsRef, (snap) => {
      setJobs(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, [userId]);

  // Deep-link entry from the WorldDb "Outreach Job pausiert"-Modal:
  // OutreachJobPausedModal stashes the jobId in sessionStorage before it
  // navigates here, and we open the detail view for exactly that job on
  // first paint. Consumed once and cleared, so a plain reload later lands
  // on the normal list view.
  useEffect(() => {
    if (!userId || Object.keys(jobs).length === 0) return;
    let target;
    try { target = sessionStorage.getItem("openOutreachJobId"); } catch (_) { target = null; }
    if (!target) return;
    try { sessionStorage.removeItem("openOutreachJobId"); } catch (_) {}
    if (jobs[target]) {
      setDetailJobId(target);
      setView("detail");
    }
  }, [userId, jobs]);

  // Load import options
  useEffect(() => {
    if (!userId) return;
    const loadImports = async () => {
      try {
        const importNamesRef = collection(firestore, `World/${userId}/import_names`);
        const q = query(importNamesRef, orderBy("name", "asc"));
        const snap = await getDocs(q);
        setImportOptions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error loading import options:", e);
      }
    };
    loadImports();
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const unsub = onValue(ref(database, `World/${userId}/settings/importListLayout`), (snap) => {
      setListLayout(sanitizeLayout(snap.val()));
    });
    return () => unsub();
  }, [userId]);

  // Load user topics (templates) – same pattern as AgentsSettings
  useEffect(() => {
    if (!userId) return;

    const selectedTopicsRef = ref(database, `World/${userId}/settings/selectedTopics`);
    const customTopicsRef = ref(database, `World/${userId}/settings/customTopics`);

    const unsubSelected = onValue(selectedTopicsRef, (snap) => {
      if (snap.exists()) setUserTopics(prev => ({ ...prev, ...snap.val() }));
    });
    const unsubCustom = onValue(customTopicsRef, (snap) => {
      if (snap.exists()) setUserTopics(prev => ({ ...prev, ...snap.val() }));
    });

    return () => { unsubSelected(); unsubCustom(); };
  }, [userId]);

  // Load AI tags
  useEffect(() => {
    if (!userId) return;
    const tagsRef = ref(database, `World/${userId}/settings/aiTags`);
    const unsub = onValue(tagsRef, (snap) => {
      if (snap.exists()) {
        const tagsObj = snap.val();
        setAiTags(Object.entries(tagsObj).map(([id, data]) => ({ id, ...data })));
      } else {
        setAiTags([]);
      }
    });
    return () => unsub();
  }, [userId]);

  // Load cleanup guidelines (multi-guideline system)
  useEffect(() => {
    if (!userId) return;
    const guidelinesRef = ref(database, `World/${userId}/settings/cleanupGuidelinesList`);
    const unsub = onValue(guidelinesRef, (snap) => {
      if (snap.exists()) {
        const obj = snap.val();
        const arr = Object.entries(obj).map(([id, data]) => ({ id, ...data })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setCleanupGuidelines(arr);
      } else {
        setCleanupGuidelines([]);
      }
    });
    return () => unsub();
  }, [userId]);

  // Load Potential-Analyzer ICP profiles (multi-profile system)
  useEffect(() => {
    if (!userId) return;
    const profilesRef = ref(database, potentialProfilesPath(userId));
    const unsub = onValue(profilesRef, (snap) => {
      setPotentialProfiles(profilesFromSnapshot(snap.exists() ? snap.val() : null));
    });
    return () => unsub();
  }, [userId]);

  // Load warmup flows
  useEffect(() => {
    if (!userId) return;
    const flowsRef = ref(database, `World/${userId}/settings/warmupFlows`);
    const unsub = onValue(flowsRef, (snap) => {
      if (snap.exists()) {
        const obj = snap.val();
        setFlowOptions(Object.entries(obj).map(([id, data]) => ({ id, name: data.name || "Unnamed Flow", steps: data.steps || [] })));
      } else {
        setFlowOptions([]);
      }
    });
    return () => unsub();
  }, [userId]);

  // Load sender emails from Firestore (same source as EmailAccountManager, EmailAiPopup, etc.)
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(firestore, 'world_users', userId, 'emailAccounts'), orderBy('email'));
    const unsub = onSnapshot(q, (snapshot) => {
      const accounts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSenderEmails(accounts);
    }, () => setSenderEmails([]));
    return () => unsub();
  }, [userId]);

  // Load queue data for active jobs
  useEffect(() => {
    if (!userId) return;
    const queueRef = ref(database, `outreachJobQueue/${userId}`);
    const unsub = onValue(queueRef, (snap) => {
      setQueueData(snap.exists() ? snap.val() : {});
    });
    return () => unsub();
  }, [userId]);

  // Newest-first archive pages (5 at a time). Counts come from job.stats, so
  // this is only for the contact list — never the whole archive.
  useEffect(() => {
    if (view !== "detail" || !detailJobId || !userId) {
      setArchiveData({});
      setArchiveHasMore(false);
      return;
    }
    let cancelled = false;
    setArchiveLoading(true);
    (async () => {
      try {
        const snap = await get(rtdbQuery(
          ref(database, `outreachJobArchive/${userId}/${detailJobId}`),
          orderByChild("completedAt"),
          limitToLast(archiveWindow)
        ));
        if (cancelled) return;
        const val = snap.exists() ? snap.val() : {};
        setArchiveData(val);
        setArchiveHasMore(Object.keys(val).length >= archiveWindow);
      } catch (e) {
        console.warn("[OutreachJobs] Failed to load archived entries:", e?.message);
        if (!cancelled) {
          setArchiveData({});
          setArchiveHasMore(false);
        }
      } finally {
        if (!cancelled) setArchiveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [view, detailJobId, userId, archiveWindow]);

  // Reset contact name / reply-flag cache when switching between jobs
  useEffect(() => {
    setQueueContactNames({});
    setContactReplyFlags({});
    setEditDailyLimit(null);
    setArchiveData({});
    setArchiveWindow(STEP_CONTACT_PAGE);
    setArchiveHasMore(false);
    setStepListVisible({});
    setQueueListVisible(STEP_CONTACT_PAGE);
    setExpandedStepIdx(null);
    setStepSettingsOpen({});
  }, [detailJobId]);

  // Inline daily-limit save (used from the Detail view). Allowed for any job
  // that isn't terminal (active / paused / draft). Prepare jobs edit their prepare
  // quota instead of the send limit, so the field written and the ceiling applied
  // both depend on the job's delivery mode.
  const saveJobDailyLimit = useCallback(
    async (jobId, value) => {
      if (!userId || !jobId) return;
      const isPrepare = jobs[jobId]?.deliveryMode === "prepare";
      const field = isPrepare ? "prepareDailyLimit" : "dailyLimit";
      const ceiling = isPrepare ? PREPARE_DAILY_LIMIT_MAX : Math.max(1, accountDailyLimit || 1);
      const next = Math.max(1, Math.min(parseInt(value, 10) || 1, ceiling));
      const current = jobs[jobId]?.[field];
      if (next === current) {
        setEditDailyLimit(null);
        return;
      }
      try {
        setSavingDailyLimit(true);
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          [field]: next,
          updatedAt: Date.now(),
        });

        // Retimes pending contacts to the new pace. Raise → pull later
        // contacts into today's leftover quota. Lower → park the overflow
        // on later working days. Wait-step pauses stay untouched.
        const job = jobs[jobId];
        const now = Date.now();
        const timezone = job?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
        const todayKey = isPrepare
          ? getLocalDayKey(timezone, now)
          : new Date().toISOString().slice(0, 10);
        let usedToday = 0;
        try {
          const statsSnap = await get(ref(database, `World/${userId}/outreachJobStats/${todayKey}`));
          usedToday = usedTodayFromStats(statsSnap.exists() ? statsSnap.val() : null, {
            isPrepare,
            jobId,
          });
        } catch (_) { /* quota read is best-effort; processor still enforces */ }

        const skipWeekends = isPrepare ? false : job?.skipWeekends !== false;
        const entries = Object.entries(queueData)
          .filter(([, e]) => e && e.jobId === jobId)
          .map(([key, e]) => ({ key, ...e }));
        const timed = planPaceReschedule({
          entries,
          steps: job?.steps || [],
          now,
          newLimit: next,
          usedToday,
          nextDayStartMs: isPrepare
            ? nextLocalDayStart(now, timezone)
            : stepWorkingDay(now, timezone, skipWeekends),
          skipWeekends,
          timezone,
          alignNextDays: isPrepare ? "localMidnight" : "offset",
        });
        const keys = Object.keys(timed);
        const CHUNK = 400;
        for (let i = 0; i < keys.length; i += CHUNK) {
          const chunk = {};
          for (const key of keys.slice(i, i + CHUNK)) {
            chunk[`${key}/executeAt`] = timed[key];
          }
          await update(ref(database, `outreachJobQueue/${userId}`), chunk);
        }

        // Clear local override; the snapshot listener will re-render with the
        // freshly saved value coming back from RTDB.
        setEditDailyLimit(null);
      } catch (err) {
        console.error("Failed to update daily limit:", err);
      } finally {
        setSavingDailyLimit(false);
      }
    },
    [userId, accountDailyLimit, jobs, queueData]
  );

  // Load SafeGuard-rejected drafts for the current detail job.
  // These drafts are created by processOutreachJobs when SafeGuard blocks an email;
  // the user can review the generated content and approve it for sending.
  useEffect(() => {
    if (!userId || !detailJobId || view !== "detail") {
      setRejectedDrafts({});
      setExpandedDraftId(null);
      setExpandedFailKey(null);
      setFailInsightByContact({});
      return;
    }
    const q = query(
      collection(firestore, "world_drafts", userId, "emails"),
      where("jobId", "==", detailJobId),
      where("errorReason", "==", "safeguard_rejected")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const byId = {};
        snap.docs.forEach((d) => { byId[d.id] = { id: d.id, ...d.data() }; });
        setRejectedDrafts(byId);
      },
      (err) => {
        console.warn("[OutreachJobs] Failed to load rejected drafts:", err?.message);
        setRejectedDrafts({});
      }
    );
    return () => unsub();
  }, [userId, detailJobId, view]);

  // Index rejected drafts by (jobId|contactId|stepIndex) for quick lookup against queue entries.
  const rejectedDraftByKey = React.useMemo(() => {
    const map = {};
    Object.values(rejectedDrafts).forEach((d) => {
      if (!d.leadId) return;
      const stepIdx = typeof d.stepIndex === "number" ? d.stepIndex : 0;
      const key = `${d.jobId || ""}|${d.leadId}|${stepIdx}`;
      // If multiple drafts exist for same (contact, step), keep the most recent one
      const existing = map[key];
      const newTs = d.timestamp?.seconds || d.timestamp?._seconds || new Date(d.date || 0).getTime() / 1000;
      const existingTs = existing?.timestamp?.seconds || existing?.timestamp?._seconds || new Date(existing?.date || 0).getTime() / 1000;
      if (!existing || newTs >= existingTs) map[key] = d;
    });
    return map;
  }, [rejectedDrafts]);

  // Approve a SafeGuard-rejected draft: calls the Cloud Function which re-queues
  // the contact with bypassSafeguard=true, then deletes the draft.
  const approveRejectedDraft = useCallback(async (draftId) => {
    if (!draftId) return;
    setApprovingDraftId(draftId);
    setApproveError(null);
    try {
      const fnInstance = getFunctions(undefined, "europe-west1");
      const approveFn = httpsCallable(fnInstance, "approveRejectedOutreachEmail");
      await approveFn({ draftId });
      setExpandedDraftId(null);
    } catch (err) {
      console.error("[OutreachJobs] approveRejectedOutreachEmail failed:", err);
      setApproveError(err?.message || "Fehler beim Freigeben der Email");
    } finally {
      setApprovingDraftId(null);
    }
  }, []);

  const toggleFailInfo = useCallback(async (qKey, contactId, entry) => {
    const next = expandedFailKey === qKey ? null : qKey;
    setExpandedFailKey(next);
    setExpandedDraftId(null);
    if (!next || !userId || !contactId) return;
    if (entry?.failAnalysis || failInsightByContact[contactId]) return;
    try {
      const snap = await getDoc(doc(firestore, `World/${userId}/db/${contactId}`));
      if (!snap.exists()) return;
      const data = snap.data() || {};
      if (data.potential_analysis || data.potential_score != null) {
        setFailInsightByContact((prev) => ({
          ...prev,
          [contactId]: compactFailAnalysis(data.potential_analysis || {}, {
            score: data.potential_score,
          }),
        }));
      }
    } catch (_) { /* insight is optional — the fail reason still shows */ }
  }, [expandedFailKey, failInsightByContact, userId]);

  const detailQueueEntries = useMemo(
    () => (detailJobId ? mergeJobEntries(queueData, archiveData, detailJobId) : []),
    [queueData, archiveData, detailJobId]
  );

  // Names + reply flags only for rows the user can currently see (5 at a time).
  useEffect(() => {
    if (view !== "detail" || !detailJobId || !userId) return;
    const stepPairs = expandedStepIdx == null
      ? []
      : contactsAtStep(detailQueueEntries, expandedStepIdx);
    const unknownIds = [...new Set([
      ...visibleContactIds(stepPairs, stepListVisible[expandedStepIdx]),
      ...visibleContactIds(detailQueueEntries, queueListVisible),
    ].filter((cId) => cId && !queueContactNames[cId]))];
    if (unknownIds.length === 0) return;

    const loadNames = async () => {
      const names = { ...queueContactNames };
      const flags = { ...contactReplyFlags };
      const insights = { ...failInsightByContact };
      const batchSize = 10;
      for (let i = 0; i < unknownIds.length; i += batchSize) {
        const batch = unknownIds.slice(i, i + batchSize);
        const q = query(
          collection(firestore, `World/${userId}/db`),
          where("__name__", "in", batch)
        );
        try {
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const data = d.data();
            names[d.id] = data.business_name || data.lead_email || d.id.slice(0, 12);
            if (data.potential_analysis || data.potential_score != null) {
              insights[d.id] = compactFailAnalysis(data.potential_analysis || {}, {
                score: data.potential_score,
              });
            }
            // Capture reply flag so the UI can surface "effective replies"
            // before the scheduler marks the queue entry terminal.
            flags[d.id] = {
              leadReply: data.lead_reply === true,
              lastReplyTs:
                typeof data.lead_last_reply_timestamp === "number"
                  ? data.lead_last_reply_timestamp
                  : null,
            };
          });
        } catch (e) {
          batch.forEach((id) => { names[id] = id.slice(0, 12); });
        }
      }
      setQueueContactNames(names);
      setContactReplyFlags(flags);
      setFailInsightByContact(insights);
    };
    loadNames();
  }, [view, detailJobId, userId, detailQueueEntries, expandedStepIdx, stepListVisible, queueListVisible]);

  // Builder helpers
  const generateJobName = useCallback(() => {
    const existing = Object.values(jobs);
    const base = "Outreach Agent";
    let num = existing.length + 1;
    let name = `${base} #${num}`;
    const names = new Set(existing.map((j) => j.name));
    while (names.has(name)) { num++; name = `${base} #${num}`; }
    return name;
  }, [jobs]);

  const resetBuilder = useCallback(() => {
    setJobName(generateJobName());
    setDeliveryMode("prepare");
    setPrepareDailyLimit(DEFAULT_PREPARE_DAILY_LIMIT);
    setDailyLimit(30);
    setSendingWindowStart("09:00");
    setSendingWindowEnd("17:00");
    setSkipWeekends(true);
    setRecurring(false);
    setPrepOnly(false);
    setSteps([]);
    setContactPool({ ...EMPTY_CONTACT_POOL });
    setApiTriggerKey("");
    setApiAllowReRun(false);
    setWhoMenuOpen(false);
    setEditingJobId(null);
    setActivationPreview(null);
    setShowValidation(false);
  }, [generateJobName]);

  const openBuilder = useCallback((jobId = null) => {
    if (jobId && jobs[jobId]) {
      const job = jobs[jobId];
      setJobName(agentLabel(job.name) || "");
      // Jobs created before prepare mode existed have no deliveryMode → they send.
      setDeliveryMode(job.deliveryMode === "prepare" ? "prepare" : "send");
      setPrepareDailyLimit(Math.min(job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT, PREPARE_DAILY_LIMIT_MAX));
      setDailyLimit(job.dailyLimit || 30);
      setSendingWindowStart(job.sendingWindowStart || "09:00");
      setSendingWindowEnd(job.sendingWindowEnd || "17:00");
      setSkipWeekends(job.skipWeekends !== false);
      setRecurring(job.recurring === true);
      setPrepOnly(job.prepOnly === true);
      setSteps(job.steps || []);
      setContactPool(job.contactPool || { ...EMPTY_CONTACT_POOL });
      setApiTriggerKey(typeof job.apiTriggerKey === "string" ? job.apiTriggerKey : "");
      setApiAllowReRun(job.apiAllowReRun === true);
      setWhoMenuOpen(false);
      setEditingJobId(jobId);
    } else {
      setJobName(generateJobName());
      setDeliveryMode("prepare");
      setPrepareDailyLimit(DEFAULT_PREPARE_DAILY_LIMIT);
      setDailyLimit(30);
      setSendingWindowStart("09:00");
      setSendingWindowEnd("17:00");
      setSkipWeekends(true);
      setRecurring(false);
      setPrepOnly(false);
      setSteps([]);
      setContactPool({ ...EMPTY_CONTACT_POOL });
      setApiTriggerKey("");
      setApiAllowReRun(false);
      setWhoMenuOpen(false);
      setEditingJobId(null);
      setActivationPreview(null);
    }
    setShowValidation(false);
    setView("builder");
  }, [jobs, generateJobName]);

  const editingJob = editingJobId ? jobs[editingJobId] : null;
  const structureLocked = isLiveJob(editingJob);

  const addStep = (type) => {
    if (structureLocked || isStepLocked(type)) return;
    const newStep = { id: generateId(), type, order: steps.length };
    if (type === "send_email") {
      newStep.templateId = "";
      newStep.templateName = "";
      newStep.senderAccountIds = [];
    }
    if (type === "clean" || type === "web_research" || type === "instagram_research") {
      newStep.onFail = "remove";
      newStep.failAction = "tag";
    }
    if (type === "google_review_audit") {
      newStep.onFail = "skip";
      newStep.failAction = "tag";
    }
    if (type === "data_connector") {
      newStep.onFail = "skip";
      newStep.failAction = "tag";
      const only = dataConnectors.length === 1 ? dataConnectors[0] : null;
      newStep.connectorId = only?.id || "";
      newStep.connectorName = only?.name || "";
    }
    if (type === "chance_radar") {
      newStep.onFail = "skip";
      newStep.failAction = "tag";
      const onlyRadar = chanceRadars.length === 1 ? chanceRadars[0] : null;
      newStep.chanceRadarId = onlyRadar?.id || "";
      newStep.chanceRadarName = onlyRadar?.name || "";
    }
    if (type === "web_research") {
      // null → backend uses World Settings (spySettings.activeModules).
      newStep.researchModes = null;
    }
    if (type === "potential_analyzer") {
      newStep.onFail = "remove";
      newStep.failAction = "tag";
      newStep.minScore = 4;
      // null → backend resolves the default ICP profile at runtime.
      newStep.icpProfileId = null;
      newStep.icpProfileName = "";
    }
    if (type === "dm_email_finder") {
      newStep.onFail = "remove";
      newStep.failAction = "tag";
      newStep.requireVerified = false;
      newStep.deepSearch = false;
    }
    if (type === "add_tag") {
      newStep.tagId = "";
      newStep.tagName = "";
    }
    if (type === "add_to_flow") {
      newStep.flowId = "";
      newStep.flowName = "";
    }
    if (type === "wait") {
      newStep.delayDays = 3;
      // Pause starts when the previous mail is sent. Draft time in Prepared
      // does not count, so a 4-day wait is 4 days after Send.
      newStep.waitFrom = "after_send";
    }
    setSteps((prev) => [...prev, newStep]);
  };

  const updateStep = (idx, updates) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...updates } : s)));
  };

  const setStepListOverride = (idx, listName, nextId, options, spec) => {
    setSteps((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const map = { ...cleanListOverride(s[spec.mapKey]) };
      const names = { ...readListOverrideNames(s, spec) };
      const currentDefault = typeof s[spec.idKey] === "string" ? s[spec.idKey] : "";
      if (!nextId || nextId === currentDefault) {
        delete map[listName];
        delete names[listName];
      } else {
        map[listName] = nextId;
        const opt = (options || []).find((o) => o.id === nextId);
        names[listName] = opt?.name || "";
      }
      return { ...s, [spec.mapKey]: map, [spec.namesKey]: names };
    }));
  };

  const setStepListTemplate = (idx, listName, templateId, topicList) => {
    setStepListOverride(idx, listName, templateId, topicList, LIST_MAP_TEMPLATE);
  };

  const removeStep = (idx) => {
    if (structureLocked) return;
    setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };

  const moveStep = (idx, direction) => {
    if (structureLocked) return;
    const newSteps = [...steps];
    const target = idx + direction;
    if (target < 0 || target >= newSteps.length) return;
    [newSteps[idx], newSteps[target]] = [newSteps[target], newSteps[idx]];
    setSteps(newSteps.map((s, i) => ({ ...s, order: i })));
  };

  // Replace the current sequence with the steps from a FLOW_TEMPLATES preset.
  // We (a) confirm before wiping user work when the sequence has content, and
  // (b) generate fresh ids so the same template can be applied twice without
  // React key collisions. Locked steps are silently skipped so a template
  // designed around a premium feature still applies cleanly for accounts that
  // don't have every research module licensed.
  const applyTemplate = (template) => {
    if (structureLocked) return;
    if (!template || !Array.isArray(template.steps)) return;
    if (steps.length > 0) {
      const ok = window.confirm(
        `Replace current ${steps.length} step${steps.length === 1 ? "" : "s"} with "${template.name}"?`
      );
      if (!ok) return;
    }
    const seeded = template.steps
      .filter((s) => !isStepLocked(s.type))
      .map((s, i) => ({ ...s, id: generateId(), order: i }));
    setSteps(seeded);
  };

  // One-time: queue a list that was not part of the original target.
  // Status, tag and delivery stay. A paused agent keeps the contacts until resume.
  const handleAddLists = async (extraNames) => {
    const jobId = editingJobId;
    const job = jobId ? jobs[jobId] : null;
    if (!job || !isLiveJob(job) || addingLists) return;
    const names = listsNotOnPool(contactPool, extraNames);
    if (!names.length) return;
    setAddingLists(true);
    try {
      const requireEmail = !jobCanDiscoverEmail(job.steps);
      const groupSnaps = await Promise.all(
        names.map((name) => getDocs(buildPoolQuery(userId, contactPool, name, { requireEmail }))),
      );
      const freshGroups = groupSnaps.map((snap) =>
        snap.docs.filter((docSnap) => {
          const history = docSnap.data().outreachHistory;
          return !history || !history[jobId];
        }),
      );
      const freshContacts = interleaveGroups(freshGroups.map((group) => sortPoolDocs(group, contactPool.sortBy)));
      const importById = new Map(freshContacts.map((d) => [d.id, d.data()?.import_name || ""]));
      const statusById = new Map(freshContacts.map((d) => [d.id, d.data()?.lead_contacted || "Not Contacted"]));

      const existingQueueSnap = await get(ref(database, `outreachJobQueue/${userId}`));
      const existingQueue = existingQueueSnap.exists() ? existingQueueSnap.val() : {};
      const archiveSnap = await get(ref(database, `outreachJobArchive/${userId}/${jobId}`));
      const archive = archiveSnap.exists() ? archiveSnap.val() : {};
      const queueRows = Object.values(existingQueue).map((entry) => (
        entry ? { ...entry, sameJob: entry.jobId === jobId } : entry
      ));
      const { blocked, backlogAts } = queueGuardForAddedLists(queueRows, Object.values(archive || {}));
      const eligibleIds = freshContacts.map((d) => d.id).filter((id) => !blocked.has(id));
      const skippedCount = freshContacts.length - eligibleIds.length;
      const now = Date.now();
      const listLabel = names.map(prettyImportName).join(", ");
      const mode = resolvePoolLists(contactPool).mode;
      const nextPool = mode === "lists" ? appendPoolLists(contactPool, names) : contactPool;

      if (eligibleIds.length > 0) {
        const executeAts = executeAtsAfterBacklog(
          eligibleIds.length,
          backlogAts,
          jobPaceOptions(job, now),
        );
        const CHUNK = 200;
        for (let i = 0; i < eligibleIds.length; i += CHUNK) {
          const queueUpdates = {};
          eligibleIds.slice(i, i + CHUNK).forEach((cid, offset) => {
            const index = i + offset;
            queueUpdates[encodeRtdbKey(cid)] = {
              contactId: cid,
              jobId,
              importName: importById.get(cid) || "",
              statusBeforeJob: statusById.get(cid) || "Not Contacted",
              currentStepIndex: 0,
              executeAt: executeAts[index],
              enteredStepAt: now,
              status: "pending",
              lastStepResult: null,
              stepHistory: {},
              retryCount: 0,
              manualSendDetected: false,
              enqueuedBy: "added_list",
            };
          });
          await update(ref(database, `outreachJobQueue/${userId}`), queueUpdates);
        }
        if (!job.prepOnly) {
          const BATCH_LIMIT = 499;
          for (let i = 0; i < eligibleIds.length; i += BATCH_LIMIT) {
            const chunk = eligibleIds.slice(i, i + BATCH_LIMIT);
            const batch = writeBatch(firestore);
            chunk.forEach((cid) => {
              batch.update(doc(firestore, `World/${userId}/db/${cid}`), { lead_contacted: "In Outreach" });
            });
            await batch.commit();
          }
        }
        await logOutreachEnrollment(firestore, userId, eligibleIds, {
          jobId,
          jobName: job.name,
          detail: `List added: ${listLabel}`,
          at: now,
        });
        const prevIds = Array.isArray(job.contactIds)
          ? job.contactIds
          : Object.values(job.contactIds || {}).filter(Boolean);
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          contactIds: [...prevIds, ...eligibleIds],
          ...(mode === "lists" ? { contactPool: persistContactPool(nextPool) } : {}),
          updatedAt: now,
        });
        await runTransaction(
          ref(database, `World/${userId}/settings/outreachJobs/${jobId}/stats/totalContacts`),
          (current) => (Number(current) || 0) + eligibleIds.length,
        );
      } else if (mode === "lists") {
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          contactPool: persistContactPool(nextPool),
          updatedAt: now,
        });
      }

      if (mode === "lists") setContactPool(nextPool);
      const paused = job.status === "paused";
      if (eligibleIds.length === 0) {
        alert(mode === "lists"
          ? `No new contacts on ${listLabel} match this agent's status and tag. The list stays on the agent.`
          : `No new contacts on ${listLabel} match this agent's status and tag.`);
      } else if (paused) {
        alert(`Added ${eligibleIds.length} contact${eligibleIds.length === 1 ? "" : "s"} from ${listLabel}. They wait in this agent until you resume it.${skippedCount ? ` ${skippedCount} already in a queue were left out.` : ""}`);
      } else if (backlogAts.length > 0) {
        alert(`Added ${eligibleIds.length} contact${eligibleIds.length === 1 ? "" : "s"} from ${listLabel}. They line up after the contacts already waiting.${skippedCount ? ` ${skippedCount} already in a queue were left out.` : ""}`);
      } else {
        alert(`Added ${eligibleIds.length} contact${eligibleIds.length === 1 ? "" : "s"} from ${listLabel}.${skippedCount ? ` ${skippedCount} already in a queue were left out.` : ""}`);
      }
      return true;
    } catch (err) {
      console.error("[OutreachJobs] add lists failed:", err);
      alert(err?.message || "Could not add that list.");
      return false;
    } finally {
      setAddingLists(false);
    }
  };

  // Save job (always saves — validation only blocks activation, not saving)
  const handleSaveJob = async () => {
    if (!jobName.trim()) {
      setShowValidation(true);
      return;
    }
    setIsSaving(true);

    try {
      const now = Date.now();
      const existingJob = editingJobId ? jobs[editingJobId] : null;
      const persistedPool = persistContactPool(contactPool);
      const apiModeActive = isApiPool(persistedPool);
      const jobData = {
        name: agentLabel(jobName.trim()),
        status: existingJob?.status || "draft",
        contactPool: persistedPool,
        deliveryMode,
        prepareDailyLimit: Math.max(1, Math.min(prepareDailyLimit, PREPARE_DAILY_LIMIT_MAX)),
        dailyLimit,
        sendingWindowStart,
        sendingWindowEnd,
        skipWeekends,
        // API-mode jobs never auto-scan — the trigger endpoint is the only
        // source of contacts. Persist the flag defensively so a stale
        // `recurring: true` from an earlier config cannot re-open the pool.
        recurring: apiModeActive ? false : recurring,
        prepOnly,
        // API-mode fields. Kept at the job level (not on `contactPool`) so
        // toggling API mode on/off doesn't rotate the audience contract.
        apiTriggerKey: apiTriggerKey || existingJob?.apiTriggerKey || null,
        apiAllowReRun: apiModeActive ? !!apiAllowReRun : (existingJob?.apiAllowReRun === true),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin",
        steps: steps.map((s, i) => {
          const { mode, names } = resolvePoolLists(contactPool);
          const persisted = persistResearchModes(
            persistStepListMaps({ ...s, order: i }, mode === "lists" ? names : [])
          );
          if (persisted.type === "send_email") {
            const tpl = topicOptions.find((t) => t.id === persisted.templateId);
            if (isFollowUpEmailType(tpl?.type) && persisted.senderFrom !== SENDER_FROM_POOL) {
              persisted.senderFrom = SENDER_FROM_PREVIOUS;
              persisted.senderAccountIds = [];
            }
            if (!isFollowUpEmailType(tpl?.type) && persisted.senderFrom === SENDER_FROM_PREVIOUS) {
              persisted.senderFrom = SENDER_FROM_POOL;
            }
          }
          return persisted;
        }),
        updatedAt: now,
      };

      if (isLiveJob(existingJob)) {
        if (!sameStepStructure(existingJob.steps, jobData.steps)) {
          alert("The sequence cannot change while this agent is running. Step settings were not saved.");
          return;
        }
        // Audience and delivery mode stay as they were at start — queued
        // contacts already sit on those rules.
        delete jobData.contactPool;
        delete jobData.deliveryMode;
      }

      let jobId = editingJobId;
      if (jobId) {
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), jobData);
      } else {
        jobData.createdAt = now;
        jobData.startedAt = null;
        jobData.completedAt = null;
        jobData.contactIds = [];
        jobData.stats = { totalContacts: 0, completed: 0, prepared: 0, sent: 0, replied: 0, skipped: 0, failed: 0, dropped: 0 };
        const newRef = push(ref(database, `World/${userId}/settings/outreachJobs`));
        jobId = newRef.key;
        jobData.id = jobId;
        await set(newRef, jobData);
      }

      // If API-trigger mode is active, ensure a stable trigger key exists.
      // The callable is idempotent: it returns the existing key if one is
      // already registered for this job, otherwise it generates + registers a
      // fresh one and stamps it on the job. Non-fatal on failure — the save
      // itself already succeeded, the sidebar can retry the ensure on
      // re-open.
      if (apiModeActive) {
        try {
          const ensureFn = httpsCallable(getFunctions(undefined, "europe-west1"), "ensureOutreachAgentKey");
          const resp = await ensureFn({ userId, jobId });
          const nextKey = resp?.data?.key;
          if (nextKey && nextKey !== apiTriggerKey) setApiTriggerKey(nextKey);
        } catch (keyErr) {
          console.warn("[OutreachJobs] ensureOutreachAgentKey failed (non-fatal):", keyErr?.message || keyErr);
        }
      }

      resetBuilder();
      setView("list");
    } catch (e) {
      console.error("Error saving job:", e);
      alert("Error saving job: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Activate job: query contacts, create queue entries
  const activateJob = async (jobId) => {
    setIsActivating(true);
    try {
      const jobData = jobs[jobId] || (await get(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`))).val();
      if (!jobData) throw new Error("Job not found");

      // Always validate, even if fetched from RTDB
      const seqErrors = validateSequence(jobData.steps || [], jobData.deliveryMode === "prepare");
      if (seqErrors.length > 0 || !jobData.steps || jobData.steps.length === 0) {
        alert("This job has errors that need to be fixed before activation:\n\n• " + (seqErrors.length > 0 ? seqErrors.join("\n• ") : "No steps in the sequence."));
        setIsActivating(false);
        return;
      }

      // Licence check. The scheduler pauses unlicensed jobs anyway, so refusing
      // here just saves the user a job that would stop on its first run.
      const jobMode = jobData.deliveryMode === "prepare" ? "prepare" : "send";
      if (jobMode === "prepare" ? !canPrepare : !canSend) {
        alert(`${MODE_LOCKED_TITLE}: "${jobMode === "prepare" ? "Prepare only" : "Send automatically"}".\n\n${MODE_LOCKED_HINT}`);
        setIsActivating(false);
        return;
      }

      // Pre-flight checks: sender accounts, cleanup guidelines
      const hasSendStep = (jobData.steps || []).some((s) => s.type === "send_email");
      const readySenders = senderEmails.filter(isReadySender);
      if (hasSendStep && readySenders.length === 0) {
        // Prepare mode needs a sender too: the draft is written from a specific
        // account's signature and brand voice, even though nothing is delivered.
        alert(`No email sender accounts configured. Go to Settings → Email → Accounts and add at least one sender before activating a job with ${stepLabel("send_email", jobMode === "prepare")} steps.`);
        setIsActivating(false);
        return;
      }
      const readyIds = new Set(readySenders.map((s) => s.id));
      const senderLockedEmpty = (jobData.steps || []).some((s) => {
        if (s.type !== "send_email") return false;
        const tpl = topicOptions.find((t) => t.id === s.templateId);
        if (usesPreviousSender(s, tpl?.type)) return false;
        const ids = normalizeSenderAccountIds(s.senderAccountIds);
        return ids && !ids.some((id) => readyIds.has(id));
      });
      if (senderLockedEmpty) {
        alert(`This ${stepLabel("send_email", jobMode === "prepare")} step has no working sender left. Pick “All that work” or at least one ready inbox.`);
        setIsActivating(false);
        return;
      }

      const hasCleanStep = (jobData.steps || []).some((s) => s.type === "clean");
      if (hasCleanStep && cleanupGuidelines.length === 0) {
        alert("No cleanup guidelines configured. Go to Settings → AI → Cleanup Guidelines and add at least one rule before activating a job with an AI Clean step.");
        setIsActivating(false);
        return;
      }

      const pool = persistContactPool(jobData.contactPool);
      if (!pool) throw new Error("Job has no contact pool configured");

      const isManualMode = isManualPool(pool);
      const isApiMode = isApiPool(pool);
      const now = Date.now();

      // API-trigger and manual pools both activate with an empty queue: the
      // contacts arrive later, either via the API endpoint or the bulk
      // action in the contact table. The scheduler treats an empty queue
      // and an API pool as valid (see shouldScanJobPool → isApiPool skip).
      if (isApiMode) {
        // Extra guard: API mode is only supported for prepare-mode agents in
        // v1. The pre-flight in the sidebar prevents saving otherwise, but
        // block here too for old jobs that predated the guard.
        if (jobData.deliveryMode !== "prepare") {
          alert("API-trigger mode currently only works with Prepare mode. Switch delivery mode before activating.");
          setIsActivating(false);
          return;
        }
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          status: "active",
          startedAt: now,
          contactIds: [],
          contactPool: pool,
          "stats/totalContacts": 0,
          "stats/completed": 0,
          "stats/sent": 0,
          "stats/replied": 0,
          "stats/skipped": 0,
          "stats/failed": 0,
          "stats/dropped": 0,
          updatedAt: now,
        });
        alert("Agent activated in API-trigger mode. It will wait for POSTs to the trigger endpoint before doing anything.");
      } else if (isManualMode) {
        // Manual mode: activate the job with an empty queue.
        // User will add contacts via the bulk action in the contact table.
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          status: "active",
          startedAt: now,
          contactIds: [],
          contactPool: pool,
          "stats/totalContacts": 0,
          "stats/completed": 0,
          "stats/sent": 0,
          "stats/replied": 0,
          "stats/skipped": 0,
          "stats/failed": 0,
          "stats/dropped": 0,
          updatedAt: now,
        });
        alert("Job activated. Add contacts via the bulk action button in your contact table.");
      } else {
        // Auto mode: query contacts from Firestore based on pool filters.
        // Multiple lists are fetched separately, then interleaved so daily
        // volume is split across them instead of draining one list first.
        const { mode, names } = resolvePoolLists(pool);
        const listNames = mode === "lists" ? names : [""];
        const requireEmail = !jobCanDiscoverEmail(jobData.steps);
        const groupSnaps = await Promise.all(
          listNames.map((name) => getDocs(buildPoolQuery(userId, pool, name, { requireEmail }))),
        );
        const contactDocs = groupSnaps.flatMap((snap) => snap.docs);

        if (contactDocs.length === 0) {
          alert("No contacts found matching your filters. Please adjust your contact pool settings.");
          setIsActivating(false);
          return;
        }

        const freshGroups = groupSnaps.map((snap) =>
          snap.docs.filter((docSnap) => {
            const history = docSnap.data().outreachHistory;
            return !history || !history[jobId];
          }),
        );
        const historySkipped = contactDocs.length - freshGroups.reduce((n, g) => n + g.length, 0);
        const freshContacts = interleaveGroups(freshGroups.map((g) => sortPoolDocs(g, pool.sortBy)));
        const importById = new Map(freshContacts.map((d) => [d.id, d.data()?.import_name || ""]));
        const statusById = new Map(freshContacts.map((d) => [d.id, d.data()?.lead_contacted || "Not Contacted"]));

        const contactIds = freshContacts.map((d) => d.id);

        if (contactIds.length === 0) {
          alert(`All ${contactDocs.length} contacts have already been through this job.`);
          setIsActivating(false);
          return;
        }

        // Check for contacts already in active (non-terminal) jobs in the queue
        const TERMINAL = new Set(["completed", "replied", "failed", "skipped"]);
        const existingQueueSnap = await get(ref(database, `outreachJobQueue/${userId}`));
        const existingQueue = existingQueueSnap.exists() ? existingQueueSnap.val() : {};
        const alreadyInActiveJob = new Set();
        for (const [, entry] of Object.entries(existingQueue)) {
          if (!TERMINAL.has(entry.status)) {
            alreadyInActiveJob.add(entry.contactId || '');
          }
        }
        const eligibleIds = contactIds.filter((id) => !alreadyInActiveJob.has(id));
        const skippedCount = contactIds.length - eligibleIds.length + historySkipped;

        if (eligibleIds.length === 0) {
          alert(`All ${contactIds.length} contacts are already in other active jobs.`);
          setIsActivating(false);
          return;
        }

        const executeAts = pacedExecuteAts(eligibleIds.length, jobPaceOptions(jobData, now));
        const queueUpdates = {};
        for (let i = 0; i < eligibleIds.length; i++) {
          const rtdbKey = encodeRtdbKey(eligibleIds[i]);

          queueUpdates[rtdbKey] = {
            contactId: eligibleIds[i],
            jobId,
            importName: importById.get(eligibleIds[i]) || "",
            statusBeforeJob: statusById.get(eligibleIds[i]) || "Not Contacted",
            currentStepIndex: 0,
            executeAt: executeAts[i],
            enteredStepAt: now,
            status: "pending",
            lastStepResult: null,
            stepHistory: {},
            retryCount: 0,
            manualSendDetected: false,
          };
        }

        await update(ref(database, `outreachJobQueue/${userId}`), queueUpdates);

        // Mark contacts as "In Outreach" so they're visible in the table and excluded
        // from manual sends. Prep-only jobs intentionally leave the status untouched.
        if (!jobData.prepOnly) {
          const BATCH_LIMIT = 499;
          for (let i = 0; i < eligibleIds.length; i += BATCH_LIMIT) {
            const chunk = eligibleIds.slice(i, i + BATCH_LIMIT);
            const batch = writeBatch(firestore);
            for (const cid of chunk) {
              batch.update(doc(firestore, `World/${userId}/db/${cid}`), { lead_contacted: 'In Outreach' });
            }
            await batch.commit();
          }
        }
        await logOutreachEnrollment(firestore, userId, eligibleIds, {
          jobId,
          jobName: jobData.name,
          detail: 'Agent started',
          at: now,
        });

        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          status: "active",
          startedAt: now,
          contactIds: eligibleIds,
          contactPool: pool,
          poolCatchupDone: false,
          "stats/totalContacts": eligibleIds.length,
          "stats/completed": 0,
          "stats/sent": 0,
          "stats/replied": 0,
          "stats/skipped": skippedCount,
          "stats/failed": 0,
          "stats/dropped": 0,
          updatedAt: now,
        });
        // So the next cron still catch-up-enqueues anyone activation missed
        // (timeout, old has_email filter). Do not wait for the hourly gate.
        await set(ref(database, `World/${userId}/settings/_allPoolCatchupsDone`), false);

        if (skippedCount > 0) {
          alert(`Job started with ${eligibleIds.length} contacts. ${skippedCount} contacts skipped (already in other jobs).`);
        }
      }
    } catch (e) {
      console.error("Error activating job:", e);
      alert("Error activating job: " + e.message);
    } finally {
      setIsActivating(false);
    }
  };

  // Pause/Resume/Stop
  const toggleJobPause = async (jobId) => {
    const job = jobs[jobId];
    if (!job) return;
    const newStatus = job.status === "paused" ? "active" : "paused";
    const updateData = { status: newStatus, updatedAt: Date.now() };
    if (newStatus === "active") { updateData.pauseReason = null; updateData.pauseMessage = null; updateData.autoResumeAt = null; }
    await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), updateData);
  };

  const openStopDialog = async (jobId) => {
    if (!jobId) return;
    setStopDialog({ jobId, loading: true, confirming: false, error: null });
    try {
      const fnInstance = getFunctions(undefined, "europe-west1");
      const stopFn = httpsCallable(fnInstance, "stopOutreachJob", { timeout: 120000 });
      const { data } = await stopFn({ jobId });
      setStopDialog({
        jobId,
        loading: false,
        confirming: false,
        error: null,
        total: data.total || 0,
        unsent: data.unsent || 0,
        sent: data.sent || 0,
        prepared: data.prepared || 0,
        pending: data.pending || 0,
      });
    } catch (e) {
      console.error("[OutreachJobs] stopOutreachJob preview failed:", e);
      setStopDialog({
        jobId,
        loading: false,
        confirming: false,
        error: e?.message || "Could not load who is still in this agent.",
      });
    }
  };

  const confirmStopJob = async () => {
    const jobId = stopDialog?.jobId;
    if (!jobId || stopDialog.confirming) return;
    setStopDialog((prev) => (prev ? { ...prev, confirming: true, error: null } : prev));
    try {
      const fnInstance = getFunctions(undefined, "europe-west1");
      const stopFn = httpsCallable(fnInstance, "stopOutreachJob", { timeout: 120000 });
      await stopFn({ jobId, confirm: true });
      setStopDialog(null);
    } catch (e) {
      console.error("[OutreachJobs] stopOutreachJob confirm failed:", e);
      setStopDialog((prev) => prev ? {
        ...prev,
        confirming: false,
        error: e?.message || "Could not stop this agent.",
      } : prev);
    }
  };

  const removeContactFromQueue = async (queueKey, contactId, jobId) => {
    try {
      const entry = queueData[queueKey] || {};
      const stepIdx = entry.currentStepIndex || 0;
      await update(ref(database, `outreachJobQueue/${userId}/${queueKey}`), {
        status: "skipped",
        failReason: "manually_removed",
        processing: null,
        completedAt: Date.now(),
      });
      // Manual skips don't go through the backend markQueueTerminal, so bump the
      // aggregate + per-step counters here. Counts must live in job.stats because
      // the entry is archived out of the hot queue afterwards.
      const jStats = jobs[jobId]?.stats || {};
      const stepStats = jStats.steps || {};
      await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
        "stats/skipped": (jStats.skipped || 0) + 1,
        [`stats/steps/${stepIdx}/skipped`]: ((stepStats[stepIdx] || {}).skipped || 0) + 1,
        updatedAt: Date.now(),
      });
    } catch (e) {
      console.error("Error removing contact:", e);
    }
  };

  const advanceContactToNextStep = async (queueKey, entry) => {
    const jobDef = jobs[entry.jobId];
    if (!jobDef?.steps) return;
    const nextIndex = (entry.currentStepIndex || 0) + 1;
    const now = Date.now();
    const si = entry.currentStepIndex || 0;

    // This is triggered on a terminal (failed/skipped) entry. Moving it forward
    // pulls it OUT of that terminal bucket, so reverse the counter that was added
    // when it became terminal. This keeps job.stats an accurate CURRENT
    // distribution (not a cumulative log), matching the pre-archive behavior
    // where reactivation simply flipped the live status back to pending.
    const jStats = jobDef.stats || {};
    const stepStats = jStats.steps || {};
    const priorStatus = entry.status; // 'failed' | 'skipped'
    const decPatch = {};
    if (priorStatus === "failed" || priorStatus === "skipped") {
      decPatch[`stats/${priorStatus}`] = Math.max(0, (jStats[priorStatus] || 0) - 1);
      decPatch[`stats/steps/${si}/${priorStatus}`] = Math.max(0, ((stepStats[si] || {})[priorStatus] || 0) - 1);
    }

    // If the entry was reactivated from the cold archive, drop the stale archived
    // terminal record so it can't shadow the fresh hot-queue entry.
    const dropStaleArchive = async () => {
      if (archiveData[queueKey]) {
        try { await remove(ref(database, `outreachJobArchive/${userId}/${entry.jobId}/${queueKey}`)); } catch (_) {}
      }
    };

    if (nextIndex >= jobDef.steps.length) {
      await update(ref(database, `outreachJobQueue/${userId}/${queueKey}`), {
        status: "completed",
        processing: null,
        completedAt: now,
        failReason: null,
        [`stepHistory/${entry.currentStepIndex}`]: { completedAt: now, result: "manually_skipped" },
      });
      await update(ref(database, `World/${userId}/settings/outreachJobs/${entry.jobId}`), {
        ...decPatch,
        "stats/completed": (jStats.completed || 0) + 1,
        [`stats/steps/${si}/completed`]: ((stepStats[si] || {}).completed || 0) + 1,
        updatedAt: now,
      });
      await dropStaleArchive();
      return;
    }

    await update(ref(database, `outreachJobQueue/${userId}/${queueKey}`), {
      status: "pending",
      currentStepIndex: nextIndex,
      executeAt: now,
      processing: null,
      failReason: null,
      retryCount: 0,
      lastStepResult: "manually_skipped",
      [`stepHistory/${entry.currentStepIndex}`]: { completedAt: now, result: "manually_skipped" },
    });
    const passPatch = {
      [`stats/steps/${si}/passed`]: ((stepStats[si] || {}).passed || 0) + 1,
    };
    await update(ref(database, `World/${userId}/settings/outreachJobs/${entry.jobId}`), {
      ...decPatch,
      ...passPatch,
      updatedAt: now,
    });
    await dropStaleArchive();
  };

  const cloneJob = async (jobId) => {
    const job = jobs[jobId];
    if (!job) return;
    try {
      const newRef = push(ref(database, `World/${userId}/settings/outreachJobs`));
      const now = Date.now();
      await set(newRef, {
        name: `${job.name} (copy)`,
        status: "draft",
        contactPool: persistContactPool(job.contactPool || EMPTY_CONTACT_POOL),
        deliveryMode: job.deliveryMode === "prepare" ? "prepare" : "send",
        prepareDailyLimit: Math.min(job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT, PREPARE_DAILY_LIMIT_MAX),
        dailyLimit: job.dailyLimit || 30,
        sendingWindowStart: job.sendingWindowStart || "09:00",
        sendingWindowEnd: job.sendingWindowEnd || "17:00",
        skipWeekends: job.skipWeekends !== false,
        recurring: isApiPool(job.contactPool) ? false : job.recurring === true,
        apiAllowReRun: job.apiAllowReRun === true,
        timezone: job.timezone || "Europe/Berlin",
        steps: (job.steps || []).map((s, i) => persistResearchModes(persistStepListMaps({ ...s, id: generateId(), order: i }))),
        createdAt: now,
        updatedAt: now,
        startedAt: null,
        completedAt: null,
        contactIds: [],
        stats: { totalContacts: 0, completed: 0, sent: 0, replied: 0, skipped: 0, failed: 0 },
      });
    } catch (e) {
      console.error("Error cloning job:", e);
      alert("Error cloning job: " + e.message);
    }
  };

  const deleteJob = async (jobId) => {
    const job = jobs[jobId];
    if (!job) return;
    if (job.status === "active") {
      alert("Please stop the job before deleting it.");
      return;
    }
    if (!window.confirm("Delete this job permanently?")) return;

    try {
      await remove(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`));
    } catch (e) {
      console.error("Error deleting job:", e);
    }
  };

  // Test run: search contacts by name/email
  const searchTestContacts = useCallback(async (searchText) => {
    if (!searchText || searchText.length < 2 || !userId) {
      setTestSearchResults([]);
      return;
    }
    setIsTestSearching(true);
    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      const lowerSearch = searchText.toLowerCase();
      const upperBound = lowerSearch.slice(0, -1) + String.fromCharCode(lowerSearch.charCodeAt(lowerSearch.length - 1) + 1);

      // Search by business_name_lower (prefix match)
      const nameQuery = query(
        dbRef,
        where("business_name_lower", ">=", lowerSearch),
        where("business_name_lower", "<", upperBound),
        firestoreLimit(8)
      );
      const nameSnap = await getDocs(nameQuery);
      const results = new Map();
      nameSnap.docs.forEach((d) => {
        const data = d.data();
        results.set(d.id, {
          id: d.id,
          name: data.business_name || d.id.slice(0, 20),
          email: data.lead_email || "",
          status: data.lead_contacted || "Not Contacted",
          hasEmail: !!data.has_email,
        });
      });

      // Also search by email prefix if it looks like an email
      if (searchText.includes("@") || searchText.includes(".")) {
        const emailQuery = query(
          dbRef,
          where("lead_email", ">=", lowerSearch),
          where("lead_email", "<", upperBound),
          firestoreLimit(8)
        );
        const emailSnap = await getDocs(emailQuery);
        emailSnap.docs.forEach((d) => {
          if (!results.has(d.id)) {
            const data = d.data();
            results.set(d.id, {
              id: d.id,
              name: data.business_name || d.id.slice(0, 20),
              email: data.lead_email || "",
              status: data.lead_contacted || "Not Contacted",
              hasEmail: !!data.has_email,
            });
          }
        });
      }

      setTestSearchResults(Array.from(results.values()));
    } catch (e) {
      console.error("Test search error:", e);
      setTestSearchResults([]);
    } finally {
      setIsTestSearching(false);
    }
  }, [userId]);

  // Debounced search
  useEffect(() => {
    if (testSearchTimer.current) clearTimeout(testSearchTimer.current);
    if (!testRunOpen) return;
    testSearchTimer.current = setTimeout(() => searchTestContacts(testSearchQuery), 350);
    return () => clearTimeout(testSearchTimer.current);
  }, [testSearchQuery, testRunOpen, searchTestContacts]);

  // Add single test contact to job queue
  const addTestContact = async (contactId, jobId) => {
    if (!contactId || !jobId) return;
    setTestRunStatus(null);
    try {
      const job = jobs[jobId] || (await get(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`))).val();
      if (!job) throw new Error("Job not found");

      // Validate sequence before allowing test run (especially for draft jobs)
      const seqErrors = validateSequence(job.steps || [], job.deliveryMode === "prepare");
      if (seqErrors.length > 0 || !job.steps || job.steps.length === 0) {
        setTestRunStatus({ type: "error", msg: "Fix job errors before testing: " + (seqErrors[0] || "No steps defined.") });
        return;
      }

      // Check if contact already in active queue
      const TERMINAL = new Set(["completed", "replied", "failed", "skipped"]);
      const existingEntry = queueData[encodeRtdbKey(contactId)];
      if (existingEntry && !TERMINAL.has(existingEntry.status)) {
        setTestRunStatus({ type: "error", msg: "This contact is already in an active job queue." });
        return;
      }

      // Check contact has email + outreachHistory
      const contactDoc = await getDocs(query(
        collection(firestore, `World/${userId}/db`),
        where("__name__", "==", contactId)
      ));
      if (contactDoc.empty) {
        setTestRunStatus({ type: "error", msg: "Contact not found." });
        return;
      }
      const contactData = contactDoc.docs[0].data();
      const jobSteps = Array.isArray(job.steps) ? job.steps : [];
      const hasEmail = !!(contactData.has_email || contactData.lead_email || computeHasEmail(contactData));
      const canDiscoverEmail = jobCanDiscoverEmail(jobSteps);
      const hasSend = jobSteps.some((s) => s.type === "send_email");
      // Research → DM finder → Prepare is a valid test on a contact that
      // starts without an address. Block only when nothing in this agent
      // can find one and Prepare/Send would immediately remove them.
      if (hasSend && !hasEmail && !canDiscoverEmail) {
        const stepName = job.deliveryMode === "prepare" ? "Prepare Email" : "Send Email";
        setTestRunStatus({
          type: "error",
          msg: `This contact has no email. ${stepName} cannot write a mail — they would leave this agent's queue immediately (${failedTagLabel(job.name)}). Pick a contact with an address, or add Web Research / DM Email Finder first.`,
        });
        return;
      }

      // Check if contact already went through this specific job
      if (contactData.outreachHistory && contactData.outreachHistory[jobId]) {
        const prev = contactData.outreachHistory[jobId];
        setTestRunStatus({ type: "error", msg: `This contact already went through this job (${prev.status}).` });
        return;
      }

      // Create queue entry with immediate execution
      const testNow = Date.now();
      const queueEntry = {
        contactId,
        jobId,
        currentStepIndex: 0,
        executeAt: testNow,
        enteredStepAt: testNow,
        status: "pending",
        lastStepResult: null,
        stepHistory: {},
        retryCount: 0,
        manualSendDetected: false,
        isTestRun: true,
        importName: contactData.import_name || "",
        statusBeforeJob: contactData.lead_contacted || "Not Contacted",
      };
      await set(ref(database, `outreachJobQueue/${userId}/${encodeRtdbKey(contactId)}`), queueEntry);

      const existingIds = Array.isArray(job.contactIds)
        ? job.contactIds
        : Object.values(job.contactIds || {}).filter(Boolean);

      if (job.status === "draft") {
        const now = Date.now();
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          status: "active",
          startedAt: now,
          updatedAt: now,
          contactIds: [...existingIds, contactId],
          "stats/totalContacts": (job.stats?.totalContacts || 0) + 1,
        });
      } else {
        await update(ref(database, `World/${userId}/settings/outreachJobs/${jobId}`), {
          contactIds: [...existingIds, contactId],
          "stats/totalContacts": (job.stats?.totalContacts || 0) + 1,
          updatedAt: Date.now(),
        });
      }

      const name = contactData.business_name || contactData.lead_email || contactId.slice(0, 12);
      setTestRunStatus({
        type: "success",
        msg: `"${name}" added to queue. Will be processed in the next cron cycle (~5 min).${job.status === "draft" ? " Job has been activated." : ""}`,
      });
      setTestSearchQuery("");
      setTestSearchResults([]);
    } catch (e) {
      console.error("Error adding test contact:", e);
      setTestRunStatus({ type: "error", msg: "Error: " + e.message });
    }
  };

  // Preview contact count for activation
  const previewContactCount = async () => {
    if (isApiPool(contactPool)) {
      setActivationPreview({ count: 0, days: 0, api: true });
      return;
    }
    if (isManualPool(contactPool)) {
      setActivationPreview({ count: 0, days: 0, manual: true });
      return;
    }
    try {
      const { mode, names } = resolvePoolLists(contactPool);
      const listNames = mode === "lists" ? names : [""];
      const perList = await Promise.all(listNames.map(async (name) => {
        const countSnap = await getCountFromServer(buildPoolQuery(userId, contactPool, name, {
          requireEmail: !jobCanDiscoverEmail(steps),
        }));
        return { name, count: countSnap.data().count };
      }));
      const count = perList.reduce((sum, row) => sum + row.count, 0);
      // In prepare mode the throughput is the prepare quota, not the send limit.
      const perDay = deliveryMode === "prepare"
        ? (prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT)
        : (dailyLimit || 30);
      const days = Math.ceil(count / perDay);
      setActivationPreview({
        count,
        days,
        perDay,
        perList: mode === "lists" && names.length > 1 ? perList : undefined,
        todaySplit: mode === "lists" && names.length > 1
          ? takeDailyShares(perList.map((row) => row.count), perDay)
          : undefined,
      });
    } catch (e) {
      console.error("Error previewing:", e);
      setActivationPreview(null);
    }
  };

  useEffect(() => {
    if (view === "builder") {
      const timer = setTimeout(() => previewContactCount(), 500);
      return () => clearTimeout(timer);
    } else {
      setActivationPreview(null);
    }
  }, [view, contactPool, dailyLimit, deliveryMode, prepareDailyLimit, steps]);

  // Research steps share this slider's budget in the backend, so the daily
  // number is the whole agent's pace and not just its mail throughput. Spelled
  // out here because the intuition is the other way round: activation queues
  // every contact at once, and it used to research all of them the same day.
  const researchPacingNote = React.useMemo(() => {
    if (!(steps || []).some((s) => s && QUALIFYING_STEPS.has(s.type))) return null;
    const perDay = deliveryMode === "prepare"
      ? Math.min(prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT, PREPARE_DAILY_LIMIT_MAX)
      : Math.min(dailyLimit || 30, Math.max(1, accountDailyLimit || 1));
    return (
      <p className="oj-sb-option-note">
        {perDay} emails a day is the yield, not the attempt count. If research or
        a gate drops contacts, replacements run the same day — up to {perDay * 4} tries.
      </p>
    );
  }, [steps, deliveryMode, prepareDailyLimit, dailyLimit, accountDailyLimit]);

  // Get topic options from userTopics (full config exposed for Live Sequence)
  const topicOptions = React.useMemo(() => {
    const options = [];
    if (!userTopics) return options;
    for (const [topicId, topicData] of Object.entries(userTopics)) {
      if (topicData && topicData.topic && typeof topicData === "object" && !Array.isArray(topicData)) {
        const subjectLines = Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [];
        const topicSpecificNotes = Array.isArray(topicData.topicSpecificNotes) ? topicData.topicSpecificNotes : [];
        options.push({
          id: topicId,
          name: topicData.topic,
          type: topicData.type,
          language: topicData.language || null,
          model: topicData.model || null,
          writingLevel: topicData.writingLevel || null,
          aiAdherence: topicData.aiAdherence || null,
          formattingStyle: topicData.formattingStyle || null,
          hyperIntelligence: !!topicData.hyperIntelligence,
          ultraMode: !!topicData.ultraMode,
          hasReferenceFile: !!(topicData.referenceFile && topicData.referenceFile.claudeFileId),
          referenceFileName: topicData.referenceFile?.fileName || null,
          smartSubjectLine: !!topicData.smartSubjectLine,
          smartSubjectStyle: topicData.smartSubjectStyle || null,
          subjectMode: topicData.subjectMode || null,
          replyPrefix: topicData.replyPrefix || null,
          smartGreeting: !!topicData.smartGreeting,
          emailPreference: topicData.emailPreference || null,
          dmFallback: topicData.dmFallback === "strict" ? "strict" : "info",
          subjectLinesCount: subjectLines.length,
          topicSpecificNotesCount: topicSpecificNotes.length,
          hasAiTag: !!topicData.aiTagId,
          maxTokens: topicData.maxTokens || null,
          aiTemperature: topicData.aiTemperature,
        });
      }
    }
    return options;
  }, [userTopics]);

  // Merge predefined tags with user's custom aiTags (same tag set as warmup/retargeting flows)
  const allTags = React.useMemo(() => {
    const customIds = new Set(aiTags.map((t) => t.id));
    const merged = [...PREDEFINED_TAGS.filter((t) => !customIds.has(t.id)), ...aiTags];
    return merged;
  }, [aiTags]);

  // Compute live stats per job.
  //
  // Active counts (pending/processing) come from the live hot queue. Terminal
  // counts (completed/replied/failed/skipped) come from the persistent
  // job.stats counters — finished entries are archived out of the hot queue
  // (Woodpecker-style) so they're no longer present in queueData. This keeps
  // the numbers correct while the scheduler no longer re-reads dead records.
  const getJobQueueStats = useCallback(
    (jobId) => {
      const entries = Object.values(queueData).filter((e) => e.jobId === jobId);
      const s = jobs[jobId]?.stats || {};
      const pending = entries.filter((e) => e.status === "pending").length;
      const processing = entries.filter((e) => e.status === "processing").length;
      // Prepare mode: email written, parked until the user sends it.
      const awaitingSend = entries.filter((e) => e.status === "awaiting_send").length;
      const completed = s.completed || 0;
      const replied = s.replied || 0;
      const failed = (s.failed || 0) + (s.failed_research || 0);
      const skipped = s.skipped || 0;
      return {
        pending,
        processing,
        awaiting_send: awaitingSend,
        completed,
        replied,
        failed,
        skipped,
        total: pending + processing + awaitingSend + completed + replied + failed + skipped,
      };
    },
    [queueData, jobs]
  );

  // ===================== RENDER =====================

  // Template topic → expected contact status mapping
  const TEMPLATE_STATUS_MAP = {
    'Introduce Yourself': 'Not Contacted',
    'Follow Up': 'Introduced',
    'Second Follow Up': 'First Follow-up',
    'Third Follow Up': 'Second Follow-up',
  };

  // ── Builder / Detail: full-screen master editor
  const renderMasterEditor = () => {
    const isBuilder = view === "builder";
    const isDetail = view === "detail" && detailJobId && jobs[detailJobId];
    if (!isBuilder && !isDetail) return null;

    // Builder validation (computed always, shown only after save attempt or on detail activate)
    const allErrors = isBuilder ? [...(steps.length === 0 ? ["Add at least one step to the sequence."] : []), ...validateSequence(steps, deliveryMode === "prepare")] : [];
    const validationErrors = showValidation ? allErrors : [];
    const validationWarnings = [];

    if (isBuilder && steps.length > 0) {
      const sendStepsArr = steps.filter((s) => s.type === "send_email");

      const isPrepareBuild = deliveryMode === "prepare";
      const emailLabel = stepLabel("send_email", isPrepareBuild);

      if (sendStepsArr.length === 0) {
        validationWarnings.push(isPrepareBuild
          ? `This sequence has no ${emailLabel} step — no emails will be prepared.`
          : `This sequence has no ${emailLabel} step — no emails will be sent.`);
      }

      if (steps[steps.length - 1].type === "wait") {
        validationWarnings.push("Sequence ends with a Wait step — that pause leads nowhere.");
      }

      if (steps[0].type === "check_reply") {
        validationWarnings.push("Check Reply is first — it scans the inbox for a reply since the last email to this contact (including another agent). Replied contacts leave; the rest continue.");
      }

      const templatesByStep = sendStepsArr.map((s) => new Set(collectStepTemplateIds(s)));
      const seenTemplates = new Map();
      let sharedAcrossSteps = false;
      templatesByStep.forEach((ids) => {
        ids.forEach((id) => {
          const prev = seenTemplates.get(id) || 0;
          seenTemplates.set(id, prev + 1);
          if (prev > 0) sharedAcrossSteps = true;
        });
      });
      if (sharedAcrossSteps) {
        validationWarnings.push(`Same template used in multiple ${emailLabel} steps — contacts will receive duplicate content.`);
      }

      if (!isManualPool(contactPool) && !isApiPool(contactPool) && isAnyListPool(contactPool) && !contactPool.statusFilter && !contactPool.tagFilter) {
        validationWarnings.push(jobCanDiscoverEmail(steps)
          ? "No contact filters set — every contact on the selected lists will enter this job."
          : "No contact filters set — all contacts with email will enter this job.");
      }

      const firstSendStep = sendStepsArr[0];
      const firstTemplateName = firstSendStep?.templateId
        ? topicOptions.find((t) => t.id === firstSendStep.templateId)?.name || ""
        : "";
      const suggestedStatus = TEMPLATE_STATUS_MAP[firstTemplateName] || null;
      if (suggestedStatus && contactPool.statusFilter && contactPool.statusFilter !== suggestedStatus) {
        validationWarnings.push(`Status "${contactPool.statusFilter}" might not match template "${firstTemplateName}".`);
      }
      if (suggestedStatus && !contactPool.statusFilter) {
        validationWarnings.push(`Your first email expects "${suggestedStatus}" contacts — consider setting a status filter.`);
      }

      // Clean step exists but no cleanup guidelines configured
      const hasCleanStep = steps.some((s) => s.type === "clean");
      if (hasCleanStep && cleanupGuidelines.length === 0) {
        validationWarnings.push("Your sequence includes an AI Clean step, but no cleanup guidelines are configured. The job will pause immediately.");
      }

      // Clean step has a guideline (default or per-list) that no longer exists
      for (const s of steps.filter((s) => s.type === "clean")) {
        const missingId = collectOverrideIds(s, LIST_MAP_GUIDELINE).find((id) => !cleanupGuidelines.some((g) => g.id === id));
        if (missingId) {
          validationWarnings.push(`Clean step references a deleted guideline "${s.guidelineName || missingId}" — it will fall back to the default.`);
          break;
        }
      }

      const hasPotentialStep = steps.some((s) => s.type === "potential_analyzer");
      if (hasPotentialStep && potentialProfiles.length === 0) {
        validationWarnings.push("Your sequence includes a Potential Analyzer step, but no ICP profiles are configured. Add one in Research Settings so scoring has something to match against.");
      }

      // Potential Analyzer step points at an ICP profile (default or per-list) that no longer exists
      for (const s of steps.filter((s) => s.type === "potential_analyzer")) {
        const missingId = collectOverrideIds(s, LIST_MAP_ICP).find((id) => !potentialProfiles.some((p) => p.id === id));
        if (missingId) {
          validationWarnings.push(`Potential Analyzer step references a deleted ICP profile "${s.icpProfileName || missingId}" — it will fall back to the default.`);
          break;
        }
      }

      // Send email before any research steps (not blocking, but suboptimal)
      const firstResearchIdx = steps.findIndex((s) => QUALIFYING_STEPS.has(s.type));
      if (sendStepsArr.length > 0 && firstResearchIdx >= 0) {
        const firstSendIndex = steps.findIndex((s) => s.type === "send_email");
        if (firstSendIndex < firstResearchIdx) {
          validationWarnings.push(`${emailLabel} comes before Research — emails will have less context about the contact.`);
        }
      }

      // Send email steps without wait between them (non-consecutive but still no wait)
      for (let i = 0; i < steps.length; i++) {
        if (steps[i].type === "send_email") {
          const nextSendIdx = steps.slice(i + 1).findIndex((s) => s.type === "send_email");
          if (nextSendIdx >= 0) {
            const between = steps.slice(i + 1, i + 1 + nextSendIdx);
            if (!between.some((s) => s.type === "wait")) {
              validationWarnings.push(isPrepareBuild
                ? `Multiple ${emailLabel} steps without a Wait between them — the follow-up is prepared before the first email can go out.`
                : `Multiple ${emailLabel} steps without a Wait between them — emails will be sent back-to-back.`);
              break;
            }
          }
        }
      }
    }

    const hasBlockingErrors = allErrors.length > 0;
    const sendStepCount = steps.filter((s) => s.type === "send_email").length;

    // Detail-specific
    const job = isDetail ? jobs[detailJobId] : null;
    const stats = job?.stats || {};
    const totalContacts = stats.totalContacts || job?.contactIds?.length || 0;
    const qStats = isDetail ? getJobQueueStats(detailJobId) : {};
    const detailIsPrepare = job?.deliveryMode === "prepare";
    // A prepare job never increments `sent` — it hands its work to the user
    // instead. Counting the mails waiting for them (they're parked, so they
    // can't also be counted as completed) is what makes the bar move while the
    // job is actually working.
    const completedTotal = detailIsPrepare
      ? (stats.completed || 0) + (stats.replied || 0) + (qStats.awaiting_send || 0)
      : (stats.sent || 0) + (stats.replied || 0) + (stats.completed || 0);
    const progress = totalContacts > 0 ? Math.round((completedTotal / totalContacts) * 100) : 0;

    // Hot queue + the current archive page (newest 5, then +5 on load more).
    // Live rows overwrite a stale archive copy of the same key.
    const queueEntries = isDetail ? detailQueueEntries : [];

    const TERMINAL_SET = new Set(["completed", "replied", "failed", "skipped"]);
    // Replies are attributed to the step index the queue entry was ON when
    // the reply-check fired. That's often an earlier step (wait / send_email)
    // rather than the explicit check_reply step — so a `check_reply` step
    // naturally shows "0 replied" in its own bucket even if many contacts
    // have replied job-wide. For `check_reply` steps we therefore surface the
    // TOTAL job-wide replied count as the primary signal (since that IS the
    // step's semantic: "contacts who replied exit here"). Other steps keep
    // their own attributed counts so you can see where replies were detected.
    const repliedEntriesAll = queueEntries.filter(([, e]) => e.status === "replied");

    // ── Effective replies (including "pending auto-stop") ─────────────────
    // The outreach scheduler only touches entries whose `executeAt <= now`.
    // During long Wait steps, an entry can sit idle for days even after the
    // inbox fetcher has already written `lead_reply: true` on the contact.
    // We surface these "pending but effectively replied" entries in the UI
    // so the user sees the real state instead of waiting for the scheduler
    // to catch up on the next run.
    const clientFindLastSendTime = (entry) => {
      if (!entry || !entry.stepHistory) return entry?.executeAt || 0;
      const sends = Object.values(entry.stepHistory)
        .filter((s) => s && s.result === "sent" && typeof s.completedAt === "number")
        .sort((a, b) => b.completedAt - a.completedAt);
      return sends.length > 0 ? sends[0].completedAt : (entry.executeAt || 0);
    };
    const isPendingButReplied = (entry) => {
      if (!entry || TERMINAL_SET.has(entry.status)) return false;
      const cid = entry.contactId;
      if (!cid) return false;
      const flag = contactReplyFlags[cid];
      if (!flag || !flag.leadReply) return false;
      if (typeof flag.lastReplyTs !== "number") return false;
      const lastSend = clientFindLastSendTime(entry);
      // Reply timestamp must be at-or-after the last send (60s skew leeway).
      // Imported leads with pre-existing replies but no outreach send are
      // naturally excluded because their lastSend >= lastReplyTs.
      return flag.lastReplyTs >= (lastSend - 60000);
    };
    const pendingRepliedEntries = queueEntries.filter(([, e]) => isPendingButReplied(e));
    // Effective = backend-confirmed replies + pending-but-reply-detected
    const effectiveRepliedEntries = [...repliedEntriesAll, ...pendingRepliedEntries];
    // For each replied contact, count how many `send_email` steps in the job
    // were NOT executed (i.e. emails that would have been sent but got skipped
    // because the reply short-circuited the sequence). This is the "saved
    // sends" metric — a direct measure of the value of auto-stop on reply.
    const sendStepIndexes = isDetail && Array.isArray(job?.steps)
      ? job.steps.reduce((acc, s, i) => (s?.type === "send_email" ? [...acc, i] : acc), [])
      : [];
    const savedSendsForEntry = (entry) => {
      if (!entry || sendStepIndexes.length === 0) return 0;
      const hist = entry.stepHistory || {};
      return sendStepIndexes.reduce((n, i) => {
        const done = hist[String(i)] || hist[i];
        return done ? n : n + 1;
      }, 0);
    };
    // "Saved sends" uses effective replies (includes pending auto-stops) so
    // the counter reflects reality even while the scheduler catches up.
    const savedSendsCount = effectiveRepliedEntries.reduce((n, [, e]) => n + savedSendsForEntry(e), 0);
    const stepFunnel = isDetail ? (job.steps || []).map((step, idx) => {
      const atStep = queueEntries.filter(([, e]) => (e.currentStepIndex || 0) === idx && !TERMINAL_SET.has(e.status));
      const waiting = atStep.filter(([, e]) => e.status === "pending" && (e.executeAt || 0) > Date.now());
      const ready = atStep.filter(([, e]) => e.status === "pending" && (e.executeAt || 0) <= Date.now());
      const processing = atStep.filter(([, e]) => e.status === "processing");
      // Prepare mode: the email exists and sits in the Prepared Mails strip. It
      // gets its own badge because it is the only state at a send step where the
      // work is actually done — lumping it in with the pending ones was how a
      // job with nothing written yet still looked like it had mails ready.
      const awaitingSend = atStep.filter(([, e]) => e.status === "awaiting_send");
      // Break `dropped` apart by terminal reason so the user sees at a glance
      // how many replied vs how many were skipped/failed at this step.
      const terminalHere = queueEntries.filter(([, e]) => (e.currentStepIndex || 0) === idx && TERMINAL_SET.has(e.status) && e.status !== "completed");
      const repliedAtStep = terminalHere.filter(([, e]) => e.status === "replied");
      const failedAtStep = terminalHere.filter(([, e]) => e.status === "failed");
      const skippedAtStep = terminalHere.filter(([, e]) => e.status === "skipped");
      // Pending-but-reply-detected entries: contact has `lead_reply: true`
      // fresher than their last send, but the scheduler hasn't processed the
      // queue entry yet (common during long Wait steps).
      const pendingRepliedHere = atStep.filter(([, e]) => isPendingButReplied(e));
      // Prefer the permanent per-step counters (job.stats.steps[idx]) which stay
      // accurate even after finished entries are archived out of the hot queue.
      // Fall back to the live merged-queue count for jobs created before the
      // counters existed / entries not yet counted.
      const stepStat = (stats.steps && stats.steps[idx]) || {};
      const skippedCount = stepStat.skipped != null ? stepStat.skipped : skippedAtStep.length;
      const failedCount = stepStat.failed != null ? stepStat.failed : failedAtStep.length;
      const repliedStepCount = stepStat.replied != null ? stepStat.replied : repliedAtStep.length;
      const passedHere = passedThroughCount(queueEntries, idx);
      const passedCount = stepStat.passed != null ? Math.max(stepStat.passed, passedHere) : passedHere;
      return {
        step, idx,
        total: atStep.length,
        waiting: waiting.length,
        ready: ready.length,
        processing: processing.length,
        awaitingSend: awaitingSend.length,
        dropped: (repliedStepCount + failedCount + skippedCount) || terminalHere.length,
        replied: repliedStepCount,
        pendingReplied: pendingRepliedHere.length,
        failed: failedCount,
        skipped: skippedCount,
        passed: passedCount,
        // For check_reply steps we expose ALL effective replied entries
        // (confirmed + pending auto-stop) so the expand panel lists everyone
        // who has actually replied. The per-step `replied` stays local.
        repliedGlobal: step.type === "check_reply" ? effectiveRepliedEntries : null,
      };
    }) : [];
    // Terminal totals come from the persistent job.stats counters (finished
    // entries are archived out of the hot queue Woodpecker-style, so they're no
    // longer in queueEntries). Fall back to the live queue count for the rare
    // transitional case where an entry hasn't been archived yet.
    const doneCount = (stats.completed || 0) || queueEntries.filter(([, e]) => e.status === "completed").length;
    const repliedCount = (stats.replied || 0) || repliedEntriesAll.length;
    // Global "auto-stop pending" tally for the pipeline terminal.
    const pendingAutoStopTotal = pendingRepliedEntries.length;

    const handleClose = () => {
      if (isBuilder) { resetBuilder(); setView("list"); }
      else { setDetailJobId(null); setTestRunOpen(false); setView("list"); }
    };

    // Portalled onto <body>. The settings pane it is rendered from sits inside a
    // transformed ancestor, which makes `position: fixed` resolve against that
    // ancestor instead of the viewport: the overlay started below the app header
    // and its top bar — the only way back to the list — was pushed off-screen.
    return createPortal(
      <div className="oj-master-overlay">
        <div className="oj-master-container">
          {/* ═══ TOP BAR ═══ */}
          <div className="oj-topbar">
            <div className="oj-topbar-left">
              <button className="oj-back-btn" onClick={handleClose}>
                <i className="fa fa-arrow-left"></i>
              </button>
              <div className="oj-topbar-divider"></div>
              {isBuilder ? (
                <input
                  className="oj-topbar-title-input"
                  type="text"
                  value={agentLabel(jobName)}
                  onChange={(e) => setJobName(e.target.value)}
                  placeholder="Name your outreach agent..."
                />
              ) : (
                <span className="oj-topbar-title">{agentLabel(job.name)}</span>
              )}
              {isDetail && (
                <span className={`oj-status-pill ${job.status}`}>
                  {job.status === "active" && <span className="oj-status-dot active"></span>}
                  {JOB_STATUS_LABELS[job.status] || job.status}
                </span>
              )}
            </div>
            <div className="oj-topbar-right">
              {isBuilder && (
                <button className="oj-topbar-btn primary" onClick={() => handleSaveJob()} disabled={isSaving}>
                  <i className="fa fa-check"></i> {isSaving ? "Saving..." : structureLocked ? "Save settings" : "Save"}
                </button>
              )}
              {isDetail && (
                <>
                  {/* Activate / Pause toggle */}
                  {(job.status === "draft" || job.status === "paused") && (
                    <button
                      className="oj-topbar-btn primary"
                      onClick={() => job.status === "draft" ? activateJob(detailJobId) : toggleJobPause(detailJobId)}
                      disabled={isActivating}
                    >
                      <i className="fa fa-play"></i> {isActivating ? "Starting..." : job.status === "draft" ? "Activate" : "Resume"}
                    </button>
                  )}
                  {job.status === "active" && (
                    <button className="oj-topbar-btn warn" onClick={() => toggleJobPause(detailJobId)}>
                      <i className="fa fa-pause"></i> Pause
                    </button>
                  )}
                  {(job.status === "active" || job.status === "paused") && (
                    <button className="oj-topbar-btn danger" onClick={() => openStopDialog(detailJobId)}>
                      <i className="fa fa-stop"></i> Stop
                    </button>
                  )}
                  {(job.status === "draft" || isLiveJob(job)) && (
                    <button className="oj-topbar-btn secondary" onClick={() => openBuilder(detailJobId)}>
                      <i className="fa fa-pen"></i> {isLiveJob(job) ? "Settings" : "Edit"}
                    </button>
                  )}
                  <button className="oj-topbar-btn secondary" onClick={() => cloneJob(detailJobId)}>
                    <i className="fa fa-copy"></i> Clone
                  </button>
                  <button
                    className={`oj-topbar-btn secondary${testRunOpen ? " active" : ""}`}
                    onClick={() => { setTestRunOpen((o) => !o); setTestRunStatus(null); setTestSearchQuery(""); setTestSearchResults([]); }}
                  >
                    <i className="fa fa-flask"></i> Test
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ═══ MAIN AREA ═══ */}
          <div className="oj-main">
            {/* ─── SIDEBAR ─── */}
            <div className="oj-sidebar">
              {isBuilder && (
                <>
                  <div className="oj-sb-section">
                    <div className="oj-sb-head">
                      <span className="oj-sb-kicker">Who</span>
                      <h5>Target contacts</h5>
                      {/*
                        3-dot menu — advanced audience sources (currently just
                        API-trigger mode). Kept outside the `structureLocked`
                        fieldset so a running agent still exposes the endpoint
                        for read-only copy. Enable/disable is guarded below.
                      */}
                      <div
                        className={`oj-sb-menu${whoMenuOpen ? " open" : ""}`}
                        ref={whoMenuRef}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="oj-sb-menu-btn"
                          aria-label="More audience options"
                          onClick={() => setWhoMenuOpen((v) => !v)}
                        >
                          <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                        </button>
                        {whoMenuOpen && (
                          <div
                            className="oj-sb-menu-pop"
                            role="menu"
                          >
                            <button
                              type="button"
                              className={`oj-sb-menu-item${isApiPool(contactPool) ? " active" : ""}`}
                              disabled={structureLocked}
                              onClick={() => {
                                setWhoMenuOpen(false);
                                if (structureLocked) return;
                                const turningOn = !isApiPool(contactPool);
                                if (turningOn && deliveryMode !== "prepare") {
                                  const ok = window.confirm(
                                    "API-trigger mode only works with Prepare delivery. Switch this agent to Prepare mode?"
                                  );
                                  if (!ok) return;
                                  if (!canPrepare) {
                                    alert(`${MODE_LOCKED_TITLE}: "Prepare only". ${MODE_LOCKED_HINT}`);
                                    return;
                                  }
                                  setDeliveryMode("prepare");
                                }
                                setContactPool((p) => applyPoolLists(p, { mode: turningOn ? "api" : "any" }));
                                if (turningOn) setRecurring(false);
                              }}
                            >
                              <i className={`fa ${isApiPool(contactPool) ? "fa-check-circle" : "fa-plug"}`}></i>
                              <div className="oj-sb-menu-item-body">
                                <strong>API trigger mode</strong>
                                <span>Push contacts one-by-one from external systems.</span>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="oj-sb-body">
                      {structureLocked && (
                        <div className="oj-sb-hint">
                          <i className="fa fa-lock"></i>
                          {isApiPool(contactPool)
                            ? "API-trigger mode is locked while this agent is running. The endpoint keeps accepting POSTs."
                            : isManualPool(contactPool)
                              ? "Filters stay as they were when this agent started. Add people from the contact table."
                              : isAnyListPool(contactPool)
                                ? "Status and tag stay as they were when this agent started. Queue a list that arrived later below."
                                : "Status and tag stay as they were when this agent started. Add another list below. Contacts already in the queue keep their place."}
                        </div>
                      )}
                      {isApiPool(contactPool) ? (
                        <ApiModePanel
                          jobId={editingJobId}
                          userId={userId}
                          apiTriggerKey={apiTriggerKey}
                          setApiTriggerKey={setApiTriggerKey}
                          apiAllowReRun={apiAllowReRun}
                          setApiAllowReRun={setApiAllowReRun}
                          apiKeyLoading={apiKeyLoading}
                          setApiKeyLoading={setApiKeyLoading}
                          apiKeyCopied={apiKeyCopied}
                          setApiKeyCopied={setApiKeyCopied}
                          deliveryMode={deliveryMode}
                          structureLocked={structureLocked}
                          onDisable={() => setContactPool((p) => applyPoolLists(p, { mode: "any" }))}
                        />
                      ) : (
                      <fieldset className="oj-sb-live" disabled={structureLocked}>
                      <div className="oj-sb-field">
                        <label>
                          Status
                          <InfoTip>
                            Combined with List and AI tag (AND). “Any status” includes everyone matching the lists — including people already in outreach. Jobs without Web Research or DM Email Finder still require an email.
                          </InfoTip>
                        </label>
                        <select value={typeof contactPool.statusFilter === "string" ? contactPool.statusFilter : ""} onChange={(e) => setContactPool((p) => ({ ...p, statusFilter: e.target.value }))}>
                          <option value="">Any status (risky)</option>
                          {STATUS_OPTIONS.map((s) => (
                            <option key={String(s.value || s)} value={String(s.value || s)}>{String(s.label || s)}</option>
                          ))}
                          {failedAgentTags(jobs, editingJobId).length > 0 && (
                            <optgroup label="Left an agent">
                              {failedAgentTags(jobs, editingJobId).map((tag) => (
                                <option key={tag.id} value={tag.tag}>{tag.tag}</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      <div className="oj-sb-field">
                        <label>
                          Import lists
                          <InfoTip>
                            The uploads this contact came from. Combined with Status and AI tag (AND). Tick several lists and the daily volume is split evenly across them. “Any list” = every import. People who already finished this job, or sit in another active job, are skipped.
                          </InfoTip>
                        </label>
                        <OutreachImportListPicker
                          pool={contactPool}
                          onChange={setContactPool}
                          importOptions={importOptions}
                          layout={listLayout}
                        />
                      </div>
                      <div className="oj-sb-field">
                        <label>
                          Required tag
                          <InfoTip>
                            Must have this tag as well as match List and Status. Pick a “Completed” tag to start this agent from people who finished another one. If you turn on “Add new matches every day”, someone who gets the tag later is picked up on the next daily scan — not live. Off = snapshot at start; later tags are ignored. The tag is also re-checked before each step; if it is removed, the contact is skipped.
                          </InfoTip>
                        </label>
                        {(() => {
                          const agentTags = completedAgentTags(jobs, editingJobId);
                          const failTags = failedAgentTags(jobs, editingJobId);
                          const selectedOrphan = contactPool.tagFilter
                            && (isCompletedTagId(contactPool.tagFilter) || isFailedTagId(contactPool.tagFilter))
                            && !agentTags.some((t) => t.id === contactPool.tagFilter)
                            && !failTags.some((t) => t.id === contactPool.tagFilter)
                            ? { id: contactPool.tagFilter, tag: labelForPoolTag(contactPool.tagFilter, { jobs, tags: allTags }) }
                            : null;
                          const starterTags = selectedOrphan && isCompletedTagId(selectedOrphan.id)
                            ? [selectedOrphan, ...agentTags]
                            : agentTags;
                          const dropoutTags = selectedOrphan && isFailedTagId(selectedOrphan.id)
                            ? [selectedOrphan, ...failTags]
                            : failTags;
                          return (
                            <>
                              <select value={contactPool.tagFilter} onChange={(e) => setContactPool((p) => ({ ...p, tagFilter: e.target.value }))}>
                                <option value="">Any tag</option>
                                {starterTags.length > 0 && (
                                  <optgroup label="Finished an agent">
                                    {starterTags.map((tag) => (
                                      <option key={tag.id} value={tag.id}>{tag.tag}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {dropoutTags.length > 0 && (
                                  <optgroup label="Left an agent">
                                    {dropoutTags.map((tag) => (
                                      <option key={tag.id} value={tag.id}>{tag.tag}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {allTags.length > 0 && (
                                  <optgroup label="Tags">
                                    {allTags.map((tag) => (
                                      <option key={tag.id} value={tag.id}>{tag.tag || tag.name || tag.id}</option>
                                    ))}
                                  </optgroup>
                                )}
                              </select>
                              {contactPool.tagFilter ? (
                                <span className="oj-sb-field-hint">
                                  {isCompletedTagId(contactPool.tagFilter)
                                    ? "People who finished that agent."
                                    : isFailedTagId(contactPool.tagFilter)
                                      ? "People who dropped out of that agent."
                                      : "Only contacts with this tag."}
                                </span>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>

                      {!isManualPool(contactPool) && (
                        <div className="oj-sb-option">
                          <div className="oj-sb-toggle-row" onClick={() => { if (!structureLocked) setRecurring((v) => !v); }}>
                            <div className={`oj-sb-toggle${recurring ? " on" : ""}`}><div className="oj-sb-toggle-knob"></div></div>
                            <span>Add new matches every day</span>
                            <InfoTip>
                              Off: everyone who matches now is queued at activation and paced at your daily limit. You do not need this for 30 drafts a day. On: about once a day we run the same List, Status and Tag filters again for people who joined later. An email is only required at Prepare / Send — Web Research or DM Email Finder can find it first.
                            </InfoTip>
                          </div>
                        </div>
                      )}

                      {activationPreview && (
                        <div className="oj-sb-preview">
                          {activationPreview.api ? (
                            <>
                              <i className="fa fa-plug"></i>
                              <span>Contacts arrive via API after activation</span>
                            </>
                          ) : activationPreview.manual ? (
                            <>
                              <i className="fa fa-hand-pointer-o"></i>
                              <span>Add contacts from the table after activation</span>
                            </>
                          ) : (
                            <>
                              <i className="fa fa-bullseye"></i>
                              <strong>{activationPreview.count}</strong>
                              <span>
                                contacts
                                {activationPreview.days > 0 ? ` · ~${activationPreview.days} day${activationPreview.days !== 1 ? "s" : ""}` : ""}
                              </span>
                            </>
                          )}
                        </div>
                      )}

                      {validationWarnings.map((w, i) => (
                        <div key={i} className="oj-sb-hint warning">
                          <i className="fa fa-exclamation-triangle"></i>
                          {w}
                        </div>
                      ))}
                      </fieldset>
                      )}
                      {structureLocked && !isApiPool(contactPool) && !isManualPool(contactPool) && (
                        <AddAgentLists
                          pool={contactPool}
                          importOptions={importOptions}
                          layout={listLayout}
                          busy={addingLists}
                          onAdd={handleAddLists}
                        />
                      )}
                    </div>
                  </div>

                  <div className="oj-sb-section">
                    <div className="oj-sb-head">
                      <span className="oj-sb-kicker">How</span>
                      <h5>Delivery</h5>
                    </div>
                    <div className="oj-sb-body">
                      {structureLocked && (
                        <div className="oj-sb-hint">
                          <i className="fa fa-lock"></i>
                          Delivery mode is locked while this agent is running.
                        </div>
                      )}
                      <div className="oj-mode-choice">
                        <button
                          type="button"
                          className={`oj-mode-card${deliveryMode === "send" ? " active" : ""}${!canSend || structureLocked ? " locked" : ""}`}
                          onClick={() => !structureLocked && canSend && setDeliveryMode("send")}
                          disabled={structureLocked || !canSend}
                          title={structureLocked ? "Locked while the agent is running" : (canSend ? undefined : MODE_LOCKED_TITLE)}
                        >
                          <i className={`fa ${canSend ? "fa-paper-plane" : "fa-lock"}`}></i>
                          <strong>Automatic Outreach</strong>
                          <span>{canSend ? "Writes and sends in the background" : MODE_LOCKED_HINT}</span>
                        </button>
                        <button
                          type="button"
                          className={`oj-mode-card${deliveryMode === "prepare" ? " active" : ""}${!canPrepare || structureLocked ? " locked" : ""}`}
                          onClick={() => !structureLocked && canPrepare && setDeliveryMode("prepare")}
                          disabled={structureLocked || !canPrepare}
                          title={structureLocked ? "Locked while the agent is running" : (canPrepare ? undefined : MODE_LOCKED_TITLE)}
                        >
                          <i className={`fa ${canPrepare ? "fa-pen-to-square" : "fa-lock"}`}></i>
                          <strong>Outreach Preparation</strong>
                          <span>{canPrepare ? "Writes the emails and waits for you" : MODE_LOCKED_HINT}</span>
                        </button>
                      </div>
                      {!canSend && !canPrepare && !outreachLoading && (
                        <div className="oj-sb-hint warning">
                          <i className="fa fa-lock"></i>
                          No delivery mode is unlocked. {MODE_LOCKED_HINT}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="oj-sb-section">
                    <div className="oj-sb-head">
                      <span className="oj-sb-kicker">Pace</span>
                      <h5>{deliveryMode === "prepare" ? "How many to prepare" : "How many to send"}</h5>
                    </div>
                    <div className="oj-sb-body">
                      {deliveryMode === "prepare" ? (
                        <>
                          {(() => {
                            const value = Math.min(prepareDailyLimit, PREPARE_DAILY_LIMIT_MAX);
                            const pct = ((value - 1) / (PREPARE_DAILY_LIMIT_MAX - 1)) * 100;
                            return (
                              <div className="oj-vol">
                                <div className="oj-vol-top">
                                  <span className="oj-vol-label">Prepare per day</span>
                                  <span className="oj-vol-val">{value}<span className="oj-vol-unit">/day</span></span>
                                </div>
                                <input
                                  type="range"
                                  className="oj-slider"
                                  min={1}
                                  max={PREPARE_DAILY_LIMIT_MAX}
                                  value={value}
                                  onChange={(e) => setPrepareDailyLimit(parseInt(e.target.value, 10))}
                                  style={{ "--oj-fill": `${pct}%` }}
                                />
                                <div className="oj-vol-scale"><span>1</span><span>{PREPARE_DAILY_LIMIT_MAX}/day</span></div>
                                <span className="oj-sb-field-hint">
                                  How many new mails to write each day. Unsent mails can pile up to {preparedQueueCap} — that waiting cap comes from your plan, not this slider.
                                </span>
                              </div>
                            );
                          })()}
                          <p className="oj-sb-option-note">No sending window — emails are ready above Targets.</p>
                          {researchPacingNote}
                        </>
                      ) : (
                        <>
                          {(() => {
                            const value = Math.min(dailyLimit, accountDailyLimit);
                            const pct = accountDailyLimit > 1 ? ((value - 1) / (accountDailyLimit - 1)) * 100 : 100;
                            return (
                              <div className="oj-vol">
                                <div className="oj-vol-top">
                                  <span className="oj-vol-label">Emails per day</span>
                                  <span className="oj-vol-val">{value}<span className="oj-vol-unit">/day</span></span>
                                </div>
                                <input
                                  type="range"
                                  className="oj-slider"
                                  min={1}
                                  max={accountDailyLimit}
                                  value={value}
                                  onChange={(e) => setDailyLimit(parseInt(e.target.value, 10))}
                                  style={{ "--oj-fill": `${pct}%` }}
                                />
                                <div className="oj-vol-scale"><span>1</span><span>{accountDailyLimit}/day</span></div>
                              </div>
                            );
                          })()}
                          <div className="oj-sb-field">
                            <label>Sending window</label>
                            <div className="oj-sb-time-row">
                              <TimeField value={sendingWindowStart} onChange={setSendingWindowStart} />
                              <span className="oj-sb-time-dash">–</span>
                              <TimeField value={sendingWindowEnd} onChange={setSendingWindowEnd} />
                            </div>
                          </div>
                          <div className="oj-sb-option">
                            <div className="oj-sb-toggle-row" onClick={() => setSkipWeekends((v) => !v)}>
                              <div className={`oj-sb-toggle${skipWeekends ? " on" : ""}`}><div className="oj-sb-toggle-knob"></div></div>
                              <span>Skip weekends</span>
                            </div>
                          </div>
                          {researchPacingNote}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="oj-sb-section oj-sb-section-quiet">
                    <div className="oj-sb-head">
                      <span className="oj-sb-kicker">More</span>
                      <h5>Options</h5>
                    </div>
                    <div className="oj-sb-body">
                      <div className="oj-sb-option">
                        <div className="oj-sb-toggle-row" onClick={() => setPrepOnly((v) => !v)}>
                          <div className={`oj-sb-toggle${prepOnly ? "" : " on"}`}><div className="oj-sb-toggle-knob"></div></div>
                          <span>Mark as “In Outreach”</span>
                          <InfoTip>
                            On: we change their status to In Outreach while the agent runs, so they leave the fresh-lead list and are not mailed twice. Off: we leave Not Contacted, Introduced, and so on exactly as they are — useful when you only prepare or qualify.
                          </InfoTip>
                        </div>
                        <p className="oj-sb-option-note">
                          {prepOnly
                            ? "Their current status is left untouched."
                            : "They show as In Outreach so they are not contacted again by mistake."}
                        </p>
                      </div>
                      <div className="oj-sb-field">
                        <label>Order</label>
                        <select value={contactPool.sortBy || "random"} onChange={(e) => setContactPool((p) => ({ ...p, sortBy: e.target.value }))}>
                          {SORT_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isDetail && (
                <>
                  {/* Stats */}
                  <div className="oj-sb-section">
                    <div className="oj-sb-section-title"><i className="fa fa-chart-bar"></i> Performance</div>
                    {totalContacts > 0 && (
                      <div className="oj-sb-progress">
                        <div className="oj-sb-progress-bar">
                          <div className="oj-sb-progress-fill" style={{ width: `${progress}%` }}></div>
                        </div>
                        <span className="oj-sb-progress-label">{progress}% complete</span>
                      </div>
                    )}
                    {(() => {
                      const drop = dropoutRate(stats, { isPrepare: detailIsPrepare });
                      const daily = detailIsPrepare
                        ? (job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT)
                        : (job.dailyLimit || 30);
                      return (
                        <div className="oj-stat-strip oj-stat-strip--sidebar" aria-label="Job performance">
                          <div className="oj-stat-cell">
                            <span className="oj-stat-val">{totalContacts}</span>
                            <span className="oj-stat-lbl">Contacts</span>
                            <span className="oj-stat-hint">{daily}/day</span>
                          </div>
                          {detailIsPrepare ? (
                            <>
                              <div className="oj-stat-cell" title={`Waiting in Prepared Mails. Cap ${preparedQueueCap}.`}>
                                <span className="oj-stat-val">{qStats.awaiting_send || 0}</span>
                                <span className="oj-stat-lbl">Ready</span>
                              </div>
                              <div className="oj-stat-cell">
                                <span className="oj-stat-val">{stats.prepared || 0}</span>
                                <span className="oj-stat-lbl">Written</span>
                              </div>
                            </>
                          ) : (
                            <div className="oj-stat-cell">
                              <span className="oj-stat-val">{stats.sent || 0}</span>
                              <span className="oj-stat-lbl">Sent</span>
                            </div>
                          )}
                          <div className="oj-stat-cell">
                            <span className="oj-stat-val">{stats.replied || 0}</span>
                            <span className="oj-stat-lbl">Replied</span>
                          </div>
                          <div className="oj-stat-cell">
                            <span className="oj-stat-val">{qStats.pending || 0}</span>
                            <span className="oj-stat-lbl">Pending</span>
                          </div>
                          <DropoutStatCell drop={drop} onOpen={() => setDropoutJobId(detailJobId)} />
                        </div>
                      );
                    })()}
                  </div>

                  {/* Job Info */}
                  <div className="oj-sb-section">
                    <div className="oj-sb-section-title"><i className="fa fa-info-circle"></i> Settings</div>
                    {/* Daily limit — inline editable even on running jobs.
                        Only locked once the job is in a terminal state. Prepare
                        jobs edit how many mails are written per day instead. */}
                    {(() => {
                      const isLocked = job.status === "completed";
                      const isPrepare = job.deliveryMode === "prepare";
                      const label = isPrepare ? "Prepare per day" : "Daily limit";
                      const ceiling = isPrepare ? PREPARE_DAILY_LIMIT_MAX : Math.max(1, accountDailyLimit || 1);
                      const storedRaw = isPrepare
                        ? (job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT)
                        : (job.dailyLimit || 30);
                      const stored = Math.max(1, Math.min(storedRaw, ceiling));
                      const current = editDailyLimit != null ? editDailyLimit : stored;
                      const isDirty = editDailyLimit != null && editDailyLimit !== stored;
                      if (isLocked) {
                        return (
                          <div className="oj-sb-info-row">
                            <span>{label}</span>
                            <strong>{stored}/day</strong>
                          </div>
                        );
                      }
                      return (
                        <div className="oj-sb-editable-field">
                          <div className="oj-sb-editable-head">
                            <span className="oj-sb-editable-label">
                              <i className={`fa ${isPrepare ? "fa-pen-to-square" : "fa-paper-plane"}`}></i> {label}
                            </span>
                            <span className={`oj-sb-editable-value${isDirty ? " dirty" : ""}`}>
                              <strong>{current}</strong>
                              <span className="oj-sb-editable-suffix">/day</span>
                              {savingDailyLimit && <i className="fa fa-spinner fa-spin"></i>}
                            </span>
                          </div>
                          <input
                            type="range"
                            className="oj-slider compact"
                            min={1}
                            max={ceiling}
                            value={current}
                            style={{ "--oj-fill": `${ceiling > 1 ? ((current - 1) / (ceiling - 1)) * 100 : 100}%` }}
                            onChange={(e) => setEditDailyLimit(parseInt(e.target.value, 10) || 1)}
                            onMouseUp={(e) => saveJobDailyLimit(detailJobId, e.target.value)}
                            onTouchEnd={(e) => saveJobDailyLimit(detailJobId, e.target.value)}
                            onKeyUp={(e) => {
                              if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
                                saveJobDailyLimit(detailJobId, e.target.value);
                              }
                            }}
                          />
                          <div className="oj-sb-editable-meta">
                            <span>1</span>
                            <span className="oj-sb-editable-hint">
                              {isDirty
                                ? "Release to apply — queued contacts are retimed"
                                : isPrepare
                                  ? `Applies now. Max ${ceiling}/day`
                                  : `Applies now. Account ceiling ${ceiling}/day`}
                            </span>
                            <span>{ceiling}</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="oj-sb-info-row">
                      <span>Delivery</span>
                      <strong>{job.deliveryMode === "prepare" ? "Prepare only" : "Send automatically"}</strong>
                    </div>
                    {job.deliveryMode !== "prepare" && (
                      <>
                        <div className="oj-sb-info-row"><span>Window</span><strong>{job.sendingWindowStart || "09:00"} — {job.sendingWindowEnd || "17:00"}</strong></div>
                        <div className="oj-sb-info-row"><span>Weekends</span><strong>{job.skipWeekends !== false ? "Paused" : "Active"}</strong></div>
                      </>
                    )}
                    <div className="oj-sb-info-row"><span>Schedule</span><strong>{isApiPool(job.contactPool) ? "API trigger" : (job.recurring ? "Daily (recurring)" : "One-time")}</strong></div>
                    {job.prepOnly && <div className="oj-sb-info-row"><span>Status</span><strong>Left unchanged</strong></div>}
                    {isApiPool(job.contactPool) && job.apiTriggerKey && (
                      <div className="oj-sb-info-row"><span>Trigger key</span><strong>{job.apiTriggerKey}</strong></div>
                    )}
                    {isApiPool(job.contactPool) && job.lastApiTriggerAt && (
                      <div className="oj-sb-info-row"><span>Last API trigger</span><strong>{new Date(job.lastApiTriggerAt).toLocaleString()}</strong></div>
                    )}
                    {job.recurring && job.lastRescanAt && <div className="oj-sb-info-row"><span>Last rescan</span><strong>{new Date(job.lastRescanAt).toLocaleString()}</strong></div>}
                    {job.contactPool?.statusFilter && <div className="oj-sb-info-row"><span>Status filter</span><strong>{String(job.contactPool.statusFilter)}</strong></div>}
                    {job.contactPool?.tagFilter && (
                      <div className="oj-sb-info-row">
                        <span>Required tag</span>
                        <strong>{labelForPoolTag(job.contactPool.tagFilter, { jobs, tags: allTags })}</strong>
                      </div>
                    )}
                    {describePoolLists(job.contactPool) && (() => {
                      const poolInfo = resolvePoolLists(job.contactPool);
                      const label = poolInfo.mode === "api"
                        ? "Source"
                        : poolInfo.mode === "manual"
                          ? "Source"
                          : (poolInfo.names.length > 1 ? "Lists" : "List");
                      return <div className="oj-sb-info-row"><span>{label}</span><strong>{describePoolLists(job.contactPool)}</strong></div>;
                    })()}
                    {!isApiPool(job.contactPool) && (
                      <div className="oj-sb-info-row"><span>Order</span><strong>{(SORT_OPTIONS.find((s) => s.value === (job.contactPool?.sortBy || "random")) || SORT_OPTIONS[0]).label}</strong></div>
                    )}
                    {job.startedAt && <div className="oj-sb-info-row"><span>Started</span><strong>{new Date(job.startedAt).toLocaleDateString()}</strong></div>}
                  </div>

                  {/* Paused notification */}
                  {job.status === "paused" && job.pauseReason && job.pauseReason !== "stopping" && (
                    <div className="oj-pause-banner">
                      <div className="oj-pause-banner-icon"><i className="fa fa-exclamation-circle"></i></div>
                      <div className="oj-pause-banner-body">
                        <strong>Job paused</strong>
                        <span>{job.pauseMessage || job.pauseReason.replace(/_/g, " ")}</span>
                        {job.pauseReason === "platform_credits_exhausted" && job.autoResumeAt > 0 && (
                          <span>Next attempt: {new Date(job.autoResumeAt).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
                        )}
                      </div>
                      <button className="oj-pause-banner-btn" onClick={() => toggleJobPause(detailJobId)} title="Resume job">
                        <i className="fa fa-play"></i> Resume
                      </button>
                    </div>
                  )}

                  {/* Test Run */}
                  {testRunOpen && (
                    <div className="oj-sb-section">
                      <div className="oj-sb-section-title"><i className="fa fa-flask"></i> Test Run</div>
                      <div className="oj-test-search">
                        <div className="oj-test-input-wrap">
                          <i className="fa fa-search"></i>
                          <input type="text" placeholder="Search contact..." value={testSearchQuery} onChange={(e) => setTestSearchQuery(e.target.value)} autoFocus />
                          {isTestSearching && <i className="fa fa-spinner fa-spin"></i>}
                        </div>
                        {testSearchResults.length > 0 && (
                          <div className="oj-test-results">
                            {testSearchResults.map((c) => {
                              const allowNoEmail = jobCanDiscoverEmail(job.steps);
                              const canAdd = c.hasEmail || allowNoEmail;
                              return (
                              <div key={c.id} className={`oj-test-result${!canAdd ? " disabled" : ""}`} onClick={() => canAdd && addTestContact(c.id, detailJobId)}>
                                <div><span className="oj-test-name">{c.name}</span><span className="oj-test-email">{c.email || "No email"}</span></div>
                                {canAdd ? <i className="fa fa-plus-circle"></i> : <span className="oj-test-no-email">No email</span>}
                              </div>
                              );
                            })}
                          </div>
                        )}
                        {testSearchQuery.length >= 2 && !isTestSearching && testSearchResults.length === 0 && (
                          <div className="oj-test-empty">No results for "{testSearchQuery}"</div>
                        )}
                      </div>
                      {testRunStatus && (
                        <div className={`oj-sb-hint ${testRunStatus.type === "success" ? "success" : "warning"}`}>
                          <i className={`fa ${testRunStatus.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
                          {testRunStatus.msg}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ─── CANVAS ─── */}
            <div className="oj-canvas">
              {isBuilder && (
                <>
                  {/* Validation errors */}
                  {validationErrors.length > 0 && (
                    <div className="oj-canvas-banner error">
                      <i className="fa fa-exclamation-circle"></i>
                      <div className="oj-canvas-banner-list">
                        {validationErrors.map((err, i) => <span key={i}>{err}</span>)}
                      </div>
                    </div>
                  )}

                  {structureLocked && (
                    <div className="oj-canvas-banner live">
                      <i className="fa fa-lock"></i>
                      <div>
                        This agent is running. You can change step settings — template, ICP, score, wait days.
                        Steps cannot be added, moved or deleted. Changes apply to contacts that have not finished that step yet.
                      </div>
                    </div>
                  )}

                  {/* Sequence */}
                  <div className="oj-pipeline">
                    {steps.length === 0 && (
                      <div className="oj-pipeline-empty">
                        <div className="oj-pipeline-empty-icon">
                          <img src="/img/star.svg" alt="" />
                        </div>
                        <h2>Build your outreach agent</h2>
                        <p>
                          {deliveryMode === "prepare"
                            ? "Stack the sequence here — write, then prepare."
                            : "Stack the sequence here — write, then send."}
                        </p>
                        <div className="oj-ghost-stack">
                          <div className="oj-start-node">
                            <span className="oj-start-icon">
                              <img src="/img/star.svg" alt="" />
                            </span>
                            <span className="oj-ghost-text">
                              <strong>Start Agent</strong>
                              <em>The agent begins here</em>
                            </span>
                          </div>
                          <div className="oj-connector">
                            <div className="oj-connector-line"></div>
                            <div className="oj-connector-dot"></div>
                          </div>
                          <button type="button" className="oj-ghost-node" onClick={() => addStep("send_email")}>
                            <span className="oj-ghost-num">1</span>
                            <span className="oj-ghost-icon oj-ghost-icon--send">
                              <i className="fa fa-envelope"></i>
                            </span>
                            <span className="oj-ghost-text">
                              <strong>{stepLabel("send_email", deliveryMode === "prepare")}</strong>
                              <em>{deliveryMode === "prepare" ? "Park it in Prepared Mails" : "Send with a template"}</em>
                            </span>
                            <i className="fa fa-plus oj-ghost-add"></i>
                          </button>
                          <div className="oj-connector">
                            <div className="oj-connector-line"></div>
                            <div className="oj-connector-dot"></div>
                          </div>
                          <div className="oj-ghost-node oj-ghost-node--hint">
                            <span className="oj-ghost-num">+</span>
                            <span className="oj-ghost-text">
                              <strong>Then wait, qualify, tag…</strong>
                              <em>More steps in the dock below</em>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {steps.length > 0 && (
                      <>
                        <div className="oj-start-node">
                          <span className="oj-start-icon">
                            <img src="/img/star.svg" alt="" />
                          </span>
                          <span className="oj-ghost-text">
                            <strong>Start Agent</strong>
                            <em>The agent begins here</em>
                          </span>
                        </div>
                        <div className="oj-connector">
                          <div className="oj-connector-line"></div>
                          <div className="oj-connector-dot"></div>
                        </div>
                      </>
                    )}
                    {steps.map((step, idx) => {
                      const def = STEP_TYPES.find((t) => t.id === step.type);
                      return (
                        <React.Fragment key={step.id}>
                          <div className={`oj-node oj-node--${step.type}`}>
                            <div className="oj-node-head">
                              <div className="oj-node-step-num">{idx + 1}</div>
                              <div className={`oj-node-icon oj-node-icon--${step.type}`}><i className={`${def?.iconPrefix || "fa"} ${def?.icon || "fa-circle"}`}></i></div>
                              <div className="oj-node-info">
                                <div className="oj-node-title">{stepLabel(step.type, deliveryMode === "prepare")}</div>
                                <div className="oj-node-desc">
                                  {step.type === "send_email" && (
                                    <>
                                      {step.templateName || "Select a template"}
                                      {Object.keys(cleanTemplateByList(step.templateByList)).length > 0 && (
                                        <span className="oj-desc-badge maps" title="Some import lists use a different template">
                                          {Object.keys(cleanTemplateByList(step.templateByList)).length} list map{Object.keys(cleanTemplateByList(step.templateByList)).length === 1 ? "" : "s"}
                                        </span>
                                      )}
                                      {(() => {
                                        const sg = globalSafeGuardCopy({
                                          enabled: globalSafeGuardOn,
                                          followUp: isFollowUpEmailType(topicOptions.find((t) => t.id === step.templateId)?.type),
                                        });
                                        return (
                                          <span className={`oj-desc-badge${sg.enabled ? " shield" : ""}`} title={sg.title}>
                                            <i className={sg.icon}></i>
                                          </span>
                                        );
                                      })()}
                                      <span className="oj-desc-badge gate" title={noRecipientChipTitle(deliveryMode === "prepare", jobName)}>needs email</span>
                                    </>
                                  )}
                                  {step.type === "add_tag" && (step.tagName || "Select a tag")}
                                  {step.type === "add_to_flow" && (step.flowName || "Select a flow")}
                                  {step.type === "wait" && (
                                    <>
                                      Pause for {step.delayDays || 3} days after the mail is sent
                                    </>
                                  )}
                                  {step.type === "check_reply" && (idx === 0
                                    ? "Inbox gate — stop if they already replied"
                                    : "Stops sequence on reply")}
                                  {step.type === "clean" && (
                                    <>
                                      {step.guidelineName || "AI qualification"}
                                      {listMapCount(step, "guidelineByList") > 0 && (
                                        <span className="oj-desc-badge maps" title="Some import lists use a different guideline">
                                          {listMapCount(step, "guidelineByList")} list map{listMapCount(step, "guidelineByList") === 1 ? "" : "s"}
                                        </span>
                                      )}
                                      {" "}
                                      {(step.onFail || "remove") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}
                                    </>
                                  )}
                                  {step.type === "web_research" && (
                                    <>
                                      {(() => {
                                        const resolved = resolveWebResearchModules(step, { activeModules: spyActiveModules });
                                        const dmOn = resolved.modules.includes("decisionMakers");
                                        if (resolved.source === "step") {
                                          return `Custom modes (${resolved.modules.length}) · ${dmOn ? "Decision Makers on" : "Decision Makers off"}`;
                                        }
                                        return dmOn
                                          ? "World Settings · Decision Makers on"
                                          : "World Settings · Decision Makers off";
                                      })()}
                                      {" "}
                                      {(step.onFail || "remove") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}
                                    </>
                                  )}
                                  {step.type === "instagram_research" && (
                                    <>Instagram data {(step.onFail || "remove") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}</>
                                  )}
                                  {step.type === "google_review_audit" && (
                                    <>Google reviews {(step.onFail || "skip") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}</>
                                  )}
                                  {step.type === "data_connector" && (
                                    <>{connectorNameFor(step) || "Choose a data source"} {(step.onFail || "skip") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}</>
                                  )}
                                  {step.type === "chance_radar" && (
                                    <>{chanceRadars.find((r) => r.id === step.chanceRadarId)?.name || step.chanceRadarName || "Choose a Chance Radar"} <span className="oj-desc-badge soft">context</span></>
                                  )}
                                  {step.type === "potential_analyzer" && (
                                    <>
                                      Potential ≥ {typeof step.minScore === "number" ? step.minScore : 4}/10
                                      {step.icpProfileId && (
                                        <> · {potentialProfiles.find((p) => p.id === step.icpProfileId)?.name || step.icpProfileName || "ICP profile"}</>
                                      )}
                                      {listMapCount(step, "icpByList") > 0 && (
                                        <span className="oj-desc-badge maps" title="Some import lists use a different ICP profile">
                                          {listMapCount(step, "icpByList")} list map{listMapCount(step, "icpByList") === 1 ? "" : "s"}
                                        </span>
                                      )}
                                      {" "}
                                      {(step.onFail || "remove") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}
                                    </>
                                  )}
                                  {step.type === "dm_email_finder" && (
                                    <>Find DM email{step.deepSearch ? " (deep)" : ""} {(step.onFail || "remove") === "remove" ? <span className="oj-desc-badge gate">drop</span> : <span className="oj-desc-badge soft">keep going</span>}</>
                                  )}
                                  {step.type === "end_flow" && `Tags “${completedTagLabel(jobName)}”`}
                                </div>
                              </div>
                              {!structureLocked && (
                                <div className="oj-node-actions">
                                  <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} title="Move up"><i className="fa fa-chevron-up"></i></button>
                                  <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} title="Move down"><i className="fa fa-chevron-down"></i></button>
                                  <button onClick={() => removeStep(idx)} className="oj-node-remove" title="Remove"><i className="fa fa-trash-alt"></i></button>
                                </div>
                              )}
                            </div>

                            {step.type === "send_email" && (
                              <div className="oj-node-body">
                                <div className="oj-pe">
                                <div className="oj-pe-panel">
                                <label className="oj-node-body-label">Default template</label>
                                <select
                                  className={!step.templateId ? "oj-select-error" : ""}
                                  value={step.templateId || ""}
                                  onChange={(e) => {
                                    const nextId = e.target.value;
                                    const tpl = topicOptions.find((t) => t.id === nextId);
                                    setSteps((prev) => prev.map((s, i) => {
                                      if (i !== idx) return s;
                                      const map = { ...cleanTemplateByList(s.templateByList) };
                                      const names = { ...readTemplateByListNames(s) };
                                      Object.keys(map).forEach((list) => {
                                        if (map[list] === nextId) {
                                          delete map[list];
                                          delete names[list];
                                        }
                                      });
                                      return {
                                        ...s,
                                        templateId: nextId,
                                        templateName: tpl?.name || "",
                                        templateByList: map,
                                        templateByListNames: names,
                                        senderFrom: isFollowUpEmailType(tpl?.type) ? SENDER_FROM_PREVIOUS : SENDER_FROM_POOL,
                                        senderAccountIds: isFollowUpEmailType(tpl?.type) ? [] : (s.senderAccountIds || []),
                                      };
                                    }));
                                  }}
                                >
                                  <option value="">Choose template...</option>
                                  {topicOptions
                                    .filter((t) => t.id === step.templateId || !steps.some((s, i) => i !== idx && s.type === "send_email" && s.templateId === t.id))
                                    .map((t) => (
                                      <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                                {!step.templateId && <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Template required</span>}
                                <ListOverridePicker
                                  contactPool={contactPool}
                                  step={step}
                                  spec={LIST_MAP_TEMPLATE}
                                  options={topicOptions}
                                  optionLabel={(t) => t.name}
                                  heading="Template per list"
                                  hint="Each list can use a different template. Default uses the one above."
                                  singleHint="Used for every selected list. Pick two or more lists in the contact pool to assign a different template per list."
                                  onChange={(listName, value) => setStepListTemplate(idx, listName, value, topicOptions)}
                                />
                                {(() => {
                                  const ready = senderEmails.filter(isReadySender);
                                  const selected = normalizeSenderAccountIds(step.senderAccountIds);
                                  const tplType = topicOptions.find((t) => t.id === step.templateId)?.type;
                                  const followUp = isFollowUpEmailType(tplType);
                                  const previous = usesPreviousSender(step, tplType);
                                  const useAll = !previous && !selected;
                                  return (
                                    <div className="oj-sender-pick">
                                      <label className="oj-node-body-label">From accounts</label>
                                      <p className="oj-list-tpl-hint">
                                        {previous
                                          ? "Uses the inbox that sent the first mail to this contact. Footer and signature stay the same."
                                          : deliveryMode === "prepare"
                                            ? (useAll
                                              ? "Prepared mails are split evenly across every ready inbox, including ones you add later."
                                              : "Prepared mails are split evenly across these inboxes. New accounts stay out until you add them here.")
                                            : (useAll
                                              ? "Sends are split across every ready inbox, including ones you add later."
                                              : "Sends are split across these inboxes. New accounts stay out until you add them here.")}
                                      </p>
                                      <div className="oj-sender-picks">
                                        {followUp && (
                                          <button
                                            type="button"
                                            className={`oj-gate-opt${previous ? " active" : ""}`}
                                            onClick={() => updateStep(idx, { senderFrom: SENDER_FROM_PREVIOUS, senderAccountIds: [] })}
                                            aria-pressed={previous}
                                          >
                                            From previous account
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className={`oj-gate-opt oj-gate-opt--multi${useAll ? " active" : ""}`}
                                          onClick={() => updateStep(idx, { senderFrom: SENDER_FROM_POOL, senderAccountIds: [] })}
                                          aria-pressed={useAll}
                                        >
                                          <span className="oj-checkdot" aria-hidden="true"></span>
                                          All that work
                                          {ready.length > 0 && <em>{ready.length}</em>}
                                        </button>
                                        {!previous && ready.map((acc) => {
                                          const on = !useAll && selected.includes(acc.id);
                                          return (
                                            <button
                                              key={acc.id}
                                              type="button"
                                              className={`oj-gate-opt oj-gate-opt--multi${on ? " active" : ""}`}
                                              onClick={() => updateStep(idx, { senderFrom: SENDER_FROM_POOL, senderAccountIds: toggleSenderAccountId(step.senderAccountIds, acc.id) })}
                                              aria-pressed={on}
                                              title={acc.sender_name ? `${acc.sender_name} · ${acc.email}` : acc.email}
                                            >
                                              <span className="oj-checkdot" aria-hidden="true"></span>
                                              {acc.email}
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {ready.length === 0 && (
                                        <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Add a sender in Settings → Email</span>
                                      )}
                                    </div>
                                  );
                                })()}
                                </div>
                                {(() => {
                                  const note = noRecipientNote({
                                    prepareMode: deliveryMode === "prepare",
                                    decisionMakerOnly: (() => {
                                      const tpl = topicOptions.find((t) => t.id === step.templateId);
                                      return tpl?.emailPreference === "decision_maker" && tpl?.dmFallback === "strict";
                                    })(),
                                    jobName,
                                  });
                                  const followUp = isFollowUpEmailType(topicOptions.find((t) => t.id === step.templateId)?.type);
                                  const sg = globalSafeGuardCopy({ enabled: globalSafeGuardOn, followUp });
                                  return (
                                    <div className="oj-pe-panel oj-pe-notes">
                                      <div className="oj-pe-note">
                                        <div className="oj-pe-note-icon"><i className={sg.icon}></i></div>
                                        <div className="oj-pe-note-text">
                                          <strong>{sg.label}</strong>
                                          <span>{sg.detail}</span>
                                        </div>
                                      </div>
                                      <div className="oj-pe-note">
                                        <div className="oj-pe-note-icon"><i className="fa fa-user-minus"></i></div>
                                        <div className="oj-pe-note-text">
                                          <strong>{note.title}</strong>
                                          <span>{note.body}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                </div>
                              </div>
                            )}
                            {step.type === "add_tag" && (
                              <div className="oj-node-body">
                                <label className="oj-node-body-label">Tag to add</label>
                                <select
                                  className={!step.tagId ? "oj-select-error" : ""}
                                  value={step.tagId || ""}
                                  onChange={(e) => {
                                    const tag = allTags.find((t) => t.id === e.target.value);
                                    updateStep(idx, { tagId: e.target.value, tagName: tag?.tag || tag?.name || "" });
                                  }}
                                >
                                  <option value="">Choose tag...</option>
                                  {allTags.map((tag) => (
                                    <option key={tag.id} value={tag.id}>{tag.tag || tag.name || tag.id}</option>
                                  ))}
                                </select>
                                {!step.tagId && <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Tag required</span>}
                              </div>
                            )}
                            {step.type === "add_to_flow" && (
                              <div className="oj-node-body">
                                <div className="oj-af-panel">
                                  <label className="oj-node-body-label">Flow to assign</label>
                                  <select
                                    className={!step.flowId ? "oj-select-error" : ""}
                                    value={step.flowId || ""}
                                    onChange={(e) => {
                                      const flow = flowOptions.find((f) => f.id === e.target.value);
                                      updateStep(idx, { flowId: e.target.value, flowName: flow?.name || "" });
                                    }}
                                  >
                                    <option value="">Choose flow...</option>
                                    {flowOptions.map((f) => (
                                      <option key={f.id} value={f.id}>{f.name}</option>
                                    ))}
                                  </select>
                                  {!step.flowId && <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Flow required</span>}
                                  <div className="oj-af-note">
                                    <div className="oj-af-note-icon"><i className="fa fa-project-diagram"></i></div>
                                    <div className="oj-af-note-text">
                                      <strong>Assigns this contact to a flow</strong>
                                      <span>They keep moving through this agent. The flow runs in parallel as its own automation.</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {step.type === "wait" && (
                              <div className="oj-node-body">
                                <div className="oj-wait">
                                  <div className="oj-wait-panel">
                                    <label className="oj-node-body-label">Delay duration</label>
                                    <div className="oj-wait-days">
                                      <input type="number" min={1} max={90} value={step.delayDays || 3} onChange={(e) => updateStep(idx, { delayDays: Math.max(1, parseInt(e.target.value) || 1), waitFrom: "after_send" })} />
                                      <span>days</span>
                                    </div>
                                    {idx > 0 && steps[idx - 1]?.type === "send_email" && (
                                      <span className="oj-gate-desc">
                                        {deliveryMode === "prepare"
                                          ? "Starts when you send the prepared mail. Days it sat in Prepared do not count."
                                          : "Starts when the mail is delivered. Generation time does not count."}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                            {(step.type === "clean" || step.type === "web_research" || step.type === "instagram_research" || step.type === "google_review_audit" || step.type === "data_connector" || step.type === "potential_analyzer" || step.type === "dm_email_finder") && (
                              <div className="oj-node-body">
                                {step.type === "data_connector" && (
                                  <div className="oj-dc">
                                    <label className="oj-node-body-label">Data source</label>
                                    <select
                                      className={!step.connectorId ? "oj-select-error" : ""}
                                      value={step.connectorId || ""}
                                      onChange={(e) => {
                                        const c = dataConnectors.find((x) => x.id === e.target.value);
                                        updateStep(idx, { connectorId: e.target.value, connectorName: c?.name || "" });
                                      }}
                                    >
                                      <option value="">{dataConnectors.length ? "Choose data source..." : "No data sources yet"}</option>
                                      {dataConnectors.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}{c.status === "error" ? " (needs attention)" : ""}</option>
                                      ))}
                                      {step.connectorId && !dataConnectors.some((c) => c.id === step.connectorId) && (
                                        <option value={step.connectorId}>{step.connectorName || "Deleted data source"} (deleted)</option>
                                      )}
                                    </select>
                                    {!step.connectorId && <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Data source required</span>}
                                    <span className="oj-gate-desc">
                                      {dataConnectors.length
                                        ? "Fetches live data from the platform for each contact. The result is stored on the contact and fed into the email."
                                        : "Connect a platform first under Settings → Data Sources."}
                                    </span>
                                  </div>
                                )}
                                {step.type === "chance_radar" && (
                                  <div className="oj-dc">
                                    <label className="oj-node-body-label">Chance Radar</label>
                                    <select
                                      value={step.chanceRadarId || ""}
                                      onChange={(e) => {
                                        const radar = chanceRadars.find((item) => item.id === e.target.value);
                                        updateStep(idx, { chanceRadarId: e.target.value, chanceRadarName: radar?.name || "" });
                                      }}
                                    >
                                      <option value="">{chanceRadars.length ? "Choose Chance Radar..." : "No Chance Radars yet"}</option>
                                      {chanceRadars.map((radar) => (
                                        <option key={radar.id} value={radar.id}>{radar.name || radar.topic || "Chance Radar"}</option>
                                      ))}
                                    </select>
                                    {!step.chanceRadarId && <span className="oj-node-error"><i className="fa fa-exclamation-circle"></i> Chance Radar required</span>}
                                    <span className="oj-gate-desc">Searches live chances and stores the text on the contact. A template that uses the same radar puts it into every variant. The cheaper search can include a weak hit.</span>
                                  </div>
                                )}
                                {step.type === "potential_analyzer" && (() => {
                                  const minScore = typeof step.minScore === "number" ? step.minScore : 4;
                                  return (
                                    <div className="oj-pa">
                                      {potentialProfiles.length > 0 && (
                                        <div className="oj-pa-panel">
                                          <label className="oj-node-body-label">Default ICP profile</label>
                                          <select
                                            value={step.icpProfileId || ""}
                                            onChange={(e) => {
                                              const nextId = e.target.value || "";
                                              const profile = potentialProfiles.find((p) => p.id === nextId);
                                              setSteps((prev) => prev.map((s, i) => {
                                                if (i !== idx) return s;
                                                const map = { ...cleanListOverride(s.icpByList) };
                                                const names = { ...readListOverrideNames(s, LIST_MAP_ICP) };
                                                Object.keys(map).forEach((list) => {
                                                  if (map[list] === nextId) {
                                                    delete map[list];
                                                    delete names[list];
                                                  }
                                                });
                                                return {
                                                  ...s,
                                                  icpProfileId: nextId || null,
                                                  icpProfileName: profile?.name || "",
                                                  icpByList: map,
                                                  icpByListNames: names,
                                                };
                                              }));
                                            }}
                                          >
                                            <option value="">Default profile</option>
                                            {potentialProfiles.map((p) => (
                                              <option key={p.id} value={p.id}>
                                                {p.name}{p.isDefault ? " (default)" : ""}
                                              </option>
                                            ))}
                                          </select>
                                          {step.icpProfileId && (() => {
                                            const profile = potentialProfiles.find((p) => p.id === step.icpProfileId);
                                            if (!profile) return null;
                                            return (
                                              <div className="oj-guideline-meta">
                                                <span className="oj-desc-badge context">
                                                  <i className="fa fa-sliders"></i> {profile.strictness || "balanced"}
                                                </span>
                                                {profile.revenueSize && (
                                                  <span className="oj-desc-badge context">
                                                    <i className="fa fa-euro-sign"></i> {profile.revenueSize}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()}
                                          <ListOverridePicker
                                            contactPool={contactPool}
                                            step={step}
                                            spec={LIST_MAP_ICP}
                                            options={potentialProfiles}
                                            optionLabel={(p) => `${p.name}${p.isDefault ? " (default)" : ""}`}
                                            heading="Per-list ICP profiles"
                                            hint="Optional. Leave a list on Default to use the profile above."
                                            singleHint="Used for every selected list. Pick two or more lists in the contact pool to assign a different ICP profile per list."
                                            onChange={(listName, value) => setStepListOverride(idx, listName, value, potentialProfiles, LIST_MAP_ICP)}
                                          />
                                        </div>
                                      )}
                                      <div className="oj-pa-panel">
                                        <label className="oj-node-body-label">Keep contacts with potential bigger or equal</label>
                                        <div className="oj-pa-score">
                                          <div className="oj-pa-pips" role="group" aria-label="Minimum potential score">
                                            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                                              <button
                                                key={n}
                                                type="button"
                                                className={`oj-pa-pip${n <= minScore ? " on" : ""}`}
                                                onClick={() => updateStep(idx, { minScore: n })}
                                                aria-label={`${n} of 10`}
                                                aria-pressed={n === minScore}
                                              />
                                            ))}
                                          </div>
                                          <span className="oj-pa-score-val">{minScore}<em>/10</em></span>
                                        </div>
                                        <span className="oj-gate-desc">
                                          {potentialProfiles.length > 0
                                            ? "Scored against the selected ICP profile. Below this score, the rule underneath decides: leave this agent, or keep going."
                                            : "Uses your ICP from Potential Analyzer settings. Below this score, the rule underneath decides: leave this agent, or keep going."}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {step.type === "dm_email_finder" && (
                                  <div className="oj-dmf-opts">
                                    <div className="oj-dmf-note">
                                      <div className="oj-dmf-note-icon"><i className="fa fa-forward"></i></div>
                                      <div className="oj-dmf-note-text">
                                        <strong>No decision makers?</strong>
                                        <span>This step is skipped automatically — they continue, no credits. The rule below is only when people were found but no usable email.</span>
                                      </div>
                                    </div>
                                    <label className="oj-node-gate-label">Search options</label>
                                    <div className="oj-gate-options">
                                      <button
                                        type="button"
                                        className={`oj-gate-opt oj-gate-opt--multi${step.deepSearch ? " active" : ""}`}
                                        onClick={() => updateStep(idx, { deepSearch: !step.deepSearch })}
                                        aria-pressed={!!step.deepSearch}
                                      >
                                        <span className="oj-checkdot" aria-hidden="true"></span>
                                        Deep search
                                      </button>
                                      <button
                                        type="button"
                                        className={`oj-gate-opt oj-gate-opt--multi${step.requireVerified ? " active" : ""}`}
                                        onClick={() => updateStep(idx, { requireVerified: !step.requireVerified })}
                                        aria-pressed={!!step.requireVerified}
                                      >
                                        <span className="oj-checkdot" aria-hidden="true"></span>
                                        Verified only
                                      </button>
                                    </div>
                                    <span className="oj-gate-desc">
                                      {step.deepSearch && step.requireVerified
                                        ? "More thorough search, higher credit cost. Catch-all and unverified addresses don’t count."
                                        : step.deepSearch
                                          ? "More thorough search, higher credit cost."
                                          : step.requireVerified
                                            ? "Catch-all and unverified addresses don’t count as found."
                                            : "Standard search. Unverified addresses still count."}
                                    </span>
                                  </div>
                                )}
                                {step.type === "web_research" && (() => {
                                  // Empty / missing researchModes → inherit World Settings.
                                  // First click seeds from those globals so Decision Makers
                                  // (and the rest) are not silently dropped.
                                  const resolved = resolveWebResearchModules(step, { activeModules: spyActiveModules });
                                  const isOverriding = resolved.source === "step";
                                  const shown = resolved.modules;
                                  const toggleMode = (id) => {
                                    const current = new Set(shown.filter((m) => m !== "core"));
                                    if (current.has(id)) current.delete(id); else current.add(id);
                                    const next = Array.from(current);
                                    updateStep(idx, { researchModes: next.length ? next : null });
                                  };
                                  const estCredits = shown.reduce((sum, id) => {
                                    const def = WEB_RESEARCH_MODES.find((m) => m.id === id);
                                    return sum + (def?.credits || 0);
                                  }, 1);
                                  return (
                                    <div className="oj-wr">
                                      <div className="oj-wr-modes-header">
                                        <div>
                                          <label className="oj-node-body-label">Research modes</label>
                                          <span className="oj-wr-modes-hint">
                                            {isOverriding
                                              ? `Overrides World Settings for this step (~${estCredits.toFixed(1)} credits per contact).`
                                              : "Using your World Settings — Decision Makers stay on if they are on there. Click a mode to override just this step."}
                                          </span>
                                        </div>
                                        {isOverriding && (
                                          <button
                                            type="button"
                                            className="altBtn altBtn--petrol oj-wr-modes-reset"
                                            onClick={() => updateStep(idx, { researchModes: null })}
                                            title="Fall back to your World Settings"
                                          >
                                            <i className="fa fa-rotate-left"></i> Reset to World Settings
                                          </button>
                                        )}
                                      </div>
                                      <div className="oj-wr-modes-grid">
                                        {WEB_RESEARCH_MODES.map((mode) => {
                                          const active = shown.includes(mode.id);
                                          return (
                                            <button
                                              key={mode.id}
                                              type="button"
                                              className={`oj-wr-mode${active ? " active" : ""}${mode.premium ? " premium" : ""}`}
                                              onClick={() => toggleMode(mode.id)}
                                              title={mode.hint}
                                              aria-pressed={active}
                                            >
                                              <div className="oj-wr-mode-icon">
                                                <i className={`${mode.iconPrefix || "fa"} ${mode.icon}`}></i>
                                              </div>
                                              <div className="oj-wr-mode-body">
                                                <strong>{mode.label}</strong>
                                                <span>{mode.hint}</span>
                                              </div>
                                              <div className="oj-wr-mode-meta">
                                                <span className="oj-wr-mode-credits">{mode.credits}c</span>
                                                {mode.premium && <span className="oj-wr-mode-badge">PRO</span>}
                                                <span className="oj-checkdot" aria-hidden="true"></span>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                      {isOverriding && !shown.includes("decisionMakers") && spyActiveModules.includes("decisionMakers") && (
                                        <div className="oj-wr-modes-warn">
                                          <i className="fa fa-exclamation-triangle"></i>
                                          World Settings have Decision Makers on — this step turned them off.
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                {step.type === "clean" && cleanupGuidelines.length > 0 && (
                                  <div className="oj-cl-panel">
                                    <label className="oj-node-body-label">Default guideline</label>
                                    <select
                                      value={step.guidelineId || ""}
                                      onChange={(e) => {
                                        const nextId = e.target.value || "";
                                        const gl = cleanupGuidelines.find((g) => g.id === nextId);
                                        setSteps((prev) => prev.map((s, i) => {
                                          if (i !== idx) return s;
                                          const map = { ...cleanListOverride(s.guidelineByList) };
                                          const names = { ...readListOverrideNames(s, LIST_MAP_GUIDELINE) };
                                          Object.keys(map).forEach((list) => {
                                            if (map[list] === nextId) {
                                              delete map[list];
                                              delete names[list];
                                            }
                                          });
                                          return {
                                            ...s,
                                            guidelineId: nextId || null,
                                            guidelineName: gl?.name || "",
                                            guidelineByList: map,
                                            guidelineByListNames: names,
                                          };
                                        }));
                                      }}
                                    >
                                      <option value="">Default guideline</option>
                                      {cleanupGuidelines.map((g) => (
                                        <option key={g.id} value={g.id}>
                                          {g.name}{g.isDefault ? " (default)" : ""}{g.hyperIntelligence ? " — Hyper" : ""}
                                        </option>
                                      ))}
                                    </select>
                                    {step.guidelineId && (() => {
                                      const gl = cleanupGuidelines.find((g) => g.id === step.guidelineId);
                                      return gl ? (
                                        <div className="oj-guideline-meta">
                                          {gl.hyperIntelligence && <span className="oj-desc-badge hyper"><i className="fa fa-bolt"></i> Hyper</span>}
                                          {gl.businessContext && <span className="oj-desc-badge context"><i className="fa fa-building"></i> Context</span>}
                                        </div>
                                      ) : null;
                                    })()}
                                    <ListOverridePicker
                                      contactPool={contactPool}
                                      step={step}
                                      spec={LIST_MAP_GUIDELINE}
                                      options={cleanupGuidelines}
                                      optionLabel={(g) => `${g.name}${g.isDefault ? " (default)" : ""}${g.hyperIntelligence ? " — Hyper" : ""}`}
                                      heading="Per-list guidelines"
                                      hint="Optional. Leave a list on Default to use the guideline above."
                                      singleHint="Used for every selected list. Pick two or more lists in the contact pool to assign a different guideline per list."
                                      onChange={(listName, value) => setStepListOverride(idx, listName, value, cleanupGuidelines, LIST_MAP_GUIDELINE)}
                                    />
                                  </div>
                                )}
                                <div className="oj-node-gate">
                                  <label className="oj-node-gate-label">{failGateHeading(step.type)}</label>
                                  <div className="oj-gate-options">
                                    <button
                                      className={`oj-gate-opt${(step.onFail || "remove") === "remove" ? " active" : ""}`}
                                      onClick={() => updateStep(idx, { onFail: "remove" })}
                                    >
                                      <i className="fa fa-user-slash"></i> Drop from agent
                                    </button>
                                    <button
                                      className={`oj-gate-opt${step.onFail === "skip" ? " active" : ""}`}
                                      onClick={() => updateStep(idx, { onFail: "skip" })}
                                    >
                                      <i className="fa fa-forward"></i> Keep going
                                    </button>
                                  </div>
                                  <span className="oj-gate-desc">
                                    {(step.onFail || "remove") === "remove"
                                      ? failGateRemoveDesc(step.type)
                                      : failGateSkipDesc(step.type)}
                                  </span>
                                  {(step.onFail || "remove") === "remove" && (
                                    <div className="oj-node-gate oj-node-gate--sub">
                                      <label className="oj-node-gate-label">What happens to them</label>
                                      <div className="oj-gate-options">
                                        <button
                                          className={`oj-gate-opt${(step.failAction || "tag") === "tag" ? " active" : ""}`}
                                          onClick={() => updateStep(idx, { failAction: "tag" })}
                                        >
                                          <i className="fa fa-tag"></i> Keep in database
                                        </button>
                                        <button
                                          className={`oj-gate-opt${step.failAction === "delete" ? " active" : ""}`}
                                          onClick={() => updateStep(idx, { failAction: "delete" })}
                                        >
                                          <i className="fa fa-trash-alt"></i> Delete contact
                                        </button>
                                        <button
                                          className={`oj-gate-opt${step.failAction === "delete_and_block" ? " active" : ""}`}
                                          onClick={() => updateStep(idx, { failAction: "delete_and_block" })}
                                        >
                                          <i className="fa fa-ban"></i> Delete & block
                                        </button>
                                      </div>
                                      <span className="oj-gate-desc">{failActionDesc(step.failAction)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {step.type === "check_reply" && (
                              <div className="oj-node-body">
                                <div className="oj-cr-panel">
                                  <div className="oj-cr-note">
                                    <div className="oj-cr-note-icon"><i className="fa fa-reply"></i></div>
                                    <div className="oj-cr-note-text">
                                      <strong>{idx === 0 ? "Inbox gate" : "Stops on reply"}</strong>
                                      <span>
                                        {idx === 0
                                          ? "Scans the inbox for a reply since the last email to this contact — including mail from another agent. Replied contacts leave; everyone else continues."
                                          : "Contacts that have replied since the last send in this sequence are removed automatically."}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {step.type === "end_flow" && (
                              <div className="oj-node-body">
                                <div className="oj-ef-panel">
                                  <div className="oj-ef-note">
                                    <div className="oj-ef-note-icon"><i className="fa fa-flag-checkered"></i></div>
                                    <div className="oj-ef-note-text">
                                      <strong>Tags “{completedTagLabel(jobName)}”</strong>
                                      <span>They stay in your list with this tag. A later agent can start from it under Required tag. Place this as the last step.</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          {idx < steps.length - 1 && (
                            <div className="oj-connector">
                              <div className="oj-connector-line"></div>
                              <div className="oj-connector-dot"></div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Add Step Dock — hidden while the agent is live so the
                      sequence cannot grow or shrink under queued contacts. */}
                  {!structureLocked && (
                  <div className="oj-add-dock">
                    <div className="oj-add-dock-header">
                      <i className="fa fa-plus-circle"></i>
                      <span>Add step</span>
                    </div>
                    <div className="oj-add-dock-grid">
                      {STEP_TYPES.map((type) => {
                        const hasEndFlow = type.id === "end_flow" && steps.some((s) => s.type === "end_flow");
                        const locked = isStepLocked(type.id);
                        const CardTag = locked ? "div" : "button";
                        return (
                          <CardTag
                            key={type.id}
                            type={locked ? undefined : "button"}
                            className={`oj-add-card oj-add-card--${type.id}${locked ? " locked" : ""}`}
                            onClick={locked ? undefined : () => !hasEndFlow && addStep(type.id)}
                            disabled={locked ? undefined : hasEndFlow}
                            title={locked ? undefined : hasEndFlow ? "Only one End Flow per job" : ""}
                          >
                            <div className="oj-add-card-icon">
                              <i className={`${type.iconPrefix || "fa"} ${type.icon}`}></i>
                            </div>
                            <div className="oj-add-card-text">
                              <strong>{stepLabel(type.id, deliveryMode === "prepare")}</strong>
                              {locked ? (
                                <ResearchLockedNotice
                                  featureName={type.label}
                                  accountEmail={researchContext.accountEmail}
                                  worldId={researchContext.worldId}
                                />
                              ) : (
                                <span>
                                  {type.id === "clean" && "AI qualification"}
                                  {type.id === "web_research" && "Enrich via website"}
                                  {type.id === "instagram_research" && "Fetch IG profile"}
                                  {type.id === "google_review_audit" && "Complaints & praise from Google"}
                                  {type.id === "data_connector" && "Live data from your platforms"}
                                  {type.id === "potential_analyzer" && "Score ICP fit & gate"}
                                  {type.id === "dm_email_finder" && "Find decision-maker email"}
                                  {type.id === "send_email" && (deliveryMode === "prepare" ? "Write it, you send it" : "Send with template")}
                                  {type.id === "add_tag" && "Label the contact"}
                                  {type.id === "add_to_flow" && "Assign to automation"}
                                  {type.id === "wait" && "Pause between steps"}
                                  {type.id === "check_reply" && "Stop if inbox has a reply"}
                                  {type.id === "end_flow" && "Complete the sequence"}
                                </span>
                              )}
                            </div>
                            <i className={`fa ${locked ? "fa-lock" : "fa-plus"} oj-add-card-plus`}></i>
                          </CardTag>
                        );
                      })}
                    </div>
                  </div>
                  )}
                </>
              )}

              {isDetail && (
                <>
                  {/* Live Funnel */}
                  <div className="oj-canvas-section">
                    <div className="oj-canvas-section-title">
                      <i className="fa fa-stream"></i> Live Sequence
                      <span className="oj-canvas-section-count">{job.steps?.length || 0} steps</span>
                    </div>
                    <div className="oj-pipeline">
                      {stepFunnel.map(({ step, idx, total, waiting, ready, processing, awaitingSend, dropped, replied: repliedAtStep, pendingReplied: pendingRepliedAtStep, failed: failedAtStep, skipped: skippedAtStep, passed: passedAtStep, repliedGlobal }) => {
                        const def = STEP_TYPES.find((t) => t.id === step.type);
                        // For check_reply, include job-wide replies so the step is expandable
                        // and the "X replied" badge makes visual sense (replies are typically
                        // attributed to earlier steps but semantically belong here).
                        const isCheckReply = step.type === "check_reply";
                        const globalRepliedCount = isCheckReply && Array.isArray(repliedGlobal) ? repliedGlobal.length : 0;
                        // For check_reply we compare effective (confirmed + pending) against
                        // only the confirmed ones, so we can surface "N pending auto-stop".
                        const confirmedRepliedCount = isCheckReply ? repliedEntriesAll.length : 0;
                        const pendingAutoStopCount = isCheckReply
                          ? Math.max(0, globalRepliedCount - confirmedRepliedCount)
                          : pendingRepliedAtStep || 0;
                        const hasContacts = total > 0 || dropped > 0 || passedAtStep > 0 || globalRepliedCount > 0;
                        const isExpanded = expandedStepIdx === idx;
                        const stepContacts = hasContacts
                          ? contactsAtStep(queueEntries, idx, {
                              isCheckReply,
                              repliedGlobal: isCheckReply && Array.isArray(repliedGlobal) ? repliedGlobal : null,
                            })
                          : [];
                        const stepVisibleN = pageSizeOrDefault(stepListVisible[idx]);
                        const visibleStepContacts = stepContacts.slice(0, stepVisibleN);
                        const stepCanLoadMore = canLoadMoreContacts(stepContacts.length, stepVisibleN, archiveHasMore);

                        // ── Build rich config chips per step type ────────────────────
                        const chips = [];
                        if (step.type === "send_email") {
                          const tpl = topicOptions.find((t) => t.id === step.templateId);
                          if (tpl) {
                            chips.push({ k: "tpl", icon: "fa fa-envelope", label: tpl.name, tone: "primary", title: "Default email template" });
                            if (tpl.subjectLinesCount > 0) chips.push({ k: "subj", icon: "fa fa-heading", label: `${tpl.subjectLinesCount} subject${tpl.subjectLinesCount === 1 ? "" : "s"}`, tone: "neutral", title: "Configured subject lines" });
                            if (tpl.smartSubjectLine) chips.push({ k: "smart", icon: "fa fa-wand-magic-sparkles", label: tpl.smartSubjectStyle ? `Smart Subject (${tpl.smartSubjectStyle})` : "Smart Subject", tone: "violet", title: "AI-generated subject line" });
                            if (tpl.subjectMode === "reply_to_previous") chips.push({ k: "rtp", icon: "fa fa-reply", label: `Reply-to-Previous${tpl.replyPrefix ? ` (${tpl.replyPrefix})` : ""}`, tone: "teal", title: "Uses previous email's subject with Re:/AW:" });
                            if (tpl.hyperIntelligence) chips.push({ k: "hyp", icon: "fa fa-bolt", label: "Hyper", tone: "amber", title: "Hyper Intelligence enabled" });
                            if (tpl.ultraMode) chips.push({ k: "ult", icon: "fa fa-rocket", label: "Ultra", tone: "pink", title: "Ultra Mode enabled" });
                            if (tpl.hasReferenceFile) chips.push({ k: "ref", icon: "fa fa-paperclip", label: tpl.referenceFileName ? `Ref: ${tpl.referenceFileName.length > 18 ? tpl.referenceFileName.slice(0, 18) + "…" : tpl.referenceFileName}` : "Reference file", tone: "slate", title: "Reference file attached to template" });
                            if (tpl.writingLevel) chips.push({ k: "wl", icon: "fa fa-pen-nib", label: tpl.writingLevel, tone: "slate", title: "Writing level" });
                            if (tpl.language) chips.push({ k: "lang", icon: "fa fa-globe", label: tpl.language, tone: "slate", title: "Language" });
                            if (tpl.topicSpecificNotesCount > 0) chips.push({ k: "notes", icon: "fa fa-list-ul", label: `${tpl.topicSpecificNotesCount} instruction${tpl.topicSpecificNotesCount === 1 ? "" : "s"}`, tone: "neutral", title: "Topic-specific instructions" });
                            if (tpl.smartGreeting) chips.push({ k: "greet", icon: "fa fa-hand", label: "Smart Greeting", tone: "slate", title: "Smart greeting enabled" });
                            if (tpl.emailPreference === "decision_maker" && tpl.dmFallback === "strict") {
                              chips.push({ k: "dm", icon: "fa fa-user-tie", label: "Decision-maker only", tone: "slate", title: "Skips the contact when there is no decision-maker address" });
                            } else if (tpl.emailPreference === "decision_maker") {
                              chips.push({ k: "dm", icon: "fa fa-user-tie", label: "Decision maker, or info@", tone: "slate", title: "Uses a decision-maker address, or the company inbox when there is none" });
                            }
                          } else if (step.templateName) {
                            chips.push({ k: "tpl", icon: "fa fa-envelope", label: step.templateName, tone: "primary", title: "Email template (data unavailable)" });
                          } else {
                            chips.push({ k: "missing", icon: "fa fa-exclamation-circle", label: "No template", tone: "danger", title: "Template not selected" });
                          }
                          const overrideNames = readTemplateByListNames(step);
                          Object.entries(cleanTemplateByList(step.templateByList)).forEach(([listName, tplId]) => {
                            const mapped = topicOptions.find((t) => t.id === tplId);
                            chips.push({
                              k: `map-${listName}`,
                              icon: "fa fa-list",
                              label: `${listName} → ${mapped?.name || overrideNames[listName] || "template"}`,
                              tone: "slate",
                              title: `List "${listName}" uses this template instead of the default`,
                            });
                          });
                          chips.push(globalSafeGuardChip({
                            enabled: globalSafeGuardOn,
                            followUp: isFollowUpEmailType(tpl?.type),
                          }));
                          // Count pending rejections at this step for visibility
                          const rejectedAtStep = Object.values(rejectedDrafts).filter(
                            (d) => (typeof d.stepIndex === "number" ? d.stepIndex : 0) === idx
                          ).length;
                          if (rejectedAtStep > 0) {
                            chips.push({
                              k: "rejections",
                              icon: "fa fa-shield-halved",
                              label: `${rejectedAtStep} awaiting review`,
                              tone: "amber",
                              title: `${rejectedAtStep} SafeGuard-rejected email${rejectedAtStep === 1 ? "" : "s"} awaiting manual approval — expand the step to review.`,
                            });
                          }
                          chips.push({
                            k: "from",
                            icon: "fa fa-inbox",
                            label: senderPoolLabel(step, senderEmails, tpl?.type),
                            tone: "primary",
                            title: usesPreviousSender(step, tpl?.type)
                              ? "Same inbox as the first mail to this contact"
                              : "Inboxes used to write this mail",
                          });
                          chips.push({
                            k: "noem",
                            icon: "fa fa-user-minus",
                            label: "Leaves if no address",
                            tone: "slate",
                            title: noRecipientChipTitle(job.deliveryMode === "prepare", job.name),
                          });
                        } else if (step.type === "wait") {
                          chips.push({ k: "d", icon: "fa fa-hourglass-half", label: `Wait ${step.delayDays || 3} day${(step.delayDays || 3) === 1 ? "" : "s"} after send`, tone: "amber", title: "Pause starts when the previous mail is sent, not when it was written" });
                        } else if (step.type === "check_reply") {
                          chips.push({ k: "cr", icon: "fa fa-reply", label: "Auto-stops on reply", tone: "violet", title: "Contact exits sequence if they've replied (exact or domain match)" });
                          chips.push({ k: "crscan", icon: "fa fa-magnifying-glass", label: "Inbox scan + flag fast-path", tone: "slate", title: "Three layers of reply detection: contact status, lead_reply flag (inbox fetcher), and live inbox scan." });
                        } else if (step.type === "clean") {
                          const gl = (cleanupGuidelines || []).find((g) => g.id === step.guidelineId);
                          if (gl) {
                            chips.push({ k: "gl", icon: "fa fa-robot", label: gl.name || "Guideline", tone: "emerald", title: "Cleanup guideline" });
                            if (gl.hyperIntelligence) chips.push({ k: "hyp", icon: "fa fa-bolt", label: "Hyper", tone: "amber", title: "Hyper Intelligence" });
                            if (gl.businessContext) chips.push({ k: "ctx", icon: "fa fa-building", label: "Business context", tone: "slate", title: "Uses business context" });
                            if (gl.isDefault) chips.push({ k: "def", icon: "fa fa-star", label: "Default", tone: "slate", title: "Default guideline" });
                          } else {
                            chips.push({ k: "gl", icon: "fa fa-robot", label: step.guidelineName || "Default guideline", tone: "emerald", title: "Cleanup guideline" });
                          }
                          const guidelineNames = readListOverrideNames(step, LIST_MAP_GUIDELINE);
                          Object.entries(cleanListOverride(step.guidelineByList)).forEach(([listName, glId]) => {
                            const mapped = (cleanupGuidelines || []).find((g) => g.id === glId);
                            chips.push({
                              k: `glmap-${listName}`,
                              icon: "fa fa-list",
                              label: `${listName} → ${mapped?.name || guidelineNames[listName] || "guideline"}`,
                              tone: "slate",
                              title: `List "${listName}" uses this guideline instead of the default`,
                            });
                          });
                          const onFail = step.onFail || "remove";
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop from agent", removeTitle: "Leaves this agent if not qualified" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "google_review_audit") {
                          const onFail = step.onFail || "skip";
                          chips.push({ k: "research", icon: "fa fa-star-half-stroke", label: "Google reviews", tone: "primary", title: "Research step" });
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop from agent", removeTitle: "Leaves this agent if the business isn't on Google Maps" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "data_connector") {
                          const onFail = step.onFail || "skip";
                          chips.push({ k: "dc", icon: "fa fa-plug", label: connectorNameFor(step) || "Data source", tone: "primary", title: "Custom researcher" });
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop if nothing found", removeTitle: "Leaves this agent if the platform returns nothing" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "web_research" || step.type === "instagram_research") {
                          const onFail = step.onFail || "remove";
                          chips.push({ k: "research", icon: step.type === "web_research" ? "fa fa-globe" : "fab fa-instagram", label: step.type === "web_research" ? "Website enrichment" : "Instagram profile", tone: step.type === "web_research" ? "primary" : "pink", title: "Research step" });
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop from agent", removeTitle: "Leaves this agent if data is unavailable" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "potential_analyzer") {
                          const onFail = step.onFail || "remove";
                          chips.push({ k: "pa", icon: "fa fa-gauge-high", label: `Potential ≥ ${typeof step.minScore === "number" ? step.minScore : 4}/10`, tone: "primary", title: "Keeps contacts at or above this ICP-fit score" });
                          const icp = (potentialProfiles || []).find((p) => p.id === step.icpProfileId);
                          if (icp) {
                            chips.push({ k: "icp", icon: "fa fa-bullseye", label: icp.name || "ICP profile", tone: "emerald", title: "ICP profile used for scoring" });
                            if (icp.isDefault) chips.push({ k: "icpdef", icon: "fa fa-star", label: "Default", tone: "slate", title: "Default ICP profile" });
                          } else {
                            chips.push({ k: "icp", icon: "fa fa-bullseye", label: step.icpProfileName || "Default ICP profile", tone: "emerald", title: "ICP profile used for scoring" });
                          }
                          const icpNames = readListOverrideNames(step, LIST_MAP_ICP);
                          Object.entries(cleanListOverride(step.icpByList)).forEach(([listName, profileId]) => {
                            const mapped = (potentialProfiles || []).find((p) => p.id === profileId);
                            chips.push({
                              k: `icpmap-${listName}`,
                              icon: "fa fa-list",
                              label: `${listName} → ${mapped?.name || icpNames[listName] || "ICP"}`,
                              tone: "slate",
                              title: `List "${listName}" uses this ICP profile instead of the default`,
                            });
                          });
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop below threshold", removeTitle: "Leaves this agent if the score is too low" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "dm_email_finder") {
                          const onFail = step.onFail || "remove";
                          chips.push({ k: "dmf", icon: "fa fa-search", label: step.deepSearch ? "DM email (deep)" : "DM email finder", tone: "primary", title: "Finds decision-maker email addresses" });
                          chips.push({ k: "nodm", icon: "fa fa-forward", label: "Skip if no DMs", tone: "slate", title: "Contacts with no decision makers skip this step automatically — no credits, they continue" });
                          if (step.requireVerified) chips.push({ k: "rv", icon: "fa fa-circle-check", label: "Verified only", tone: "emerald", title: "Only verified emails count as found" });
                          pushOnFailChip(chips, onFail, { removeLabel: "Drop if none found", removeTitle: "Leaves this agent if decision makers exist but no email is found" });
                          if (onFail === "remove") pushFailActionChip(chips, step.failAction);
                        } else if (step.type === "add_tag") {
                          const tag = (allTags || []).find((t) => t.id === step.tagId);
                          chips.push({ k: "tag", icon: tag?.icon || "fa fa-tag", label: tag?.tag || tag?.name || step.tagName || "Tag", tone: "amber", title: "Tag to add", color: tag?.color });
                        } else if (step.type === "add_to_flow") {
                          const flow = (flowOptions || []).find((f) => f.id === step.flowId);
                          chips.push({ k: "flow", icon: "fa fa-project-diagram", label: flow?.name || step.flowName || "Flow", tone: "indigo", title: "Flow to assign" });
                          if (flow && Array.isArray(flow.steps)) chips.push({ k: "flows", icon: "fa fa-list-ol", label: `${flow.steps.length} step${flow.steps.length === 1 ? "" : "s"}`, tone: "slate", title: "Flow steps" });
                        } else if (step.type === "end_flow") {
                          chips.push({ k: "end", icon: "fa fa-flag-checkered", label: completedTagLabel(job.name), tone: "emerald", title: `Tags the contact “${completedTagLabel(job.name)}” when this agent finishes` });
                        }

                        return (
                          <React.Fragment key={step.id || idx}>
                            <div
                              className={`oj-node oj-node--live oj-node--${step.type}${isExpanded ? " expanded" : ""}${hasContacts ? " clickable" : ""}`}
                              onClick={() => hasContacts && setExpandedStepIdx(isExpanded ? null : idx)}
                            >
                              <div className="oj-node-head">
                                <div className="oj-node-step-num">{idx + 1}</div>
                                <div className={`oj-node-icon oj-node-icon--${step.type}`}><i className={`${def?.iconPrefix || "fa"} ${def?.icon || "fa-circle"}`}></i></div>
                                <div className="oj-node-info">
                                  <div className="oj-node-title">{stepLabel(step.type, detailIsPrepare)}</div>
                                  <div className="oj-node-desc">
                                    {step.type === "send_email" && (step.templateName || "No template selected")}
                                    {step.type === "wait" && (
                                      <>
                                        Pause {step.delayDays || 3} day{(step.delayDays || 3) === 1 ? "" : "s"} after the mail is sent
                                      </>
                                    )}
                                    {step.type === "check_reply" && "Removes contacts who already replied"}
                                    {step.type === "clean" && (step.guidelineName || "AI qualification")}
                                    {step.type === "web_research" && (() => {
                                      const resolved = resolveWebResearchModules(step, { activeModules: spyActiveModules });
                                      const dmOn = resolved.modules.includes("decisionMakers");
                                      if (resolved.source === "step") {
                                        return `Custom modes · ${dmOn ? "Decision Makers on" : "Decision Makers off"}`;
                                      }
                                      return dmOn
                                        ? "World Settings · Decision Makers on"
                                        : "World Settings · Decision Makers off";
                                    })()}
                                    {step.type === "instagram_research" && "Fetch Instagram profile data"}
                                    {step.type === "google_review_audit" && "Audit Google reviews — feeds the email"}
                                    {step.type === "data_connector" && `${connectorNameFor(step) || "Custom researcher"} — feeds the email`}
                                    {step.type === "potential_analyzer" && `Keep contacts scoring ≥ ${typeof step.minScore === "number" ? step.minScore : 4}/10`}
                                    {step.type === "dm_email_finder" && `Find decision-maker email${step.deepSearch ? " (deep search)" : ""}`}
                                    {step.type === "add_tag" && (step.tagName || "Tag contact")}
                                    {step.type === "add_to_flow" && (step.flowName || "Assign to automation flow")}
                                    {step.type === "end_flow" && `Tags “${completedTagLabel(job.name)}”`}
                                  </div>
                                </div>
                                {hasContacts && (
                                  <div className="oj-node-expand">
                                    <i className={`fa fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                                  </div>
                                )}
                              </div>
                              <div className="oj-node-badges">
                                {processing > 0 && <span className="oj-badge processing"><i className="fa fa-spinner fa-spin"></i> {processing} processing</span>}
                                {ready > 0 && <span className="oj-badge ready" title="Due now — these contacts are picked up by the next scheduler run. Nothing has been done for them at this step yet."><i className="fa fa-bolt"></i> {ready} due</span>}
                                {awaitingSend > 0 && <span className="oj-badge awaiting" title="Email written and waiting for you in the Prepared Mails strip above your Targets."><i className="fa fa-envelope-open-text"></i> {awaitingSend} written</span>}
                                {waiting > 0 && <span className="oj-badge waiting"><i className="fa fa-clock"></i> {waiting} in queue</span>}
                                {/* For check_reply show the job-wide reply count (that's the semantic intent).
                                    For other steps, show only the replies attributed to that specific step. */}
                                {isCheckReply
                                  ? globalRepliedCount > 0 && (
                                      <span className="oj-badge replied" title={`Total replies detected for this job (replies may have been caught at earlier steps). ${confirmedRepliedCount} already auto-stopped; ${pendingAutoStopCount} queued for next scheduler run. Expand to see who.`}>
                                        <i className="fa fa-reply"></i> {globalRepliedCount} replied
                                      </span>
                                    )
                                  : repliedAtStep > 0 && (
                                      <span className="oj-badge replied" title="Replies detected while contact was at this step">
                                        <i className="fa fa-reply"></i> {repliedAtStep} replied
                                      </span>
                                    )}
                                {pendingAutoStopCount > 0 && (
                                  <span
                                    className="oj-badge pending-stop"
                                    title={`${pendingAutoStopCount} contact${pendingAutoStopCount === 1 ? " has" : "s have"} already replied in your inbox but the outreach queue entry is still in a long Wait. ${isCheckReply ? "It" : "They"} will be auto-stopped on the next scheduler pass — no further emails will be sent.`}
                                  >
                                    <i className="fa fa-hourglass-half"></i> {pendingAutoStopCount} auto-stop pending
                                  </span>
                                )}
                                {isCheckReply && savedSendsCount > 0 && (
                                  <span
                                    className="oj-badge saved"
                                    title={`${savedSendsCount} follow-up email${savedSendsCount === 1 ? "" : "s"} across ${globalRepliedCount} replied contact${globalRepliedCount === 1 ? "" : "s"} were NOT sent because the contact replied before reaching those steps.`}
                                  >
                                    <i className="fa fa-hand"></i> {savedSendsCount} sends saved
                                  </span>
                                )}
                                {skippedAtStep > 0 && <span className="oj-badge skipped" title="Contacts skipped at this step (SafeGuard, filters, opt-out, etc.)"><i className="fa fa-forward"></i> {skippedAtStep} skipped</span>}
                                {failedAtStep > 0 && <span className="oj-badge failed" title="Contacts that failed at this step"><i className="fa fa-exclamation-triangle"></i> {failedAtStep} failed</span>}
                                {passedAtStep > 0 && <span className="oj-badge done" title="Contacts that already ran this step and moved on"><i className="fa fa-check"></i> {passedAtStep} passed</span>}
                                {total === 0 && dropped === 0 && passedAtStep === 0 && globalRepliedCount === 0 && <span className="oj-badge empty">No contacts yet</span>}
                              </div>
                              {chips.length > 0 && (
                                <div className={`oj-node-chips${stepSettingsOpen[idx] ? " is-open" : ""}`}>
                                  <button
                                    type="button"
                                    className="oj-node-chips-toggle"
                                    aria-expanded={!!stepSettingsOpen[idx]}
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      setStepSettingsOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
                                    }}
                                  >
                                    <span className="oj-node-chips-label">Settings</span>
                                    <i className={`fa fa-chevron-${stepSettingsOpen[idx] ? "up" : "down"}`} aria-hidden="true"></i>
                                  </button>
                                  {stepSettingsOpen[idx] && (
                                    <div className="oj-node-chips-list">
                                      {chips.map((c) => (
                                        <span key={c.k} className={`oj-chip oj-chip--${c.tone}`} title={c.title} style={c.color ? { "--chip-accent": c.color } : undefined}>
                                          <i className={c.icon}></i>
                                          <span>{c.label}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              {isExpanded && (stepContacts.length > 0 || archiveHasMore) && (
                                <div className="oj-step-contacts">
                                  {visibleStepContacts.map(([qKey, e]) => {
                                    const cId = e.contactId || qKey;
                                    const hereNow = isHereAtStep(e, idx);
                                    const passedThrough = isPassedThroughStep(e, idx);
                                    const hist = stepHistoryAt(e, idx);
                                    const isActive = hereNow && (e.status === "pending" || e.status === "processing");
                                    const isSafeGuardRejected = hereNow && typeof e.failReason === "string" && e.failReason.startsWith("safeguard_rejected");
                                    const isReplied = hereNow && e.status === "replied";
                                    const isPendingAutoStop = hereNow && !isReplied && isPendingButReplied(e);
                                    // Treat pending-auto-stop entries like replied ones for the
                                    // "detected at different step" / "saved sends" UI semantics.
                                    const isEffectivelyReplied = isReplied || isPendingAutoStop;
                                    // If we're rendering under a check_reply step but the entry was
                                    // actually detected at a different step, show the detection step.
                                    const detectedAtDifferentStep = isCheckReply && isEffectivelyReplied && (e.currentStepIndex || 0) !== idx;
                                    const detectedStepLabel = detectedAtDifferentStep
                                      ? (() => {
                                          const detIdx = e.currentStepIndex || 0;
                                          const detStep = (job.steps || [])[detIdx];
                                          if (!detStep) return `step ${detIdx + 1}`;
                                          return `${detIdx + 1}. ${stepLabel(detStep.type, detailIsPrepare)}`;
                                        })()
                                      : null;
                                    const draft = e.draftId && rejectedDrafts[e.draftId]
                                      ? rejectedDrafts[e.draftId]
                                      : (isSafeGuardRejected ? rejectedDraftByKey[`${e.jobId}|${cId}|${idx}`] : null);
                                    const rating = typeof e.safeguardRating === "number"
                                      ? e.safeguardRating
                                      : (draft?.safeGuardRating ?? null);
                                    const minRating = typeof e.minimumRating === "number"
                                      ? e.minimumRating
                                      : (draft?.minimumRating ?? null);
                                    const ratingTitle = minRating != null
                                      ? `SafeGuard Rating ${rating}/10 — required minimum ${minRating}/10`
                                      : `SafeGuard Rating ${rating}/10`;
                                    const isExpandedDraft = draft && expandedDraftId === draft.id;
                                    const isApproving = draft && approvingDraftId === draft.id;
                                    const isDropout = hereNow && (e.status === "failed" || e.status === "skipped");
                                    const canInspectFail = isDropout && !isSafeGuardRejected;
                                    const isExpandedFail = canInspectFail && expandedFailKey === qKey;
                                    const failInsight = e.failAnalysis || failInsightByContact[cId] || null;
                                    const threshold = parsePotentialThreshold(e.failReason);
                                    const failScore = failInsight?.score ?? e.gatedScore ?? threshold?.score;
                                    const failMin = failInsight?.minScore ?? e.minScore ?? threshold?.minScore;
                                    return (
                                    <React.Fragment key={qKey}>
                                      <div
                                        className={`oj-step-contact ${passedThrough ? "passed" : e.status}${isSafeGuardRejected ? " safeguard-rejected" : ""}${isPendingAutoStop ? " pending-auto-stop" : ""}${isExpandedDraft || isExpandedFail ? " has-preview" : ""}`}
                                        onClick={(ev) => {
                                          if (draft && isSafeGuardRejected) {
                                            ev.stopPropagation();
                                            setExpandedDraftId(isExpandedDraft ? null : draft.id);
                                          } else if (canInspectFail) {
                                            ev.stopPropagation();
                                            toggleFailInfo(qKey, cId, e);
                                          }
                                        }}
                                        style={(draft && isSafeGuardRejected) || canInspectFail ? { cursor: "pointer" } : undefined}
                                      >
                                        <span className="oj-step-contact-name">{queueContactNames[cId] || cId.slice(0, 14) + "…"}</span>
                                        {isSafeGuardRejected && rating != null ? (
                                          <span className="oj-step-contact-sg-rating" title={ratingTitle}>
                                            <i className="fa fa-shield-alt"></i> {rating}/10
                                          </span>
                                        ) : isReplied ? (
                                          <span className="oj-step-contact-status replied" title="Contact replied and was removed from the sequence">
                                            <i className="fa fa-reply"></i> replied
                                          </span>
                                        ) : isPendingAutoStop ? (
                                          <span className="oj-step-contact-status pending-auto-stop" title="Reply detected in your inbox — outreach will auto-stop on the next scheduler pass. No further emails will be sent.">
                                            <i className="fa fa-hourglass-half"></i> auto-stop pending
                                          </span>
                                        ) : passedThrough ? (
                                          <span className="oj-step-contact-status passed" title="Already ran this step and moved on">
                                            <i className="fa fa-check"></i> passed
                                          </span>
                                        ) : (
                                          <span className={`oj-step-contact-status ${e.status}`}>{e.status}</span>
                                        )}
                                        {detectedAtDifferentStep && (
                                          <span className="oj-step-contact-detected-at" title="The reply was detected while the contact was waiting at / being processed for this step">
                                            <i className="fa fa-location-dot"></i> {detectedStepLabel}
                                          </span>
                                        )}
                                        {isEffectivelyReplied && (() => {
                                          const saved = savedSendsForEntry(e);
                                          if (saved <= 0) return null;
                                          return (
                                            <span
                                              className="oj-step-contact-saved"
                                              title={`${saved} follow-up email${saved === 1 ? "" : "s"} will not be sent to this contact because they replied.`}
                                            >
                                              <i className="fa fa-hand"></i> {saved} saved
                                            </span>
                                          );
                                        })()}
                                        {hereNow && e.executeAt && e.status === "pending" && e.executeAt > Date.now() && (
                                          <span className="oj-step-contact-time"><i className="fa fa-clock"></i> {new Date(e.executeAt).toLocaleString([], { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                                        )}
                                        {hereNow && e.completedAt && (e.status === "failed" || e.status === "skipped" || e.status === "replied" || e.status === "completed") && (
                                          <span className="oj-step-contact-time"><i className="fa fa-clock"></i> {new Date(e.completedAt).toLocaleString([], { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                                        )}
                                        {passedThrough && hist?.completedAt && (
                                          <span className="oj-step-contact-time"><i className="fa fa-clock"></i> {new Date(hist.completedAt).toLocaleString([], { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                                        )}
                                        {isSafeGuardRejected ? (
                                          <span className="oj-step-contact-reason sg-skipped">Skipped</span>
                                        ) : isReplied ? (
                                          e.failReason && (
                                            <span className="oj-step-contact-reason replied" title="How the reply was detected">
                                              {e.failReason === 'fetcher_flag' && 'via inbox fetcher'}
                                              {e.failReason === 'inbox_reply' && 'exact email match'}
                                              {typeof e.failReason === 'string' && e.failReason.startsWith('domain_reply:') && `domain match · ${e.failReason.slice('domain_reply:'.length).trim()}`}
                                              {!['fetcher_flag', 'inbox_reply'].includes(e.failReason) && !(typeof e.failReason === 'string' && e.failReason.startsWith('domain_reply:')) && e.failReason.replace(/_/g, ' ')}
                                            </span>
                                          )
                                        ) : passedThrough ? (
                                          (() => {
                                            const outcome = describeStepHistory(step.type, hist);
                                            const emails = stepHistoryEmails(hist);
                                            if (!outcome) return null;
                                            return (
                                              <span className="oj-step-contact-reason" title={emails.length ? emails.join(" · ") : outcome}>
                                                {outcome}
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          e.failReason && (
                                            <span className="oj-step-contact-reason" title={humanizeFailReason(e.failReason)}>
                                              {e.failReason.replace(/_/g, " ")}
                                            </span>
                                          )
                                        )}
                                        {canInspectFail && (
                                          <button
                                            className="oj-step-contact-view"
                                            title={isExpandedFail ? "Hide what happened" : "What happened"}
                                            onClick={(ev) => {
                                              ev.stopPropagation();
                                              toggleFailInfo(qKey, cId, e);
                                            }}
                                          >
                                            <i className={`fa ${isExpandedFail ? "fa-chevron-up" : "fa-circle-info"}`}></i>
                                          </button>
                                        )}
                                        {draft && isSafeGuardRejected && (
                                          <button
                                            className="oj-step-contact-view"
                                            title={isExpandedDraft ? "Hide email preview" : "View generated email"}
                                            onClick={(ev) => {
                                              ev.stopPropagation();
                                              setExpandedDraftId(isExpandedDraft ? null : draft.id);
                                            }}
                                          >
                                            <i className={`fa fa-chevron-${isExpandedDraft ? "up" : "down"}`}></i>
                                          </button>
                                        )}
                                        {isActive && (
                                          <button
                                            className="oj-step-contact-remove"
                                            title="Remove from job"
                                            onClick={(ev) => { ev.stopPropagation(); removeContactFromQueue(qKey, cId, e.jobId); }}
                                          >
                                            <i className="fa fa-times"></i>
                                          </button>
                                        )}
                                      </div>
                                      {isExpandedDraft && draft && (
                                        <div className="oj-sg-preview" onClick={(ev) => ev.stopPropagation()}>
                                          <div className="oj-sg-preview-head">
                                            <div className="oj-sg-preview-title">
                                              <i className="fa fa-shield-halved"></i>
                                              SafeGuard Review
                                              {rating != null && (
                                                <span className="oj-sg-preview-rating" title={ratingTitle}>
                                                  Rating {rating}/10
                                                  {minRating != null && <span className="oj-sg-preview-rating-min"> · min {minRating}</span>}
                                                </span>
                                              )}
                                            </div>
                                            <button className="oj-sg-preview-close" onClick={() => setExpandedDraftId(null)} title="Close preview"><i className="fa fa-times"></i></button>
                                          </div>
                                          {Array.isArray(draft.safeGuardIssues) && draft.safeGuardIssues.length > 0 && (
                                            <div className="oj-sg-issues">
                                              <div className="oj-sg-issues-label"><i className="fa fa-triangle-exclamation"></i> Issues</div>
                                              <ul>{draft.safeGuardIssues.slice(0, 8).map((iss, i) => <li key={i}>{String(iss)}</li>)}</ul>
                                            </div>
                                          )}
                                          {Array.isArray(draft.safeGuardImprovements) && draft.safeGuardImprovements.length > 0 && (
                                            <div className="oj-sg-improvements">
                                              <div className="oj-sg-issues-label"><i className="fa fa-lightbulb"></i> Suggested improvements</div>
                                              <ul>{draft.safeGuardImprovements.slice(0, 8).map((imp, i) => <li key={i}>{String(imp)}</li>)}</ul>
                                            </div>
                                          )}
                                          <div className="oj-sg-email">
                                            <div className="oj-sg-email-meta">
                                              <div><span className="oj-sg-email-meta-label">From</span><span>{draft.from || "—"}</span></div>
                                              <div><span className="oj-sg-email-meta-label">To</span><span>{draft.to || "—"}</span></div>
                                              <div><span className="oj-sg-email-meta-label">Subject</span><span className="oj-sg-email-subject">{draft.subject || "—"}</span></div>
                                            </div>
                                            <div className="oj-sg-email-body" dangerouslySetInnerHTML={{ __html: draft.content || "<p style=\"color:#94a3b8;\">Kein Body gespeichert.</p>" }} />
                                          </div>
                                          {approveError && approvingDraftId === null && (
                                            <div className="oj-sg-error"><i className="fa fa-circle-exclamation"></i> {approveError}</div>
                                          )}
                                          <div className="oj-sg-preview-actions">
                                            <button
                                              className="oj-sg-approve-btn"
                                              disabled={isApproving}
                                              onClick={() => approveRejectedDraft(draft.id)}
                                              title="Re-queue this contact with SafeGuard bypass so the email actually sends on the next scheduler run"
                                            >
                                              <i className={`fa ${isApproving ? "fa-spinner fa-spin" : "fa-paper-plane"}`}></i>
                                              {isApproving ? "Approving..." : "Approve & Send"}
                                            </button>
                                            <span className="oj-sg-approve-hint">Bypasses SafeGuard for this one send only.</span>
                                          </div>
                                        </div>
                                      )}
                                      {isExpandedFail && (
                                        <div className="oj-fail-preview" onClick={(ev) => ev.stopPropagation()}>
                                          <div className="oj-fail-preview-head">
                                            <div className="oj-fail-preview-title">
                                              <i className="fa fa-circle-info"></i>
                                              What happened
                                              {failScore != null && (
                                                <span className="oj-fail-preview-score">
                                                  {failScore}/10
                                                  {failMin != null && <span> · min {failMin}</span>}
                                                </span>
                                              )}
                                            </div>
                                            <button type="button" className="oj-sg-preview-close" onClick={() => setExpandedFailKey(null)} title="Close">
                                              <i className="fa fa-times"></i>
                                            </button>
                                          </div>
                                          <p className="oj-fail-preview-lead">{humanizeFailReason(e.failReason)}</p>
                                          {failInsight?.fitLabel && (
                                            <div className="oj-fail-preview-fit">{failInsight.fitLabel}</div>
                                          )}
                                          {failInsight?.summary && (
                                            <p className="oj-fail-preview-summary">{failInsight.summary}</p>
                                          )}
                                          {Array.isArray(failInsight?.bullets) && failInsight.bullets.length > 0 && (
                                            <ul className="oj-fail-preview-bullets">
                                              {failInsight.bullets.map((b, i) => <li key={i}>{b}</li>)}
                                            </ul>
                                          )}
                                          <div className="oj-fail-preview-meta">
                                            {e.failAction === "tag" && <span>Kept in the database</span>}
                                            {e.failAction === "delete" && <span>Deleted from the database</span>}
                                            {e.failAction === "blacklist" && <span>Deleted and blacklisted</span>}
                                            {e.completedAt && (
                                              <span>{new Date(e.completedAt).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                                            )}
                                            {failInsight?.dataConfidence && <span>Confidence {failInsight.dataConfidence}</span>}
                                          </div>
                                        </div>
                                      )}
                                    </React.Fragment>
                                  );})}
                                  {stepCanLoadMore && (
                                    <button
                                      type="button"
                                      className="oj-step-contacts-more"
                                      disabled={archiveLoading}
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        setStepListVisible((prev) => ({
                                          ...prev,
                                          [idx]: nextPageSize(prev[idx]),
                                        }));
                                        if (visibleStepContacts.length >= stepContacts.length && archiveHasMore) {
                                          setArchiveWindow((w) => w + STEP_CONTACT_PAGE);
                                        }
                                      }}
                                    >
                                      {archiveLoading ? "Loading…" : "Mehr laden"}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            {idx < stepFunnel.length - 1 && (
                              <div className="oj-connector">
                                <div className="oj-connector-line"></div>
                                {total > 0 && <span className="oj-connector-count">{total}</span>}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <div className="oj-pipeline-terminal">
                        {doneCount > 0 && <span className="oj-badge done"><i className="fa fa-check-circle"></i> {doneCount} completed</span>}
                        {repliedCount > 0 && <span className="oj-badge replied"><i className="fa fa-reply"></i> {repliedCount} replied</span>}
                        {pendingAutoStopTotal > 0 && (
                          <span
                            className="oj-badge pending-stop"
                            title={`${pendingAutoStopTotal} contact${pendingAutoStopTotal === 1 ? " has" : "s have"} already replied in your inbox — their queue entries will be auto-stopped on the next scheduler pass.`}
                          >
                            <i className="fa fa-hourglass-half"></i> {pendingAutoStopTotal} auto-stop pending
                          </span>
                        )}
                        {savedSendsCount > 0 && (
                          <span
                            className="oj-badge saved"
                            title={`${savedSendsCount} follow-up email${savedSendsCount === 1 ? "" : "s"} skipped because contacts replied before reaching those steps.`}
                          >
                            <i className="fa fa-hand"></i> {savedSendsCount} sends saved
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Queue */}
                  {queueEntries.length > 0 && (
                    <div className="oj-canvas-section">
                      <div className="oj-canvas-section-title">
                        <i className="fa fa-list"></i> Queue
                        <span className="oj-canvas-section-count">{queueEntries.length}</span>
                      </div>
                      <div className="oj-queue">
                        {queueEntries.slice(0, pageSizeOrDefault(queueListVisible)).map(([qKey, entry]) => {
                          const cId = entry.contactId || qKey;
                          const stepIdx = entry.currentStepIndex || 0;
                          const stepDef = job.steps?.[stepIdx];
                          const stepType = STEP_TYPES.find((t) => t.id === stepDef?.type);
                          const contactName = queueContactNames[cId] || cId.slice(0, 12) + "...";
                          const totalSteps = job.steps?.length || 0;
                          const completedSteps = Object.keys(entry.stepHistory || {}).length;
                          const isTerminal = TERMINAL_SET.has(entry.status);
                          // Prepare mode: email written, parked until the user sends
                          // it. Its executeAt is only the expiry date, so showing a
                          // countdown would read like a scheduled send.
                          const isAwaitingSend = entry.status === "awaiting_send";
                          const now = Date.now();

                          const getRelativeTime = (ts) => {
                            const diff = ts - now;
                            const absDiff = Math.abs(diff);
                            if (absDiff < 60000) return diff > 0 ? "in < 1 min" : "just now";
                            if (absDiff < 3600000) { const m = Math.round(absDiff / 60000); return diff > 0 ? `in ${m} min` : `${m} min ago`; }
                            if (absDiff < 86400000) { const h = Math.round(absDiff / 3600000); return diff > 0 ? `in ${h}h` : `${h}h ago`; }
                            const d = Math.round(absDiff / 86400000);
                            return diff > 0 ? `in ${d}d` : `${d}d ago`;
                          };

                          return (
                            <div key={qKey} className={`oj-queue-row ${entry.status}`}>
                              <div className="oj-queue-row-main">
                                <span className="oj-queue-name" title={cId}>{contactName}</span>
                                <span className={`oj-queue-status ${entry.status}`}>
                                  {isAwaitingSend ? "ready to send" : entry.status}
                                </span>
                              </div>
                              <div className="oj-queue-row-details">
                                <span className="oj-queue-step" title={stepDef ? stepLabel(stepDef.type, detailIsPrepare) : `Step ${stepIdx + 1}`}>
                                  {stepType && <i className={`${stepType.iconPrefix || "fa"} ${stepType.icon}`}></i>}
                                  {stepDef ? stepLabel(stepDef.type, detailIsPrepare) : `Step ${stepIdx + 1}`}
                                </span>
                                {totalSteps > 1 && (
                                  <span className="oj-queue-progress">
                                    <span className="oj-queue-progress-bar">
                                      <span className="oj-queue-progress-fill" style={{ width: `${Math.round(((isTerminal ? totalSteps : stepIdx) / totalSteps) * 100)}%` }}></span>
                                    </span>
                                    <span className="oj-queue-progress-label">{isTerminal && entry.status === "completed" ? totalSteps : stepIdx}/{totalSteps}</span>
                                  </span>
                                )}
                                {entry.failReason && (
                                  <span className="oj-queue-reason" title={entry.failReason.replace(/_/g, " ")}>
                                    <i className="fa fa-exclamation-circle"></i> {entry.failReason.replace(/_/g, " ")}
                                  </span>
                                )}
                                {!entry.failReason && entry.lastStepResult && (
                                  <span className={`oj-queue-result ${entry.lastStepResult === "sent" ? "success" : ""}`}>
                                    {entry.lastStepResult === "sent" && <i className="fa fa-check"></i>}
                                    {entry.lastStepResult.replace(/_/g, " ")}
                                  </span>
                                )}
                                {isAwaitingSend && (
                                  <span className="oj-queue-time due" title="Send it from the Prepared Mails strip above your Targets.">
                                    <i className="fa fa-envelope-open-text"></i>
                                    Waiting for you
                                  </span>
                                )}
                                {!isTerminal && !isAwaitingSend && entry.executeAt && (
                                  <span className={`oj-queue-time ${entry.executeAt <= now ? "due" : ""}`}>
                                    <i className={`fa ${entry.executeAt <= now ? "fa-bolt" : "fa-clock"}`}></i>
                                    {entry.executeAt <= now
                                      ? "Due now"
                                      : getRelativeTime(entry.executeAt)}
                                    <span className="oj-queue-time-abs">
                                      {new Date(entry.executeAt).toLocaleString([], { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                  </span>
                                )}
                                {isTerminal && entry.completedAt && (
                                  <span className="oj-queue-completed-at">
                                    <i className="fa fa-calendar-check"></i>
                                    {getRelativeTime(entry.completedAt)}
                                  </span>
                                )}
                              </div>
                              {(entry.status === "failed" || entry.status === "skipped") && (entry.currentStepIndex || 0) + 1 < totalSteps && (
                                <button
                                  className="oj-queue-advance"
                                  title="Skip this step and continue to next"
                                  onClick={() => advanceContactToNextStep(qKey, entry)}
                                >
                                  <i className="fa fa-forward"></i> Next Step
                                </button>
                              )}
                              {!isTerminal && (
                                <button
                                  className="oj-queue-remove"
                                  title="Remove from job"
                                  onClick={() => removeContactFromQueue(qKey, cId, entry.jobId)}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              )}
                            </div>
                          );
                        })}
                        {canLoadMoreContacts(queueEntries.length, queueListVisible, archiveHasMore) && (
                          <button
                            type="button"
                            className="oj-queue-more"
                            disabled={archiveLoading}
                            onClick={() => {
                              setQueueListVisible((n) => nextPageSize(n));
                              if (pageSizeOrDefault(queueListVisible) >= queueEntries.length && archiveHasMore) {
                                setArchiveWindow((w) => w + STEP_CONTACT_PAGE);
                              }
                            }}
                          >
                            {archiveLoading ? "Loading…" : "Mehr laden"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ─── TEMPLATES DRAWER (builder only) ───
                Right-hand pane with starter flow presets. Collapsed by default
                to a slim rail — expanding shows the 5 templates with a vertical
                preview each, using the same sequence preview as the job cards
                on the overview. Clicking Apply seeds the step editor
                with fresh ids (guarded by a confirm when there is existing
                work), so users can iterate on top of a template safely. */}
            {isBuilder && !structureLocked && (
              <aside className={`oj-templates${templatesOpen ? " open" : ""}`}>
                <button
                  type="button"
                  className="oj-templates-toggle"
                  onClick={() => setTemplatesOpen((v) => !v)}
                  title={templatesOpen ? "Collapse templates" : "Show popular flows"}
                >
                  <i className={`fa ${templatesOpen ? "fa-chevron-right" : "fa-shapes"}`}></i>
                  {!templatesOpen && <span className="oj-templates-toggle-label">Popular flows</span>}
                </button>
                {templatesOpen && (
                  <div className="oj-templates-body">
                    <div className="oj-templates-head">
                      <span className="oj-sb-kicker">Templates</span>
                      <h5>Popular flows</h5>
                      <p>Pick a starter, then tune it to your ICP. Applying replaces the current sequence.</p>
                    </div>
                    <div className="oj-templates-list">
                      {FLOW_TEMPLATES.map((tpl) => (
                          <div key={tpl.id} className="oj-template-card">
                            <div className="oj-template-card-info">
                              <strong>{tpl.name}</strong>
                              <span>{tpl.tagline}</span>
                              <span className="oj-template-card-count">
                                {tpl.steps.length} step{tpl.steps.length === 1 ? "" : "s"}
                              </span>
                            </div>
                            {renderSequencePreview(tpl.steps.map((s, i) => ({ ...s, id: `${tpl.id}-${i}` })), {
                              isPrepare: deliveryMode === "prepare",
                              includeStart: true,
                            })}
                            <button
                              type="button"
                              className="oj-template-apply"
                              onClick={() => applyTemplate(tpl)}
                            >
                              <i className="fa fa-wand-magic-sparkles"></i>
                              {steps.length > 0 ? "Replace with this" : "Use this flow"}
                            </button>
                          </div>
                      ))}
                    </div>
                    <div className="oj-templates-footnote">
                      <i className="fa fa-info-circle"></i>
                      Templates leave templates/tags empty — pick your own after applying.
                    </div>
                  </div>
                )}
              </aside>
            )}
          </div>
        </div>
        {stopDialog && (
          <div className="oj-stop-overlay" onClick={() => !stopDialog.confirming && setStopDialog(null)}>
            <div className="oj-stop-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="oj-stop-title">
              <div className="oj-stop-icon">
                <i className="fa fa-stop" aria-hidden="true" />
              </div>
              <h3 id="oj-stop-title">Stop this agent?</h3>
              {stopDialog.loading ? (
                <p className="oj-stop-copy">Checking who is still in this sequence…</p>
              ) : (
                <>
                  <p className="oj-stop-copy">
                    Everyone who has not finished leaves the queue.
                    They cannot join this agent again. If you only want to change
                    templates, Pause instead — Stop cannot be undone.
                  </p>
                  {stopDialog.total > 0 ? (
                    <ul className="oj-stop-list">
                      {stopDialog.unsent > 0 && (
                        <li>
                          <strong>{stopDialog.unsent}</strong> never emailed by this agent
                          — status restored (e.g. Not Contacted)
                        </li>
                      )}
                      {stopDialog.sent > 0 && (
                        <li>
                          <strong>{stopDialog.sent}</strong> already emailed
                          — they keep Introduced / Follow-up
                        </li>
                      )}
                      {stopDialog.prepared > 0 && (
                        <li>
                          <strong>{stopDialog.prepared}</strong> prepared {stopDialog.prepared === 1 ? "draft" : "drafts"} from this agent
                          — deleted and removed from Prepared
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p className="oj-stop-copy">No contacts are still in this agent. Stop marks it completed.</p>
                  )}
                </>
              )}
              {stopDialog.error && <p className="oj-stop-error">{stopDialog.error}</p>}
              <div className="oj-stop-actions">
                <button
                  type="button"
                  className="oj-topbar-btn secondary"
                  disabled={stopDialog.confirming}
                  onClick={() => setStopDialog(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="oj-topbar-btn danger"
                  disabled={stopDialog.loading || stopDialog.confirming || (stopDialog.error && stopDialog.total == null)}
                  onClick={confirmStopJob}
                >
                  {stopDialog.confirming ? "Stopping…" : "Stop & reset"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>,
      document.body,
    );
  };

  // ── List View (overview)
  const jobEntries = Object.entries(jobs).sort(([, a], [, b]) => (b.createdAt || 0) - (a.createdAt || 0));
  const activeJobs = jobEntries.filter(([, j]) => j.status === "active" || j.status === "paused");
  const draftJobs = jobEntries.filter(([, j]) => j.status === "draft");
  const doneJobs = jobEntries.filter(([, j]) => j.status === "completed");

  const jobAudience = (job) => {
    const pool = job.contactPool || {};
    const resolved = resolvePoolLists(pool);
    return {
      lists: resolved.mode === "lists" ? resolved.names : [],
      manual: resolved.mode === "manual",
      status: pool.statusFilter ? String(pool.statusFilter) : "",
      tag: pool.tagFilter ? labelForPoolTag(pool.tagFilter, { jobs, tags: allTags }) : "",
    };
  };

  const previewCardFlow = (jobSteps, isPrepare = false) => {
    const raw = Array.isArray(jobSteps) ? jobSteps : [];
    const overflow = raw.length > PREVIEW_STEP_CAP ? raw.length - PREVIEW_STEP_CAP : 0;
    const steps = overflow > 0 ? raw.slice(0, PREVIEW_STEP_CAP) : raw;
    return (
      <ol className="oj-card-flow">
        {steps.map((ps, i) => {
          const def = STEP_TYPES.find((t) => t.id === ps.type);
          const label = stepLabel(ps.type, isPrepare);
          const hint = ps.type === "send_email"
            ? sendStepMailHint(ps, topicOptions, userTopics)
            : null;
          const mail = hint && (hint.name || hint.subject) ? hint : null;
          const last = i === steps.length - 1 && overflow === 0;
          return (
            <li
              key={ps.id || `flow-${i}`}
              className={`oj-card-flow-step oj-card-flow-step--${ps.type}${ps.type === "end_flow" ? " end" : ""}${mail ? " mail" : ""}`}
            >
              <span className="oj-card-flow-rail" aria-hidden="true">
                <span className="oj-card-flow-dot">
                  <i className={`${def?.iconPrefix || "fa"} ${def?.icon || "fa-circle"}`} />
                </span>
                {!last && <span className="oj-card-flow-stem" />}
              </span>
              <span className="oj-card-flow-body">
                <span className="oj-card-flow-label">
                  {label}
                  {ps.type === "wait" && (
                    <span className="oj-card-flow-wait">{ps.delayDays || 3}d</span>
                  )}
                </span>
                {mail?.name && <span className="oj-card-flow-mail">{mail.name}</span>}
                {mail?.subject && <span className="oj-card-flow-subject">{mail.subject}</span>}
              </span>
            </li>
          );
        })}
        {overflow > 0 && (
          <li className="oj-card-flow-more">+{overflow} more</li>
        )}
      </ol>
    );
  };

  const renderJobCard = ([jobId, job]) => {
    const stats = job.stats || {};
    const qStats = getJobQueueStats(jobId);
    const totalContacts = stats.totalContacts || job.contactIds?.length || 0;
    const doneCount = (stats.sent || 0) + (stats.replied || 0) + (stats.completed || 0);
    const progress = totalContacts > 0 ? Math.round(doneCount / totalContacts * 100) : 0;
    const isPrepare = job.deliveryMode === "prepare";
    const isError = job.status === "paused" && !!job.pauseReason && job.pauseReason !== "stopping";
    const statusLabel = isError ? "Error" : (JOB_STATUS_LABELS[job.status] || job.status);
    const audience = jobAudience(job);
    const visibleLists = audience.lists.slice(0, 2);
    const hiddenLists = audience.lists.length - visibleLists.length;

    return (
      <article
        key={jobId}
        className={`oj-card ${job.status}`}
        onClick={() => { setDetailJobId(jobId); setView("detail"); }}
      >
        <div className="oj-card-header">
          <div className="oj-card-info">
            <span className={`oj-status-pill ${job.status}${isError ? " error" : ""}`}>
              {job.status === "active" && <span className="oj-status-dot active"></span>}
              {isError && <span className="oj-status-dot error"></span>}
              {statusLabel}
            </span>
            <h4>{agentLabel(job.name) || "Untitled agent"}</h4>
            <div className="oj-card-audience">
              {audience.manual && (
                <span className="oj-card-list quiet">Hand-picked</span>
              )}
              {visibleLists.map((name) => (
                <span key={name} className="oj-card-list" title={name}>
                  {prettyImportName(name) || name}
                </span>
              ))}
              {hiddenLists > 0 && (
                <span
                  className="oj-card-list more"
                  title={audience.lists.slice(2).join(" · ")}
                >
                  +{hiddenLists}
                </span>
              )}
              {audience.status && (
                <span className="oj-card-filter">{audience.status}</span>
              )}
              {audience.tag && (
                <span className="oj-card-filter">{audience.tag}</span>
              )}
            </div>
          </div>
          <div className="oj-card-actions" onClick={(e) => e.stopPropagation()}>
            {(job.status === "draft" || isLiveJob(job)) && (
              <button type="button" onClick={() => openBuilder(jobId)} title={isLiveJob(job) ? "Edit settings" : "Edit"} aria-label={isLiveJob(job) ? "Edit settings" : "Edit job"}>
                <i className="fa fa-pen"></i>
              </button>
            )}
            {job.status === "active" && (
              <button type="button" className="warn" onClick={() => toggleJobPause(jobId)} title="Pause" aria-label="Pause job">
                <i className="fa fa-pause"></i>
              </button>
            )}
            {job.status === "paused" && (
              <button type="button" className="primary" onClick={() => toggleJobPause(jobId)} title="Resume" aria-label="Resume job">
                <i className="fa fa-play"></i>
              </button>
            )}
            <button type="button" onClick={() => cloneJob(jobId)} title="Clone" aria-label="Clone job">
              <i className="fa fa-copy"></i>
            </button>
            {(job.status === "draft" || job.status === "completed") && (
              <button type="button" className="danger" onClick={() => deleteJob(jobId)} title="Delete" aria-label="Delete job">
                <i className="fa fa-trash"></i>
              </button>
            )}
          </div>
        </div>

        {(() => {
          const drop = dropoutRate(stats, { isPrepare });
          const daily = isPrepare
            ? (job.prepareDailyLimit || DEFAULT_PREPARE_DAILY_LIMIT)
            : (job.dailyLimit || 30);
          return (
            <div className="oj-stat-strip" aria-label="Job activity">
              <div className="oj-stat-cell">
                <span className="oj-stat-val">{totalContacts}</span>
                <span className="oj-stat-lbl">Contacts</span>
                <span className="oj-stat-hint">{isPrepare ? "Prepare" : "Auto send"} · {daily}/day</span>
              </div>
              {/* `stats.prepared` counts every mail this job has ever written, so
                  labelling it "Prepared" read as "waiting for you now" and stayed
                  high after the drafts were sent. The actionable number is the
                  parked queue — same split as the sidebar (Ready / Written). */}
              {isPrepare ? (
                <div className="oj-stat-cell" title="Waiting in Prepared Mails">
                  <span className="oj-stat-val">{qStats.awaiting_send || 0}</span>
                  <span className="oj-stat-lbl">Ready</span>
                  <span className="oj-stat-hint">{stats.prepared || 0} written</span>
                </div>
              ) : (
                <div className="oj-stat-cell">
                  <span className="oj-stat-val">{stats.prepared || 0}</span>
                  <span className="oj-stat-lbl">Prepared</span>
                </div>
              )}
              <div className="oj-stat-cell">
                <span className="oj-stat-val">{stats.sent || 0}</span>
                <span className="oj-stat-lbl">Sent</span>
              </div>
              <div className="oj-stat-cell">
                <span className="oj-stat-val">{qStats.pending || 0}</span>
                <span className="oj-stat-lbl">Pending</span>
              </div>
              <DropoutStatCell drop={drop} onOpen={() => setDropoutJobId(jobId)} />
            </div>
          );
        })()}

        {(job.steps || []).length > 0 && (
          <div className="oj-card-preview">{previewCardFlow(job.steps, isPrepare)}</div>
        )}

        {(job.status === "active" || job.status === "paused") && totalContacts > 0 && (
          <div className="oj-card-progress" aria-hidden="true">
            <div className="oj-card-progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        )}
        {isError && (
          <div className="oj-card-error">
            <i className="fa fa-exclamation-circle"></i>
            {job.pauseMessage || job.pauseReason.replace(/_/g, " ")}
          </div>
        )}
      </article>
    );
  };

  return (
    <>
      {(view === "builder" || view === "detail") && renderMasterEditor()}
      {dropoutJobId && jobs[dropoutJobId] && createPortal(
        <DropoutBreakdownModal
          userId={userId}
          jobId={dropoutJobId}
          job={jobs[dropoutJobId]}
          onClose={() => setDropoutJobId(null)}
        />,
        document.body,
      )}

      <div className={`oj-overview${view !== "list" ? " oj-hidden" : ""}`}>
        <div className="oj-cards">
          {jobEntries.length === 0 && (
            <article className="oj-card oj-card-seed">
              <div className="oj-card-info">
                <h4>Create Outreach Agent</h4>
                <p>Researches each contact, then either sends or prepares.</p>
              </div>
              <div className="oj-seed-modes">
                <div className="oj-seed-mode">
                  <i className="fa fa-paper-plane"></i>
                  <div>
                    <strong>Automatic Outreach</strong>
                    <span>Writes and sends in the background</span>
                  </div>
                </div>
                <div className="oj-seed-mode">
                  <i className="fa fa-pen-to-square"></i>
                  <div>
                    <strong>Outreach Preparation</strong>
                    <span>Writes the emails and waits for you</span>
                  </div>
                </div>
              </div>
              <button type="button" className="oj-seed-btn" onClick={() => openBuilder()}>
                <i className="fa fa-plus"></i>
                Create an agent
              </button>
            </article>
          )}
          {activeJobs.map(renderJobCard)}
          {draftJobs.map(renderJobCard)}
          {doneJobs.map(renderJobCard)}
          <button type="button" className="oj-add-card" onClick={() => openBuilder()}>
            <span className="oj-add-card-inner">
              <span className="oj-add-card-icon"><i className="fa fa-plus"></i></span>
              <span>Add New Agent</span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default OutreachJobsSettings;
